import React, { useState, useEffect, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View, Text, ScrollView, TouchableOpacity, Image, TextInput,
  StyleSheet, Switch, Alert, ActivityIndicator, RefreshControl, DeviceEventEmitter, Platform, StatusBar, Linking,} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import { showAppAlert } from '../components/AppDialog';
import { SafeAreaView } from 'react-native-safe-area-context';
import { colors, spacing, radius } from '../theme';
import { Divider } from '../components/UI';
import { userProfile } from '../data';
import { getWardrobe } from '../services/DatabaseService';
import { generateOutfits } from '../services/OutfitMatchingService';
import { useAuth } from '../context/AuthContext';
import { getFullName, getDisplayEmail, getFirstName } from '../utils/userDisplay';
import { useTheme } from '../context/ThemeContext';
import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
import { getAIUsageToday, FREE_LIMITS, PREMIUM_PLANS, PREMIUM_PLANS_YEARLY, isPremium, getLimits, getTierBadge, TIERS, isSubscriptionExpired } from '../services/PremiumService';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { fetchSubscription, activateSubscription, changePlan, cancelSubscription } from '../services/SubscriptionService';
import { purchaseSubscription, restorePurchases, initIAP, endIAP } from '../services/IAPService';
import PremiumModal from '../components/PremiumModal';
import UpgradeModal from '../components/UpgradeModal';
import DowngradeFreeConfirmModal from '../components/DowngradeFreeConfirmModal';
import AppModal from '../components/AppModal';
import PlanComparisonTable from '../components/PlanComparisonTable';
import RestoreResultModal from '../components/RestoreResultModal';
import { useI18n } from '../i18n';
import { openLegalUrl } from '../utils/openLegalUrl';
// Import AchievementsService retiré v1.7.20 — section Récompenses supprimée
// import { computeAchievements, computeUserLevel, TIER_COLORS } from '../services/AchievementsService';
import { computeStyleProfile } from '../services/StyleProfileService';
import { getFavorites } from '../services/FavoritesService';
import { getWallet, getHistory, requestWithdrawal } from '../services/CJTrackingService';
import UseCreditsModal from '../components/UseCreditsModal';
import { getCreditBalance, getCreditHistory } from '../services/MytenueCredits';
import { formatRate, WITHDRAWAL_MIN_EUR, CASHBACK_RATES } from '../config/CashbackConfig';
import { getTierRewardExamples } from '../services/CashbackDisplayService';
import { checkIsAdmin } from '../services/AdminService';
import Constants from 'expo-constants';

function formatDate(val, lang = 'fr') {
  if (!val) return lang === 'en' ? 'Recently' : 'Récemment';
  try {
    const d = new Date(val);
    if (isNaN(d)) return String(val);
    const monthsFr = ['Jan','Fév','Mar','Avr','Mai','Juin',
                      'Juil','Août','Sep','Oct','Nov','Déc'];
    const monthsEn = ['Jan','Feb','Mar','Apr','May','Jun',
                      'Jul','Aug','Sep','Oct','Nov','Dec'];
    const months = lang === 'en' ? monthsEn : monthsFr;
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return String(val); }
}

// Mois abrégés bilingues pour les dates de renouvellement / expiration
function getShortMonths(lang) {
  if (lang === 'en') {
    return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  }
  return ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
}

// Mois complets bilingues (utilisé dans messages d'annulation, renouvellement)
function getFullMonths(lang) {
  if (lang === 'en') {
    return ['January','February','March','April','May','June','July','August','September','October','November','December'];
  }
  return ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
}

// Format date complet bilingue : FR "3 juin 2026" / EN "June 3, 2026"
function formatFullDate(d, lang) {
  if (!d || isNaN(d.getTime())) return '';
  const months = getFullMonths(lang);
  if (lang === 'en') {
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// Format date court bilingue : FR "3 juin 2026" / EN "Jun 3, 2026"
function formatShortDate(d, lang) {
  if (!d || isNaN(d.getTime())) return '';
  const months = getShortMonths(lang);
  if (lang === 'en') {
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// v1.10.52 : Formatage timestamp relatif avec heure/minute pour le renouvellement
// d'abonnement. Le backend stocke current_period_end en TIMESTAMPTZ (timestamp
// avec timezone), donc on a la précision à la seconde près. On veut afficher :
//   - "dans 12 min" si < 1 heure
//   - "dans 5 h 23 min" si < 24 heures
//   - "demain à 14h32" si demain
//   - "le 15 mai à 14h32 (dans 16 j)" si plus loin
export function formatRenewalTime(isoString, lang = 'fr', t = null) {
  if (!isoString) return null;
  try {
    const target = new Date(isoString);
    if (isNaN(target)) return null;
    const now = new Date();
    const diffMs = target - now;
    if (diffMs <= 0) return null;

    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Format heure : FR "14h32" / EN "2:32 PM"
    const hh = target.getHours();
    const mm = target.getMinutes().toString().padStart(2, '0');
    let timeStr;
    if (lang === 'en') {
      const ampm = hh >= 12 ? 'PM' : 'AM';
      const hh12 = hh % 12 || 12;
      timeStr = `${hh12}:${mm} ${ampm}`;
    } else {
      timeStr = `${hh.toString().padStart(2, '0')}h${mm}`;
    }

    const tx = (key, fallback, replacements = {}) => {
      let s = (t && t(key)) || fallback;
      if (s === key) s = fallback;
      Object.keys(replacements).forEach(k => {
        s = s.replace(`{${k}}`, replacements[k]);
      });
      return s;
    };

    if (diffMin < 60) {
      return tx('renewal_in_min',
        lang === 'en' ? `🔄 Renewal in ${diffMin} min` : `🔄 Renouvellement dans ${diffMin} min`,
        { min: diffMin });
    }
    if (diffHours < 24) {
      const remainMin = diffMin % 60;
      const hStr = lang === 'en'
        ? (remainMin > 0 ? `${diffHours} h ${remainMin}` : `${diffHours} h`)
        : (remainMin > 0 ? `${diffHours} h ${remainMin}` : `${diffHours} h`);
      return tx('renewal_in_hours',
        lang === 'en' ? `🔄 Renewal in ${hStr} (at ${timeStr})` : `🔄 Renouvellement dans ${hStr} (à ${timeStr})`,
        { hours: hStr, time: timeStr });
    }
    if (diffDays === 1) {
      return tx('renewal_tomorrow',
        lang === 'en' ? `🔄 Renewal tomorrow at ${timeStr}` : `🔄 Renouvellement demain à ${timeStr}`,
        { time: timeStr });
    }
    // Plus loin → date courte
    const months = getShortMonths(lang);
    const dateStr = lang === 'en'
      ? `${months[target.getMonth()]} ${target.getDate()}`
      : `${target.getDate()} ${months[target.getMonth()]}`;
    return tx('renewal_on_with_time',
      lang === 'en'
        ? `🔄 Renewal on ${dateStr} at ${timeStr} (in ${diffDays} d)`
        : `🔄 Renouvellement le ${dateStr} à ${timeStr} (dans ${diffDays} j)`,
      { date: dateStr, time: timeStr, days: diffDays });
  } catch {
    return null;
  }
}

export default function ProfileScreen({ navigation }) {
  const { dark, theme, setDark: setDarkMode } = useTheme();
  const darkMode = dark;
  const { t, lang, setLang, region, allLanguages, price } = useI18n();
  // v1.12.663 — isFR utilisé dans handleChangePlan (manquait → ReferenceError)
  const isFR = !lang || lang.startsWith('fr');
  // v1.12.117 — useAuth remonté en tête : ligne 586 → ici. Sur Hermes natif
  // ça passait, mais Metro web minifié levait "Cannot access 'Ie' before
  // initialization" car useEffects (lignes 115+) référencent user avant son
  // déclarateur original.
  const { user, signOut, deleteAccount, getValidToken, updateUserProfile, reloadProfile } = useAuth();
  const [aiUsed,        setAiUsed]        = React.useState(0);
  const [userLimits,    setUserLimits]    = React.useState(null);
  // ═══ v1.10.19 : Compteur Try-ons (synchro avec backend RDS) ═══
  const [tryonUsage,    setTryonUsage]    = React.useState({ count: 0, limit: 0, tier: 'free', isPaidTier: false, nextResetDate: null });
  // v1.12.967 — Consentement mesure d'audience anonyme (RGPD). Défaut ON (intérêt légitime).
  const [analyticsOn, setAnalyticsOn] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      try { const v = await AsyncStorage.getItem('outfit_cookie_consent'); setAnalyticsOn(v !== 'declined'); } catch {}
    })();
  }, []);
  const handleToggleAnalytics = React.useCallback(async (val) => {
    setAnalyticsOn(val);
    try { require('../services/AnalyticsService').setAnalyticsConsent(val ? 'accepted' : 'declined'); } catch {}
  }, []);
  // v1.12.602 — V5.4 : addons bonus actifs (boost wardrobe TTL + subs récurrentes + try-ons/scans packs)
  const [addonsBalance, setAddonsBalance] = React.useState({ tryons: 0, scans: 0, wardrobe: 0, wardrobeQuota: null });
  // v1.12.175 : photo perso "Mon mannequin"
  const [hasPersonalMannequin, setHasPersonalMannequin] = React.useState(false);
  const [personalMannequinUrl, setPersonalMannequinUrl] = React.useState(null);
  // v1.12.585 — anti-clignotement : on cache 60s l'URL signée S3 du mannequin
  const personalMannequinFetchedAtRef = React.useRef(0);
  // v1.12.586 — throttle global du useFocusEffect : skip tous les re-fetchs
  // si moins de 30s depuis le dernier focus (évite clignotement Today's Usage,
  // Mannequin, Try-on, etc. à chaque navigation).
  const profileLastFocusAtRef = React.useRef(0);
  // v1.12.563 — Ville localisée via Nominatim selon lang du profil
  const [cityLocalized, setCityLocalized] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await AsyncStorage.getItem('outfit_weather_v1');
        if (!raw) return;
        const { lat, lon } = JSON.parse(raw);
        if (typeof lat !== 'number' || typeof lon !== 'number') return;
        const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=${lang}&zoom=10`;
        const res = await fetch(url, { headers: { 'User-Agent': 'Mytenue/1.0' } });
        if (!res.ok) return;
        const data = await res.json();
        const city = data?.address?.city || data?.address?.town || data?.address?.village || data?.address?.municipality || null;
        if (city && !cancelled) setCityLocalized(city);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [lang]);
  // v1.12.249 : reminders calendrier (Premium+)
  const [calReminderEnabled, setCalReminderEnabled] = React.useState(false);
  const [calReminderHour, setCalReminderHour] = React.useState(8);
  React.useEffect(() => {
    if (!user?.id) return;
    const { getReminderSettings } = require('../services/CalendarReminderService');
    getReminderSettings(user.id).then(s => {
      setCalReminderEnabled(!!s.enabled);
      setCalReminderHour(s.hour ?? 8);
    }).catch(() => {});
  }, [user?.id]);
  const toggleCalReminder = React.useCallback(async (val) => {
    if (!user?.id) {
      const { showAppAlert } = require('../components/AppDialog');
      showAppAlert(t('login_required') || 'Connexion requise', t('login_to_enable_reminders') || 'Connectez-vous pour activer les rappels.', [{ text: 'OK' }]);
      return;
    }
    const { enableDailyReminder, disableDailyReminder } = require('../services/CalendarReminderService');
    const { showAppAlert } = require('../components/AppDialog');
    if (val) {
      const r = await enableDailyReminder({ userId: user.id, hour: calReminderHour, minute: 0 });
      console.log('[ProfileReminder] enable result:', r);
      if (r.ok) {
        setCalReminderEnabled(true);
        return;
      }
      // v1.12.543 — Feedback explicite à l'user selon le motif d'échec
      if (r.reason === 'permission_denied') {
        showAppAlert(t('permission_required') || 'Permission requise', t('permission_required_reminders') || 'Autorisez les notifications dans les réglages système.', [{ text: 'OK' }]);
      } else if (r.reason === 'notifications_module_unavailable' || (typeof Platform !== 'undefined' && Platform.OS === 'web')) {
        showAppAlert(
          t('mobile_only') || 'Disponible sur mobile uniquement',
          t('reminders_mobile_only') || 'Les rappels quotidiens nécessitent l\'application iOS ou Android.',
          [{ text: 'OK' }]
        );
      } else {
        showAppAlert(
          t('error') || 'Erreur',
          `${t('cannot_enable_reminder') || 'Impossible d\'activer le rappel.'}\n\n(${r.reason || 'unknown'})`,
          [{ text: 'OK' }]
        );
      }
      // Garder le toggle off pour cohérence visuelle
      setCalReminderEnabled(false);
    } else {
      await disableDailyReminder({ userId: user.id });
      setCalReminderEnabled(false);
    }
  }, [user?.id, calReminderHour, t]);
  // Scroll en haut quand on revient sur cet écran
  const scrollRef = useResetScrollOnFocus();

  // ═══ Charger les limites du tier utilisateur ═══
  // ═══ FIX 2026-04 (v1.7.60) : recalcul si l'abonnement vient d'expirer ═══
  // Avant : ce useEffect ne dépendait que de user.tier et user.plan. Si la
  //   date premiumExpiresAt passait à hier (expiration naturelle), user.tier
  //   restait 'premium' côté contexte → les limites restaient celles du tier
  //   payant et l'user avait toujours accès à 30 looks/jour, ∞ dressing, etc.
  //   alors que le backend lui aurait répondu free.
  // Maintenant : on ajoute user.premiumExpiresAt aux deps. getLimits() utilise
  //   getTier() en interne qui détecte l'expiration via la date passée et
  //   retourne 'free' → les limites Free sont automatiquement appliquées dès
  //   que la date est franchie ou qu'on récupère un nouveau profil.
  React.useEffect(() => {
    getLimits(user).then(setUserLimits).catch(() => setUserLimits(null));
  }, [user?.tier, user?.plan, user?.premiumExpiresAt]);

  // v1.12.673 — Sync currentTier depuis user.tier au mount/changement.
  // BUG: avant, currentTier restait 'free' (state initial) même si user.tier='basic'
  // → handleChangePlan voyait hasActiveSub=false → startCheckout → 409 → erreur visible
  React.useEffect(() => {
    if (user?.tier && user.tier !== currentTier) {
      setCurrentTier(user.tier);
    }
  }, [user?.tier]);
  const [showPremium,   setShowPremium]   = React.useState(false);
  // v1.12.608 — V5.6 : preview downgrade avant cancel
  const [showDowngradeConfirm, setShowDowngradeConfirm] = React.useState(false);
  // v1.12.611 — Modal de suppression de compte (remplace window.confirm natif)
  // 'idle' | 'step1' | 'step2' | 'deleting' | 'error'
  const [deleteStep, setDeleteStep] = React.useState('idle');
  const [deleteError, setDeleteError] = React.useState('');
  // v1.12.613 — useCallback OBLIGATOIRE : sans ça, la ref change à chaque
  // render et le useEffect [visible, close] dans AppModal cleanup → push/pop
  // history → popstate fire → close → modal disparaît au moindre re-render.
  // Symptôme : "click Continuer → la popup se ferme sans rien faire".
  const deleteModalClose = React.useCallback(() => {
    setDeleteStep(prev => prev === 'deleting' ? prev : 'idle');
  }, []);
  // ═══ v1.8.5 : popup d'upgrade dédié (ouvert depuis banner cashback) ═══
  // ═══ v1.8.10 : feature dynamique selon le contexte d'ouverture ═══
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [upgradeModalFeature, setUpgradeModalFeature] = React.useState('cashback');
  const [purchasing,    setPurchasing]    = React.useState(false);
  const [restoring,     setRestoring]     = React.useState(false);
  // Résultat de la dernière restauration → ouvre RestoreResultModal
  // { kind: 'success'|'already'|'expired'|'none'|'error', details?: ... }
  const [restoreResult, setRestoreResult] = React.useState(null);
  const [userPlan,      setUserPlan]      = React.useState('free');
  // v1.12.117 — currentTier remonté ici (avant ligne 598 qui calcule
  // isPremiumUser → utilise currentTier). Sans ça, TDZ web 'Cannot access
  // Ze before initialization' au render Profile.
  const [currentTier,   setCurrentTier]   = React.useState('free');
  // ═══ Période actuelle de l'abonnement : 'monthly' | 'yearly' | null ═══
  // Distinct de userPlan (qui peut être 'free'/'cancelled'/etc).
  // Sert à savoir quel couple (tier, period) est ACTIF dans l'UI plans.
  const [currentPeriod, setCurrentPeriod] = React.useState(null);
  const [showManage,    setShowManage]    = React.useState(false);
  const [refreshing,    setRefreshing]    = React.useState(false);
  const loadStatsRef = React.useRef(null);
  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try { if (loadStatsRef.current) await loadStatsRef.current(); } catch {}
    // v1.12.957 — un reload (pull-to-refresh) doit suffire pour voir les limites
    // mises à jour après un achat (add-on, abo). On re-fetch usages + solde add-ons
    // et on remet à zéro le throttle du focus pour que le prochain focus re-fetch aussi.
    try {
      profileLastFocusAtRef.current = 0;
      const u = userRef.current;
      const token = (await (typeof getValidTokenRef.current === 'function' ? getValidTokenRef.current() : null)) || u?.accessToken;
      getAIUsageToday(u?.id || u?.sub, u).then(c => setAiUsed(c)).catch(() => {});
      if (token) {
        const { fetchTryOnUsage } = require('../services/TryOnService');
        fetchTryOnUsage(token).then(usage => setTryonUsage(usage)).catch(() => {});
        const { AWS_CONFIG } = require('../config/aws_config');
        const b = await fetch(`${AWS_CONFIG.apiUrl}/addons/balance`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null).catch(() => null);
        if (b) setAddonsBalance(b);
      }
    } catch {}
    setRefreshing(false);
  }, []);

  // Recharger à chaque fois que l'écran est affiché
  useEffect(() => {
    // v1.12.954 — passer le cognito sub pour que RC configure avec l'App User ID
    // (évite une éventuelle session anonyme si ProfileScreen init RC en premier).
    initIAP(user?.id);
    return () => { endIAP(); };
  }, []);

  // v1.12.632 — Deps stables (user.id/sub seulement) + refs pour les
  // fonctions parent. Avant : deps [user, reloadProfile, getValidToken]
  // → user objet change à chaque update, getValidToken non mémoisé → callback
  // re-créé → useFocusEffect cleanup + re-fire en boucle → cascade gs/hs
  // (React fiber render loop) qui peut crash le navigateur.
  const reloadProfileRef = React.useRef(reloadProfile);
  const getValidTokenRef = React.useRef(getValidToken);
  const userRef = React.useRef(user);
  React.useEffect(() => { reloadProfileRef.current = reloadProfile; }, [reloadProfile]);
  React.useEffect(() => { getValidTokenRef.current = getValidToken; }, [getValidToken]);
  React.useEffect(() => { userRef.current = user; }, [user]);
  useFocusEffect(useCallback(() => {
    // v1.12.586 — Throttle 30s : évite re-fetchs en rafale qui font clignoter
    //   l'écran Profil (mannequin S3 signed URL, today's usage, tryon usage, etc.).
    const nowMsFocus = Date.now();
    if (nowMsFocus - profileLastFocusAtRef.current < 30000) {
      console.log('[PROFILE] focus throttled (< 30s)');
      return;
    }
    profileLastFocusAtRef.current = nowMsFocus;
    (async () => {
      const curUser = userRef.current;
      const curReload = reloadProfileRef.current;
      const curGetToken = getValidTokenRef.current;
      if (curReload) {
        try { await curReload(); } catch {}
      }
      const token = (await (typeof curGetToken === 'function' ? curGetToken() : null)) || curUser?.accessToken;
      getAIUsageToday(curUser?.id || curUser?.sub, curUser).then(count => {
        console.log('[PROFILE] AI usage today:', count);
        setAiUsed(count);
      });
      if (token) {
        const { fetchTryOnUsage, fetchPersonalMannequin } = require('../services/TryOnService');
        fetchTryOnUsage(token).then(usage => {
          console.log('[PROFILE] Try-on usage:', usage);
          setTryonUsage(usage);
        });
        // v1.12.602 — V5.4 : fetch addons balance (boost + subs + packs actifs)
        try {
          const { AWS_CONFIG } = require('../config/aws_config');
          fetch(`${AWS_CONFIG.apiUrl}/addons/balance`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).then(b => {
            if (b) setAddonsBalance(b);
          }).catch(() => {});
        } catch {}
        // v1.12.585 — skip fetchPersonalMannequin si déjà fait il y a < 60s
        // (l'URL S3 pré-signée change à chaque appel → image re-télécharge → clignote).
        const nowMs = Date.now();
        if (nowMs - personalMannequinFetchedAtRef.current > 60000) {
          fetchPersonalMannequin(token)
            .then(d => {
              personalMannequinFetchedAtRef.current = Date.now();
              setHasPersonalMannequin(!!d?.hasPersonalMannequin);
              setPersonalMannequinUrl(d?.viewUrl || null);
            })
            .catch(() => {});
        }
      }
    })();
  }, []));

  // ═══ Écouter les events 'ai_usage_updated' émis par PremiumService ═══
  // → mise à jour en temps réel du compteur quand une analyse IA est faite
  //   depuis n'importe quel écran (Dressing, Looks, Voyage, Suggestions...)
  React.useEffect(() => {
    const { DeviceEventEmitter } = require('react-native');
    const sub = DeviceEventEmitter.addListener('ai_usage_updated', (data) => {
      if (data && typeof data.current === 'number') {
        console.log('[PROFILE] AI usage live update:', data.current);
        setAiUsed(data.current);
      }
    });
    return () => sub.remove();
  }, []);

  // v1.12.766 — Écouter `tryon_usage_changed` émis par OutfitCard/FlatLayButton.
  // Sans ça, le compteur tryon affiché restait stale après une génération flat-lay
  // ou try-on depuis Tenue du jour / Looks AI / Voyage / Mes Looks / Stylist.
  React.useEffect(() => {
    const { DeviceEventEmitter } = require('react-native');
    const sub = DeviceEventEmitter.addListener('tryon_usage_changed', (data) => {
      if (!data) return;
      console.log('[PROFILE] Try-on usage live update:', data);
      setTryonUsage(prev => ({
        ...prev,
        count: typeof data.count === 'number' ? data.count : prev.count,
        limit: typeof data.limit === 'number' ? data.limit : prev.limit,
        tier:  typeof data.tier === 'string' ? data.tier : prev.tier,
        isPaidTier: typeof data.isPaidTier === 'boolean' ? data.isPaidTier : prev.isPaidTier,
        nextResetDate: data.nextResetDate || prev.nextResetDate,
      }));
    });
    return () => sub.remove();
  }, []);

  // v1.12.957 — Refresh auto après un achat add-on (émis par AddonsShopScreen).
  // → met à jour solde/limites (try-on, IA, dressing) sans redémarrer l'app.
  React.useEffect(() => {
    const { DeviceEventEmitter } = require('react-native');
    const sub = DeviceEventEmitter.addListener('addon_balance_changed', () => {
      console.log('[PROFILE] addon_balance_changed → refresh');
      onRefresh();
    });
    return () => sub.remove();
  }, [onRefresh]);

  // v1.10.52 : Tick toutes les 60s pour rafraîchir l'affichage "dans X min"
  // du renouvellement abonnement (le timer change avec le temps qui passe).
  // useState hack pour forcer un re-render sans appel backend.
  const [, setNowTick] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setNowTick(t => t + 1), 60000); // 60s
    return () => clearInterval(interval);
  }, []);

  // v1.12.268 — Tick toutes les 30s pour countdown reset IA (compte à rebours minuit Paris)
  // + auto-refresh du compteur si on passe minuit (Paris timezone)
  const [aiResetCountdown, setAiResetCountdown] = React.useState('');
  const lastDayRef = React.useRef(null);
  React.useEffect(() => {
    const refreshCountdown = () => {
      try {
        // Date Paris (Europe/Paris)
        const nowParis = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
        const tomorrow = new Date(nowParis);
        tomorrow.setDate(nowParis.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const diffMs = tomorrow.getTime() - nowParis.getTime();
        const hours = Math.floor(diffMs / 3600000);
        const mins = Math.floor((diffMs % 3600000) / 60000);
        const secs = Math.floor((diffMs % 60000) / 1000);
        let formatted;
        if (hours > 0) formatted = `${hours}h ${String(mins).padStart(2,'0')}m`;
        else if (mins > 0) formatted = `${mins}m ${String(secs).padStart(2,'0')}s`;
        else formatted = `${secs}s`;
        setAiResetCountdown(formatted);

        // Détection du passage de minuit → refresh compteur AI
        const todayKey = nowParis.toDateString();
        if (lastDayRef.current && lastDayRef.current !== todayKey) {
          console.log('[PROFILE] 🌙 Midnight crossed → refresh AI usage');
          getAIUsageToday(user?.id || user?.sub, user).then(count => setAiUsed(count));
        }
        lastDayRef.current = todayKey;
      } catch (e) { console.warn('[PROFILE] countdown error:', e?.message); }
    };
    refreshCountdown();
    // Tick toutes les 30s (ou 1s pour les dernières minutes — pour rester simple, 30s constant)
    const interval = setInterval(refreshCountdown, 30000);
    return () => clearInterval(interval);
  }, [user?.id, user?.sub]);

  React.useEffect(() => {
    // Charger le plan depuis le serveur
    fetchSubscription(user?.id || user?.sub, user?.accessToken).then(sub => {
      if (!sub) return;
      if (sub.status === 'active') {
        setUserPlan(sub.period || 'monthly');
        setCurrentPeriod(sub.period === 'yearly' ? 'yearly' : 'monthly');
      } else if (sub.status === 'cancelled') {
        setUserPlan('cancelled');
        setCurrentPeriod(null);
      } else {
        // Plan gratuit — utiliser le plan du user context comme fallback
        const localPlan = user?.plan;
        if (localPlan && localPlan !== 'free') setUserPlan(localPlan);
        else setUserPlan('free');
      }
    }).catch(() => {
      // Fallback sur le plan du user context
      if (user?.plan && user.plan !== 'free') setUserPlan(user.plan);
    });
  }, [user?.id]);

  // ═══ v1.12.948 — Re-sync période depuis le profil backend (source autoritaire) ═══
  // L'effet ci-dessus ne tourne qu'au montage ([user?.id]) → après un changement
  // de formule (basic→premium, switch mensuel/annuel), userPlan/currentPeriod
  // restaient figés sur une période périmée → le bouton « Passer au mensuel/annuel »
  // s'affichait à l'envers (ex : bouton « mensuel » alors qu'on est déjà mensuel).
  // user.subscriptionPeriod est rechargé par AuthContext après chaque
  // 'subscription_changed' → on resynchronise dès qu'il change.
  React.useEffect(() => {
    if (!user?.subscriptionPeriod) return;
    if (user?.subscriptionCancelled) { setUserPlan('cancelled'); setCurrentPeriod(null); return; }
    const p = user.subscriptionPeriod === 'yearly' ? 'yearly' : 'monthly';
    setUserPlan(p);
    setCurrentPeriod(p);
  }, [user?.subscriptionPeriod, user?.subscriptionCancelled, user?.tier]);

  const handleSubscribe = async (planId) => {
    if (purchasing) return;

    // Extraire tier et period du planId (ex: "basic", "basic_yearly", "premium", "gold_yearly")
    const tier   = planId.replace('_yearly', '');
    const period = planId.includes('_yearly') ? 'yearly' : 'monthly';

    // Sur web : RevenueCat/IAP n'existe pas → on redirige vers Stripe Checkout.
    // L'utilisateur quitte la page. Au retour (/billing/success) le webhook
    // Stripe a déjà mis à jour profile.tier en DB ; useStripeCallback refresh.
    if (Platform.OS === 'web') {
      if (tier === 'free') {
        // Pas de paiement pour le tier free — passer direct à la suite si besoin.
        showAppAlert(t('free_plan_no_payment'), t('no_payment_required'));
        return;
      }
      try {
        const realTier = currentTier || user?.tier || 'free';
        const hasActiveSub = realTier && realTier !== 'free';

        // v1.12.675 — Popup confirm AVANT basculement (rassure le user)
        if (hasActiveSub) {
          const MONTHLY_PRICES = { basic: 5.99, premium: 9.99, gold: 16.99 };
          const YEARLY_PRICES  = { basic: 59.90, premium: 99.90, gold: 169.90 };
          const newPrice = period === 'yearly' ? YEARLY_PRICES[tier] : MONTHLY_PRICES[tier];
          const periodLbl = period === 'yearly' ? (isFR ? 'annuel' : 'yearly') : (isFR ? 'mensuel' : 'monthly');
          const tierLbl = tier.charAt(0).toUpperCase() + tier.slice(1);
          const currentLbl = realTier.charAt(0).toUpperCase() + realTier.slice(1);
          const TIER_RANK = { free: 0, basic: 1, premium: 2, gold: 3 };
          const isMonthlyToYearlySame = realTier === tier && currentPeriod === 'monthly' && period === 'yearly';
          const isYearlyToMonthlySame = realTier === tier && currentPeriod === 'yearly' && period === 'monthly';
          const isUpgrade = (TIER_RANK[tier] || 0) > (TIER_RANK[realTier] || 0);
          const isDowngrade = (TIER_RANK[tier] || 0) < (TIER_RANK[realTier] || 0);

          let confirmTitle, confirmMsg;
          if (isMonthlyToYearlySame) {
            confirmTitle = isFR ? '✓ Passer en annuel' : '✓ Switch to yearly';
            confirmMsg = isFR
              ? `Vous allez passer ${tierLbl} en annuel.\n\n• Paiement immédiat : ${newPrice} € pour 1 an\n• Vos jours mensuels restants sont préservés\n• L'annuel commencera à la fin de votre période mensuelle en cours\n• Aucun jour perdu, aucune surprise`
              : `Switch ${tierLbl} to yearly.\n\n• Pay now: ${newPrice} € for 1 year\n• Your remaining monthly days are preserved\n• Yearly starts at end of current monthly period\n• No days lost`;
          } else if (isYearlyToMonthlySame) {
            confirmTitle = isFR ? '↓ Passer en mensuel' : '↓ Switch to monthly';
            confirmMsg = isFR
              ? `Vous allez passer ${tierLbl} en mensuel.\n\n• Aucun prélèvement immédiat\n• Vous gardez votre abonnement annuel jusqu'à expiration\n• Bascule automatique en mensuel à la fin de l'année (${newPrice} €/mois ensuite)`
              : `Switch ${tierLbl} to monthly.\n\n• No charge now\n• Keep yearly until expiration\n• Automatic switch to monthly at year end (${newPrice} €/month after)`;
          } else if (isUpgrade) {
            // v1.12.675 — Récupérer le montant exact via preview Stripe
            let amountText = '';
            try {
              const { previewChangeSubscription } = require('../services/StripeService');
              const preview = await previewChangeSubscription(tier, period);
              if (preview?.amountDueEur !== undefined) {
                const eur = preview.amountDueEur.toFixed(2).replace('.', ',');
                if (preview.isCredit) {
                  amountText = isFR ? `\n\n💰 Montant prélevé MAINTENANT : 0 € (crédit de ${Math.abs(preview.amountDueEur).toFixed(2).replace('.',',')} € appliqué).` : `\n\n💰 Charged NOW: €0 (credit of €${Math.abs(preview.amountDueEur).toFixed(2)} applied).`;
                } else {
                  amountText = isFR ? `\n\n💰 Montant prélevé MAINTENANT : ${eur} € (prorata calculé par Stripe).` : `\n\n💰 Charged NOW: €${eur} (Stripe-calculated proration).`;
                }
              }
            } catch (pe) {
              console.warn('[handleSubscribe] preview failed:', pe?.message);
            }
            confirmTitle = isFR ? `✓ Passer à ${tierLbl}` : `✓ Switch to ${tierLbl}`;
            confirmMsg = isFR
              ? `Vous allez basculer de ${currentLbl} vers ${tierLbl} ${periodLbl}.\n\n• Nouveau prix : ${newPrice} €${period === 'yearly' ? '/an' : '/mois'}\n• Activation immédiate${amountText}\n• Prochaines factures au tarif ${tierLbl}`
              : `Switch from ${currentLbl} to ${tierLbl} ${periodLbl}.\n\n• New price: ${newPrice} €${period === 'yearly' ? '/year' : '/month'}\n• Active immediately${amountText}\n• Future invoices at ${tierLbl} price`;
          } else if (isDowngrade) {
            confirmTitle = isFR ? `↓ Passer à ${tierLbl}` : `↓ Switch to ${tierLbl}`;
            confirmMsg = isFR
              ? `Vous allez basculer de ${currentLbl} vers ${tierLbl} ${periodLbl}.\n\n• Aucun prélèvement immédiat\n• Vous gardez ${currentLbl} jusqu'à la fin de votre période payée\n• Bascule automatique vers ${tierLbl} à cette date (${newPrice} €${period === 'yearly' ? '/an' : '/mois'})`
              : `Switch from ${currentLbl} to ${tierLbl} ${periodLbl}.\n\n• No charge now\n• Keep ${currentLbl} until end of paid period\n• Automatic switch to ${tierLbl} at that date (${newPrice} €${period === 'yearly' ? '/year' : '/month'})`;
          } else {
            confirmTitle = isFR ? 'Confirmer le changement' : 'Confirm change';
            confirmMsg = isFR
              ? `Passer à ${tierLbl} ${periodLbl} ?`
              : `Switch to ${tierLbl} ${periodLbl} ?`;
          }

          const confirmIdx = await showAppAlert(confirmTitle, confirmMsg, [
            { text: isFR ? 'Annuler' : 'Cancel', style: 'cancel' },
            { text: isFR ? 'Confirmer' : 'Confirm', style: 'default' },
          ]);
          if (confirmIdx !== 1) return;
        }

        setPurchasing(true);
        if (hasActiveSub) {
          const { changeSubscription, cancelPendingChange } = require('../services/StripeService');
          const { showProcessing, hideProcessing } = require('../components/ProcessingOverlay');
          try {
            showProcessing(
              isFR ? 'Paiement en cours…' : 'Processing payment…',
              isFR ? `Finalisation de votre abonnement ${tier} ${period === 'yearly' ? 'annuel' : 'mensuel'}.` : `Finalizing your ${tier} ${period} subscription.`
            );
            const result = await changeSubscription(tier, period);
            hideProcessing();
            setPurchasing(false);
            if (result?.mode === 'updated') {
              showAppAlert(isFR ? 'Abonnement modifié' : 'Subscription updated', isFR ? `Plan ${tier} ${period === 'yearly' ? 'annuel' : 'mensuel'} actif immédiatement.` : `${tier} ${period} plan active immediately.`);
            } else if (result?.mode === 'scheduled') {
              const dStr = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
              showAppAlert(isFR ? 'Changement programmé' : 'Change scheduled', isFR ? `Bascule le ${dStr}.` : `Switch on ${dStr}.`);
            } else if (result?.mode === 'prepaid_yearly') {
              const dStr = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
              showAppAlert(isFR ? '✓ Annuel confirmé' : '✓ Yearly confirmed', isFR ? `Paiement de ${result.amountPaid}€ reçu. Effectif le ${dStr}.` : `Payment ${result.amountPaid}€ received. Effective ${dStr}.`);
            }
            if (reloadProfile) [1000, 3000, 6000, 12000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
            return;
          } catch (e2) {
            hideProcessing();
            setPurchasing(false);
            const code2 = e2?.code || '';
            if (code2 === 'pending_change_conflict') {
              const idx = await showAppAlert(
                isFR ? 'Changement en attente' : 'Pending change',
                isFR ? 'Vous avez déjà un changement programmé. L\'annuler pour passer à ce nouveau plan ?\n\nSi prépayé annuel: remboursement automatique.'
                     : 'You have a pending change. Cancel it to switch to this new plan?\n\nIf prepaid yearly: automatic refund.',
                [{ text: isFR ? 'Garder' : 'Keep', style: 'cancel' }, { text: isFR ? 'Annuler le pending' : 'Cancel pending', style: 'destructive' }]
              );
              if (idx === 1) {
                try {
                  showProcessing(isFR ? 'Annulation en cours…' : 'Cancelling…', isFR ? 'Annulation du changement programmé et remboursement.' : 'Cancelling pending change and refund.');
                  const r = await cancelPendingChange();
                  hideProcessing();
                  const rmsg = r?.refunded ? (isFR ? ` Remboursement: ${r.refunded}€.` : ` Refund: ${r.refunded}€.`) : '';
                  showAppAlert(isFR ? 'Pending annulé' : 'Pending cancelled', (isFR ? 'Annulé.' : 'Cancelled.') + rmsg + (isFR ? ' Recliquez pour souscrire.' : ' Click again to subscribe.'));
                  if (reloadProfile) [500, 2000, 5000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
                } catch (e3) {
                  hideProcessing();
                  showAppAlert(isFR ? 'Erreur' : 'Error', e3?.message || '');
                }
              }
              return;
            }
            if (code2 === 'already_on_this_plan') {
              showAppAlert(isFR ? 'Plan identique' : 'Same plan', isFR ? `Vous êtes déjà sur ${tier} ${period === 'yearly' ? 'annuel' : 'mensuel'}.` : `You're already on ${tier} ${period}.`);
              return;
            }
            showAppAlert(t('payment_unavailable'), e2?.message || (lang === 'en' ? 'Stripe error' : 'Erreur Stripe'));
            return;
          }
        }
        // User free → Checkout normal (avec application Crédit Mytenue auto)
        const { purchaseSubscriptionWithCredits } = require('../services/CreditAwarePurchase');
        await purchaseSubscriptionWithCredits({ tier, period, user, getValidToken, lang });
      } catch (e) {
        setPurchasing(false);
        showAppAlert(t('payment_unavailable'), e?.message || (lang === 'en' ? 'Stripe error' : 'Erreur Stripe'));
      }
      return;
    }

    // ═══ FIX 2026-04 (v1.7.64) : confirmation si déjà abonné ═══
    // Avant : l'user pouvait cliquer "Choisir" sur un plan différent de son
    //   plan actuel sans aucune alerte → 2 abonnements simultanés possibles
    //   côté DB (avant le fix backend) ou facturations multiples si timing
    //   particulier. UX confuse : pas clair que l'ancien plan serait remplacé.
    // Maintenant : si l'user est déjà abonné à un autre plan (différent tier
    //   OU différent period), on lui demande confirmation explicite avec un
    //   message clair sur ce qui va se passer (ancien remplacé, prorata Apple/
    //   Google, nouvelle facturation).
    const hasActiveSub = isPremiumUser && currentTier && currentTier !== 'free';
    const isSamePlan = hasActiveSub && currentTier === tier && currentPeriod === period;
    const isDifferentPlan = hasActiveSub && !isSamePlan && tier !== 'free';

    if (isSamePlan) {
      Alert.alert(t('current_plan_label'), t('already_on_plan'));
      return;
    }

    if (isDifferentPlan) {
      const periodYearly = lang === 'en' ? 'Yearly' : 'Annuel';
      const periodMonthly = lang === 'en' ? 'Monthly' : 'Mensuel';
      const oldLabel = `${TIERS[currentTier]?.label || currentTier} ${currentPeriod === 'yearly' ? periodYearly : periodMonthly}`;
      const newLabel = `${TIERS[tier]?.label || tier} ${period === 'yearly' ? periodYearly : periodMonthly}`;
      const prorataMsg = Platform.OS === 'ios' ? t('prorata_apple') : t('prorata_google');
      const msg = t('change_formula_msg')
        .replace('{oldPlan}', oldLabel)
        .replace('{newPlan}', newLabel)
        .replace('{prorataMsg}', prorataMsg);
      const confirmed = await new Promise(resolve => {
        Alert.alert(
          t('change_formula_q'),
          msg,
          [
            { text: t('cancel'), style: 'cancel', onPress: () => resolve(false) },
            { text: t('continue'), style: 'default', onPress: () => resolve(true) },
          ]
        );
      });
      if (!confirmed) return;
    }

    setPurchasing(true);
    try {
      // ═══ ACHAT VIA REVENUECAT + PLAY STORE (prod) ═══
      // Le flow : RC ouvre le popup Google Play → user paie (sandbox en mode
      // testeur interne) → RC valide → webhook vers Lambda → DB update tier.
      // purchaseSubscription fait tout ça pour nous et renvoie { success: true }.
      console.log('[Profile v2] 🛒 RC purchase flow — tier=' + tier + ' period=' + period);
      const result = await purchaseSubscription(tier, period, user);

      if (result?.cancelled) {
        console.log('[Profile] achat annulé par l\'utilisateur');
        return;
      }
      if (!result?.success) {
        console.warn('[Profile] achat échoué:', result);
        // ═══ v1.7.66 : message spécifique selon le code d'erreur ═══
        if (result?.error === 'iap_not_configured') {
          Alert.alert(
            t('purchase_unavailable'),
            result.message || t('purchase_unavailable_msg')
          );
        } else {
          Alert.alert(
            t('purchase_failed'),
            result?.message || t('purchase_failed_msg')
          );
        }
        return;
      }

      // Achat OK : RC a confirmé et le webhook est envoyé en arrière-plan
      // Côté app on met à jour l'UI optimistiquement
      setCurrentTier(tier);
      setCurrentPeriod(period);
      setUserPlan(planId);
      setShowPremium(false);
      setShowManage(false);
      // ═══ v1.7.61 : refermer la section "Changer de formule" après succès ═══
      // L'user vient de confirmer son nouveau plan → on peut cacher la liste
      // des plans et le ramener en haut pour qu'il voie sa nouvelle carte Premium.
      setShowAllPlans(false);
      try {
        scrollRef.current?.scrollTo?.({ y: 0, animated: true });
      } catch {}

      // Sauvegarder le tier en AsyncStorage + notifier les autres écrans
      try {
        const { saveTierLocally, getLimits } = require('../services/PremiumService');
        const uid = user?.id || user?.sub;
        if (uid) await saveTierLocally(uid, tier);
        if (updateUserProfile) updateUserProfile({ tier });
        const newLimits = await getLimits({ ...user, tier });
        setUserLimits(newLimits);
        console.log('[Profile] ✅ achat confirmé, tier mis à jour:', tier);

        // ═══ FIX 2026-04 : forcer un re-fetch DB pour récupérer la nouvelle ═══
        // valeur de premium_expires_at. Sans ça, l'ancien expiresAt (la date
        // expirée du précédent abonnement) reste en mémoire et le bandeau
        // "Abonnement expiré" peut se re-afficher au prochain render.
        // On attend ~500ms pour laisser le webhook RC propager côté backend.
        setTimeout(() => {
          if (reloadProfile) {
            console.log('[Profile] 🔄 reloadProfile post-achat → sync premiumExpiresAt');
            reloadProfile();
          }
        }, 500);
      } catch (e) {
        console.warn('[Profile] erreur post-achat:', e.message);
      }
    } catch (e) {
      console.error('[Profile] subscribe error:', e.message);
      Alert.alert(t('error'), e.message || t('purchase_failed'));
    } finally {
      setPurchasing(false);
    }
  };

  // ═══ SUPPRIMÉ : _handleSubscribe_legacy (TEST_MODE) ═══
  // Cette fonction contournait RevenueCat en appelant directement /set-tier
  // avec platform: 'test'. Elle a été supprimée car elle polluait les tests
  // d'intégration RC et créait de la confusion : les achats n'atterrissaient
  // pas dans RC ni dans Lambda webhook.
  // La production utilise uniquement handleSubscribe (ci-dessus) via RevenueCat.

  const handleRestorePurchases = async () => {
    if (restoring) return;
    setRestoring(true);
    // ═══ Helper : détecter si une erreur RevenueCat signifie "pas configuré" ═══
    // Ces erreurs techniques (singleton non configuré, SDK non initialisé, etc.)
    // ne doivent pas être affichées en brut à l'utilisateur. Elles signifient en
    // pratique : "il n'y a pas d'abonnement à restaurer sur cet appareil".
    // → on traite comme `kind: 'none'` (message élégant en français) au lieu
    //   d'`error` (qui afficherait un texte technique anglais).
    const isRcNotConfiguredError = (msg) => {
      if (!msg || typeof msg !== 'string') return false;
      const m = msg.toLowerCase();
      return m.includes('no singleton instance')
          || m.includes('configure purchases')
          || m.includes('configuring-sdk')
          || m.includes('sdk not configured')
          || m.includes('purchases is not configured');
    };
    try {
      // ═══ 1. Tenter via RevenueCat ═══
      const result = await restorePurchases(user);
      if (result?.success) {
        const sub = await fetchSubscription(user?.id, user?.accessToken);
        if (sub?.status === 'active') setUserPlan(sub.period || 'monthly');

        // ═══ FIX 2026-04 : synchroniser tier local + AsyncStorage + DB ═══
        // Sans ça, après restore le bandeau "expiré" continuait de s'afficher
        // car user.tier et user.premiumExpiresAt restaient stale dans le contexte.
        try {
          const restoredTier = result?.tier || sub?.tier || 'premium';
          const { saveTierLocally, getLimits } = require('../services/PremiumService');
          const uid = user?.id || user?.sub;
          if (uid) await saveTierLocally(uid, restoredTier);
          if (updateUserProfile) updateUserProfile({ tier: restoredTier });
          setCurrentTier(restoredTier);
          const newLimits = await getLimits({ ...user, tier: restoredTier });
          setUserLimits(newLimits);
          console.log(`[Profile] ✅ tier restauré localement: ${restoredTier}`);
          // Force re-fetch DB après ~500ms pour laisser le webhook RC propager
          setTimeout(() => {
            if (reloadProfile) {
              console.log('[Profile] 🔄 reloadProfile post-restore');
              reloadProfile();
            }
          }, 500);
        } catch (syncErr) {
          console.warn('[Profile] post-restore sync failed:', syncErr.message);
        }

        setRestoreResult({ kind: 'success' });
        setRestoring(false);
        return;
      }

      // ═══ 2. RC ne trouve pas — vérifier en DB ═══
      console.log('[Profile] RevenueCat did not find active sub, checking DB...');
      const sub = await fetchSubscription(user?.id, user?.accessToken);
      if (sub?.status === 'active') {
        setUserPlan(sub.period || 'monthly');
        // ═══ FIX 2026-04 : sync tier aussi quand on tombe sur DB ═══
        try {
          const dbTier = sub.tier || 'premium';
          const { saveTierLocally, getLimits } = require('../services/PremiumService');
          const uid = user?.id || user?.sub;
          if (uid) await saveTierLocally(uid, dbTier);
          if (updateUserProfile) updateUserProfile({ tier: dbTier });
          setCurrentTier(dbTier);
          const newLimits = await getLimits({ ...user, tier: dbTier });
          setUserLimits(newLimits);
          if (reloadProfile) setTimeout(() => reloadProfile(), 300);
        } catch {}
        setRestoreResult({
          kind: 'already',
          details: { tier: sub.tier || 'Premium', period: sub.period },
        });
        setRestoring(false);
        return;
      }

      // ═══ 3. Diagnostic selon le résultat RC ═══
      if (result?.error) {
        // Erreur RC : si c'est juste "pas configuré" → traiter comme "rien à restaurer"
        if (isRcNotConfiguredError(result.error)) {
          console.log('[Profile] RC not configured → treating as "nothing to restore"');
          setRestoreResult({ kind: 'none' });
        } else {
          setRestoreResult({ kind: 'error', details: { message: result.error } });
        }
      } else if (result?.allPurchases?.length > 0) {
        // RC voit des achats passés mais aucun actif → abonnement expiré/annulé
        setRestoreResult({
          kind: 'expired',
          details: { allPurchases: result.allPurchases },
        });
      } else {
        // Vraiment rien à restaurer
        setRestoreResult({ kind: 'none' });
      }
    } catch(e) {
      console.error('[Profile] restore error:', e.message);
      // Même règle pour les exceptions levées : si c'est RC pas configuré,
      // ce n'est pas une erreur user-facing, c'est juste qu'il n'y a rien à restaurer.
      if (isRcNotConfiguredError(e.message)) {
        setRestoreResult({ kind: 'none' });
      } else {
        setRestoreResult({ kind: 'error', details: { message: e.message || 'Erreur inconnue' } });
      }
    } finally {
      setRestoring(false);
    }
  };

  const handleChangePlan = async (planId) => {
    // v1.12.636 — Sur web : un changement de plan doit passer par Stripe
    // pour prélever le nouveau prix. Avant, on appelait juste /subscription PUT
    // qui changeait la DB mais ne créait aucune session Stripe → aucun
    // paiement réel du nouveau prix annuel. Maintenant : redirection vers
    // Stripe Checkout avec le tier + nouvelle période (Stripe gère la
    // proration via le webhook customer.subscription.updated).
    if (Platform.OS === 'web') {
      try {
        // v1.12.658 — Si user déjà abonné: change-subscription (no Checkout)
        // Sinon (free) → startCheckout (1er achat)
        const hasActiveSub = isPremiumUser && currentTier && currentTier !== 'free';
        // v1.12.664 — handleChangePlan reçoit soit:
        //   - 'yearly' / 'monthly' (juste switch period, garder tier)
        //   - 'basic_yearly' / 'premium' (tier+period explicite)
        let targetTier, targetPeriod;
        if (planId === 'yearly' || planId === 'monthly') {
          targetTier = currentTier;
          targetPeriod = planId;
        } else {
          targetTier = planId.replace('_yearly', '');
          targetPeriod = planId.includes('_yearly') ? 'yearly' : 'monthly';
        }
        if (hasActiveSub) {
          // v1.12.661 — Confirmation explicite avant change
          const tierLabel = targetTier.charAt(0).toUpperCase() + targetTier.slice(1);
          const periodLabel = targetPeriod === 'yearly' ? (isFR ? 'annuel' : 'yearly') : (isFR ? 'mensuel' : 'monthly');
          const currentLabel = (currentTier || 'free').charAt(0).toUpperCase() + (currentTier || 'free').slice(1);
          const currentPLabel = currentPeriod === 'yearly' ? (isFR ? 'annuel' : 'yearly') : (isFR ? 'mensuel' : 'monthly');
          // v1.12.662 — Message spécifique pour switch monthly → yearly SAME tier
          const isMonthlyToYearlySame = currentTier === targetTier && currentPeriod === 'monthly' && targetPeriod === 'yearly';
          const isYearlyToMonthlySame = currentTier === targetTier && currentPeriod === 'yearly' && targetPeriod === 'monthly';
          const YEARLY_PRICES = { basic: 59.90, premium: 99.90, gold: 169.90 };
          let confirmMsg;
          if (isMonthlyToYearlySame) {
            confirmMsg = isFR
              ? `Passer ${tierLabel} en annuel :\n\n• Vous payez ${YEARLY_PRICES[targetTier]} € maintenant pour 1 an d'abonnement.\n• Vos jours restants de l'abonnement mensuel sont préservés.\n• Votre abonnement annuel commencera à la fin de votre période mensuelle en cours.\n• Aucun jour perdu.`
              : `Switch ${tierLabel} to yearly:\n\n• You pay ${YEARLY_PRICES[targetTier]} € now for 1 year.\n• Your remaining monthly days are preserved.\n• Yearly subscription starts at the end of your current monthly period.\n• No days lost.`;
          } else if (isYearlyToMonthlySame) {
            confirmMsg = isFR
              ? `Passer ${tierLabel} en mensuel :\n\n• Vous gardez votre abonnement annuel jusqu'à sa date d'expiration.\n• Aucun remboursement (vous profitez de ce que vous avez payé).\n• La bascule en mensuel sera automatique à la fin de l'année.\n• Aucun prélèvement immédiat.`
              : `Switch ${tierLabel} to monthly:\n\n• You keep your yearly subscription until expiration.\n• No refund (you enjoy what you paid for).\n• Automatic switch to monthly at the end of the year.\n• No immediate charge.`;
          } else {
            confirmMsg = isFR
              ? `Voulez-vous passer de ${currentLabel} ${currentPLabel} à ${tierLabel} ${periodLabel} ?\n\nProrata facturé immédiatement si upgrade, ou bascule à la fin de période si downgrade.`
              : `Switch from ${currentLabel} ${currentPLabel} to ${tierLabel} ${periodLabel} ?\n\nProration billed immediately for upgrade, or applied at period end for downgrade.`;
          }
          const confirmIdx = await showAppAlert(
            isFR ? 'Confirmer le changement' : 'Confirm change',
            confirmMsg,
            [
              { text: isFR ? 'Annuler' : 'Cancel', style: 'cancel' },
              { text: isFR ? 'Confirmer' : 'Confirm', style: 'default' },
            ]
          );
          if (confirmIdx !== 1) return; // user cancelled
          const { changeSubscription } = require('../services/StripeService');
          const { showProcessing, hideProcessing } = require('../components/ProcessingOverlay');
          try {
            showProcessing(
              isFR ? 'Paiement en cours…' : 'Processing payment…',
              isFR ? `Finalisation de votre abonnement ${targetTier} ${targetPeriod === 'yearly' ? 'annuel' : 'mensuel'}.` : `Finalizing ${targetTier} ${targetPeriod}.`
            );
            const result = await changeSubscription(targetTier, targetPeriod);
            hideProcessing();
            if (result?.mode === 'updated') {
              showAppAlert(
                isFR ? 'Abonnement modifié' : 'Subscription updated',
                isFR ? `Votre nouveau plan ${targetTier} ${targetPeriod === 'yearly' ? 'annuel' : 'mensuel'} est actif immédiatement.`
                     : `Your new ${targetTier} ${targetPeriod} plan is active immediately.`
              );
              if (reloadProfile) {
                [1000, 3000, 6000, 12000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
              }
            } else if (result?.mode === 'prepaid_yearly') {
              // v1.12.662 — Switch monthly → yearly prepaid
              const effectiveDate = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
              const renewDate = result.nextRenewalAt ? new Date(result.nextRenewalAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
              showAppAlert(
                isFR ? '✓ Abonnement annuel confirmé' : '✓ Yearly subscription confirmed',
                isFR ? `Paiement de ${result.amountPaid} € reçu.\n\nVotre abonnement annuel commencera le ${effectiveDate} (à la fin de votre période mensuelle).\nProchain renouvellement : ${renewDate}.`
                     : `Payment of ${result.amountPaid} € received.\n\nYour yearly subscription will start on ${effectiveDate} (end of your monthly period).\nNext renewal: ${renewDate}.`
              );
              if (reloadProfile) {
                [1000, 3000, 6000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
              }
            } else if (result?.mode === 'scheduled') {
              const date = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString(isFR ? 'fr-FR' : 'en-US') : '';
              showAppAlert(
                isFR ? 'Changement programmé' : 'Change scheduled',
                isFR ? `Votre plan ${targetTier} ${targetPeriod === 'yearly' ? 'annuel' : 'mensuel'} sera actif le ${date}. Vous conservez votre plan actuel jusque-là.`
                     : `Your ${targetTier} ${targetPeriod} plan will be active on ${date}. You keep your current plan until then.`
              );
              if (reloadProfile) {
                [1000, 3000, 6000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
              }
            }
            return;
          } catch (e) {
            hideProcessing();
            const code = e?.code || '';
            const msg = e?.message || '';
            // v1.12.659 — Fallback Checkout si sub Stripe absente/cancelled
            if (code === 'no_active_subscription' || msg.includes('no_active_subscription') || msg.includes('Use create-checkout-session') || msg.includes('does not exist')) {
              console.warn('[Profile] changeSubscription failed → fallback Checkout:', msg);
              const { purchaseSubscriptionWithCredits } = require('../services/CreditAwarePurchase');
              await purchaseSubscriptionWithCredits({ tier: targetTier, period: targetPeriod, user, getValidToken, lang });
              return;
            }
            // v1.12.661 — Messages clairs au lieu de codes techniques
            if (code === 'already_on_this_plan' || msg.includes('already_on_this_plan')) {
              showAppAlert(
                isFR ? 'Plan identique' : 'Same plan',
                isFR ? `Vous êtes déjà abonné à ${tierLabel} ${periodLabel}. Choisissez un autre plan pour changer.`
                     : `You're already on ${tierLabel} ${periodLabel}. Choose a different plan to switch.`
              );
              return;
            }
            // v1.12.667/669 — Schedule pending déjà programmé
            if (code === 'already_scheduled_to_this_plan' || msg.includes('already_scheduled_to_this_plan')) {
              showAppAlert(
                isFR ? 'Changement déjà programmé' : 'Change already scheduled',
                isFR ? `Un passage à ${tierLabel} ${periodLabel} est déjà programmé. Vous ne pouvez pas le re-souscrire.`
                     : `A switch to ${tierLabel} ${periodLabel} is already scheduled. You cannot re-subscribe.`
              );
              return;
            }
            // v1.12.670 — Conflit pending → propose d'annuler le pending
            if (code === 'pending_change_conflict' || msg.includes('pending_change_conflict')) {
              const confirmIdx = await showAppAlert(
                isFR ? 'Changement programmé en attente' : 'Pending change',
                isFR ? 'Vous avez déjà un changement d\'abonnement programmé. Voulez-vous l\'annuler pour faire ce nouveau changement ?\n\nSi votre changement programmé était un paiement annuel prépayé, vous serez remboursé.'
                     : 'You have a pending subscription change. Cancel it to make this new change?\n\nIf your pending change was a prepaid yearly, you will be refunded.',
                [
                  { text: isFR ? 'Garder l\'ancien' : 'Keep pending', style: 'cancel' },
                  { text: isFR ? 'Annuler le pending' : 'Cancel pending', style: 'destructive' },
                ]
              );
              if (confirmIdx === 1) {
                try {
                  const { cancelPendingChange } = require('../services/StripeService');
                  const result = await cancelPendingChange();
                  const refundMsg = result?.refunded ? (isFR ? ` Remboursement: ${result.refunded} €.` : ` Refund: ${result.refunded} €.`) : '';
                  showAppAlert(
                    isFR ? 'Changement annulé' : 'Pending cancelled',
                    (isFR ? 'Votre changement programmé a été annulé.' : 'Your pending change was cancelled.') + refundMsg + (isFR ? '\n\nVous pouvez maintenant faire un nouveau changement.' : '\n\nYou can now make a new change.')
                  );
                  if (reloadProfile) {
                    [500, 2000, 5000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
                  }
                } catch (e2) {
                  showAppAlert(
                    isFR ? 'Erreur' : 'Error',
                    e2?.message || (isFR ? 'Impossible d\'annuler le changement' : 'Cannot cancel pending change')
                  );
                }
              }
              return;
            }
            throw e;
          }
        }
        // Sinon (premier achat depuis free): Checkout (avec application Crédit Mytenue auto)
        try {
          const { purchaseSubscriptionWithCredits } = require('../services/CreditAwarePurchase');
          await purchaseSubscriptionWithCredits({ tier: targetTier, period: targetPeriod, user, getValidToken, lang });
        } catch (e) {
          // v1.12.672 — Si backend dit "déjà abonné", fallback auto vers change-sub
          const code = e?.code || '';
          if (code === 'already_subscribed' || (e?.message || '').includes('already_subscribed')) {
            console.warn('[Profile] startCheckout → already_subscribed, fallback changeSubscription');
            const { changeSubscription } = require('../services/StripeService');
            try {
              const result = await changeSubscription(targetTier, targetPeriod);
              if (result?.mode === 'updated') {
                showAppAlert(isFR ? 'Abonnement modifié' : 'Subscription updated', isFR ? 'Plan actif immédiatement.' : 'Plan active immediately.');
              } else if (result?.mode === 'scheduled') {
                showAppAlert(isFR ? 'Changement programmé' : 'Change scheduled', isFR ? 'Bascule à la fin de la période.' : 'Switch at period end.');
              } else if (result?.mode === 'prepaid_yearly') {
                showAppAlert(isFR ? '✓ Annuel confirmé' : '✓ Yearly confirmed', isFR ? `Paiement de ${result.amountPaid}€ reçu.` : `Payment of ${result.amountPaid}€ received.`);
              }
              if (reloadProfile) [1000, 3000, 6000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
            } catch (e2) {
              // Si le fallback retourne pending_change_conflict, popup spécifique
              const code2 = e2?.code || '';
              if (code2 === 'pending_change_conflict') {
                showAppAlert(
                  isFR ? 'Changement en attente' : 'Pending change',
                  isFR ? 'Vous avez un changement programmé. Annulez-le d\'abord depuis votre profil.'
                       : 'You have a pending change. Cancel it first from your profile.'
                );
              } else {
                Alert.alert(t('error'), e2?.message || t('cannot_modify_plan'));
              }
            }
            return;
          }
          throw e;
        }
      } catch (e) {
        console.warn('[Profile] startCheckout for plan change failed:', e?.message);
        Alert.alert(t('error'), e?.message || t('cannot_modify_plan'));
      }
      return;
    }
    // Mobile (iOS/Android) : un changement de période DOIT passer par un achat
    // StoreKit / RevenueCat réel (RC.purchasePackage) — Apple/Google gèrent le
    // paiement, la proration et le changement de produit (→ webhook PRODUCT_CHANGE).
    // v1.12.946 — Avant : on appelait changePlan() → PUT /subscription (DB seulement)
    //   → AUCUN paiement ni changement réel côté Apple. L'user « passait à l'annuel »
    //   sans jamais être facturé l'annuel et restait sur le produit mensuel Apple.
    //   Maintenant : vrai achat IAP, comme le bouton « Changer de formule ».
    const targetTier = (planId === 'yearly' || planId === 'monthly')
      ? (currentTier && currentTier !== 'free' ? currentTier : 'premium')
      : planId.replace('_yearly', '');
    const targetPeriod = planId.includes('yearly') ? 'yearly' : 'monthly';
    try {
      const result = await purchaseSubscription(targetTier, targetPeriod, user);
      if (result?.cancelled) return;            // user a annulé la fiche de paiement Apple/Google
      if (!result?.success) throw new Error('purchase_failed');
      setUserPlan(targetPeriod);
      setShowManage(false);
      // purchaseSubscription émet 'subscription_changed' → AuthContext recharge le
      // profil ; on ajoute des retries ici aussi par sécurité.
      if (reloadProfile) [1000, 3000, 6000, 12000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
      const tierData = TIERS[targetTier] || TIERS.premium;
      const priceMonth = targetPeriod === 'yearly'
        ? (tierData.priceYearlyMonth || tierData.priceYearly / 12).toFixed(2).replace('.', ',')
        : tierData.priceMonthly.toFixed(2).replace('.', ',');
      const priceLabelKey = targetPeriod === 'yearly' ? 'plan_change_yearly' : 'plan_change_monthly';
      Alert.alert(t('plan_modified'), t(priceLabelKey).replace('{price}', priceMonth));
    } catch (e) {
      Alert.alert(t('error'), t('cannot_modify_plan'));
    }
  };

  // v1.12.608 — V5.6 : exécute l'annulation Stripe (extrait de handleUnsubscribe)
  // Appelé par le bouton "Confirmer" de DowngradeFreeConfirmModal sur web
  // ET depuis le Alert.alert sur mobile.
  const performCancelSubscription = async () => {
    const expiresAt = user?.premiumExpiresAt;
    let dateStr = 'la fin de la période en cours';
    if (expiresAt) {
      const d = new Date(expiresAt);
      if (!isNaN(d.getTime())) {
        const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
        dateStr = `le ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
      }
    }
    try {
      const { showProcessing, hideProcessing } = require('../components/ProcessingOverlay');
      showProcessing(
        isFR ? 'Résiliation en cours…' : 'Cancelling…',
        isFR ? 'Annulation de votre abonnement, vous gardez accès jusqu\'à expiration.' : 'Cancelling your subscription, you keep access until expiration.'
      );
      const result = await cancelSubscription();
      hideProcessing();
      if (result?.success) {
        if (updateUserProfile) updateUserProfile({ subscriptionCancelled: true });
        setShowManage(false);
        setShowDowngradeConfirm(false);
        Alert.alert(
          'Abonnement annulé',
          `Votre accès Premium reste actif jusqu'au ${dateStr.replace(/^le /, '')}.\n\n` +
          `Aucun prélèvement supplémentaire ne sera effectué.\n\n` +
          `Vos abonnements Dressing additionnels seront automatiquement résiliés à la fin de la période.`,
          [{ text: 'OK' }]
        );
        if (reloadProfile) setTimeout(() => reloadProfile(), 500);
        return;
      }
      const errCode = result?.error || 'unknown';
      const errMsg  = result?.message || '';
      console.warn(`[Profile] cancel failed: code=${errCode} msg=${errMsg}`);
      let userMsg;
      switch (errCode) {
        case 'endpoint_not_found':
          userMsg = "Service de désabonnement temporairement indisponible. Réessayez dans quelques minutes ou contactez le support.";
          break;
        case 'unauthorized':
          userMsg = "Votre session a expiré. Reconnectez-vous puis réessayez.";
          break;
        case 'network':
          userMsg = "Pas de connexion internet. Vérifiez votre réseau et réessayez.";
          break;
        case 'server_error':
          userMsg = `Erreur côté serveur. Réessayez dans un instant.\n\n(${errMsg})`;
          break;
        default:
          userMsg = `Impossible de désactiver l'abonnement.\n\n${errMsg || 'Réessayez dans un instant ou contactez le support.'}`;
      }
      Alert.alert('Erreur', userMsg);
    } catch (e) {
      try { require('../components/ProcessingOverlay').hideProcessing(); } catch {}
      console.warn('[Profile] cancelSubscription threw:', e?.message);
      Alert.alert('Erreur', `Impossible de désactiver l'abonnement.\n\n${e?.message || 'Erreur inattendue'}`);
    }
  };

  const handleUnsubscribe = () => {
    // v1.12.608 — Sur web : preview détaillé via DowngradeFreeConfirmModal
    // (Alert.alert ne fonctionne pas correctement sur RN-Web). Sur mobile :
    // garder le Alert natif classique.
    if (Platform.OS === 'web') {
      setShowDowngradeConfirm(true);
      return;
    }
    // ═══ FIX 2026-04 : annulation conforme à la pratique standard ═══
    // Avant : on basculait IMMÉDIATEMENT le user en free (côté local seulement,
    //   la DB restait active). Bug double : (1) l'user perdait son accès
    //   payé pour la fin de période, (2) la DB restait en active → tier
    //   premium en backend → incohérence.
    // Maintenant : on appelle DELETE /subscription qui marque la sub comme
    //   cancelled mais garde l'accès jusqu'à current_period_end. Le user voit
    //   la date d'expiration et bénéficie de son abonnement jusqu'à cette date.

    // Calcul de la date d'expiration formatée à la française
    const expiresAt = user?.premiumExpiresAt;
    let dateStr = 'la fin de la période en cours';
    if (expiresAt) {
      const d = new Date(expiresAt);
      if (!isNaN(d.getTime())) {
        const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
        dateStr = `le ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
      }
    }

    Alert.alert(
      'Se désabonner',
      `Votre abonnement Premium restera actif jusqu'au ${dateStr.replace(/^le /, '')}.\n\n` +
      `Après cette date, votre compte basculera automatiquement sur la formule gratuite.\n\n` +
      `Vous pouvez réactiver votre abonnement à tout moment avant l'expiration.`,
      [
        { text: 'Annuler', style: 'cancel' },
        {
          text: 'Se désabonner',
          style: 'destructive',
          onPress: async () => {
            try {
              const { showProcessing, hideProcessing } = require('../components/ProcessingOverlay');
              showProcessing('Résiliation en cours…', 'Annulation de votre abonnement.');
              const result = await cancelSubscription();
              hideProcessing();
              // ═══ v1.7.56 : on accepte aussi "pas de sub active" comme succès ═══
              // Cas typiques où ça arrive :
              //   1. User a déjà annulé (re-clic) → DB n'a pas de sub active
              //   2. User en environnement sandbox/test sans sub réelle en DB
              //      mais profile.tier = 'premium' (set manuellement via admin)
              // Dans ces cas, on flag quand même 'cancelled' localement pour
              // que l'UI reflète l'état "désabonné" et que le texte change.
              if (result?.success) {
                // L'accès reste actif jusqu'à expiration côté DB.
                // On ne change PAS currentTier ni userPlan localement —
                // l'user doit voir qu'il a encore accès à ses avantages.
                if (updateUserProfile) {
                  updateUserProfile({ subscriptionCancelled: true });
                }
                setShowManage(false);
                Alert.alert(
                  'Abonnement annulé',
                  `Votre accès Premium reste actif jusqu'au ${dateStr.replace(/^le /, '')}.\n\n` +
                  `Aucun prélèvement supplémentaire ne sera effectué.`,
                  [{ text: 'OK' }]
                );
                if (reloadProfile) setTimeout(() => reloadProfile(), 500);
                return;
              }

              // ═══ Diagnostic d'erreur précis (v1.7.56) ═══
              // Avant : "Impossible de désactiver" générique sans info.
              // Maintenant : on connaît la cause via result.error et on
              // affiche un message adapté + log précis pour le debug.
              const errCode = result?.error || 'unknown';
              const errMsg  = result?.message || '';
              console.warn(`[Profile] cancel failed: code=${errCode} msg=${errMsg}`);

              let userMsg;
              switch (errCode) {
                case 'endpoint_not_found':
                  userMsg = "Service de désabonnement temporairement indisponible. Une mise à jour est en cours côté serveur. Réessayez dans quelques minutes ou contactez le support.";
                  break;
                case 'unauthorized':
                  userMsg = "Votre session a expiré. Reconnectez-vous puis réessayez.";
                  break;
                case 'network':
                  userMsg = "Pas de connexion internet. Vérifiez votre réseau et réessayez.";
                  break;
                case 'server_error':
                  userMsg = `Erreur côté serveur. Réessayez dans un instant.\n\n(${errMsg})`;
                  break;
                default:
                  userMsg = `Impossible de désactiver l'abonnement.\n\n${errMsg ? errMsg : 'Réessayez dans un instant ou contactez le support.'}`;
              }
              Alert.alert('Erreur', userMsg);
            } catch (e) {
              try { require('../components/ProcessingOverlay').hideProcessing(); } catch {}
              console.warn('[Profile] cancelSubscription threw:', e?.message);
              Alert.alert(
                'Erreur',
                `Impossible de désactiver l'abonnement.\n\n${e?.message || 'Erreur inattendue'}`
              );
            }
          },
        },
      ]
    );
  };

  // useAuth() remonté tout en haut du composant (v1.12.117) — voir ligne 93

  // ═══ FIX 2026-04 (v1.7.64) : isPremiumUser respecte l'expiration ═══
  // Avant : isPremiumUser = userPlan === 'monthly' || 'yearly'. Bug : si la
  //   date premiumExpiresAt était passée mais que userPlan local restait
  //   "yearly" (cache AsyncStorage), on affichait "✓ PLAN ACTIF" + carte
  //   Premium dorée alors que l'abonnement avait expiré la veille.
  // Maintenant : on combine 3 conditions :
  //   - userPlan dit qu'on est sur un plan payant
  //   - currentTier (issu de getTier qui détecte l'expiration) n'est PAS 'free'
  //   - La date d'expiration n'est pas dépassée (si présente)
  const isPremiumUser = (() => {
    const planMatch = userPlan === 'monthly' || userPlan === 'yearly';
    if (!planMatch) return false;
    if (currentTier === 'free') return false; // tier déjà rebasculé en free
    // Check explicite de la date d'expiration
    const expiresAt = user?.premiumExpiresAt;
    if (expiresAt) {
      const t = new Date(expiresAt).getTime();
      if (!isNaN(t) && t < Date.now()) {
        // Date expirée → ne plus afficher comme Premium actif
        return false;
      }
    }
    return true;
  })();

  // ═══ FIX 2026-04 (v1.7.69) : hasPremiumState pour permettre désabonnement ═══
  // Permet d'afficher la carte Premium et le bouton "Se désabonner" même si
  // l'abonnement est techniquement expiré côté local. Cas d'usage :
  //   - L'app pense que c'est expiré (premiumExpiresAt en cache stale) mais
  //     l'abonnement Apple/Google est encore actif → user veut désabonner
  //     pour éviter le renouvellement automatique
  //   - L'app a perdu la trace mais le user voit dans App Store qu'il est abonné
  //   - Cas de bug détection expiration → l'user reste bloqué sans pouvoir cliquer
  // Critère : un signe quelconque indique un état payant (pas free).
  const hasPremiumState = (
    isPremiumUser
    || (currentTier && currentTier !== 'free')
    || userPlan === 'monthly'
    || userPlan === 'yearly'
  );
  // ═══ FIX 2026-04 : libellé dynamique selon currentTier + currentPeriod ═══
  const currentPlanLabel = (() => {
    if (!isPremiumUser || !currentTier || currentTier === 'free') return null;
    const tierData = TIERS[currentTier];
    if (!tierData) return null;
    if (userPlan === 'yearly') {
      const m = (tierData.priceYearlyMonth || tierData.priceYearly / 12).toFixed(2).replace('.', ',');
      return t('yearly_price').replace('{price}', m);
    }
    return t('monthly_price').replace('{price}', tierData.priceMonthly.toFixed(2).replace('.', ','));
  })();

  // Admin check — affiche le bouton 🔐 Admin dans ACCÈS RAPIDE si true
  const [isAdmin, setIsAdmin] = React.useState(false);
  React.useEffect(() => {
    if (user?.accessToken) {
      checkIsAdmin(user).then(setIsAdmin).catch(() => setIsAdmin(false));
    }
  }, [user?.accessToken]);
  const profile = user || userProfile;
  // ═══ DEBUG : log gender pour diagnostiquer l'absence du badge ═══
  React.useEffect(() => {
    console.log('[ProfileScreen] profile.gender =', JSON.stringify(profile?.gender), 'typeof=', typeof profile?.gender);
  }, [profile?.gender]);
  const [notifs,  setNotifs]  = useState(true);
  const [statsData, setStatsData] = useState({ looks: 0, pieces: 0, purchases: 0, aiAnalyzed: 0, favorites: 0, trips: 0 });
  const [styleProfile, setStyleProfile] = useState(null);
  // currentTier remonté plus haut (juste après userPlan) pour éviter TDZ web — v1.12.117
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' | 'yearly'
  const [showAllPlans, setShowAllPlans] = useState(false); // voir tous les plans en mode abonné
  // ═══ Ref sur le wrap des plans pour scroll précis (v1.7.56 → v1.7.87) ═══
  // Avant : on faisait scrollToEnd avec timeout 150ms après setShowAllPlans(true).
  //   Bug : à 150ms le rendu n'est pas terminé (PlanComparisonTable + billing
  //   toggle + 3 plans s'ajoutent), donc scrollToEnd mesure une hauteur partielle
  //   et ne scrolle pas au bon endroit. L'user reste sur la carte Premium.
  // ═══ FIX 2026-04 (v1.7.87) : 2 refs distinctes ═══
  //   Le tableau des plans est rendu à 2 endroits possibles :
  //     (1) "Top" : juste sous la carte Premium (quand un abonné cliquez sur le
  //         bouton doré "Changer de formule")
  //     (2) "Bottom" : en bas de l'écran dans la section "Choisir une formule"
  //         (quand l'user est free OU quand un abonné a toggle showAllPlans
  //         depuis le 2ème bouton "Changer de formule")
  //   Avant on utilisait la même ref `plansWrapRef` sur les 2 emplacements →
  //   React écrasait la valeur (la dernière vue rendue gagnait). Selon les
  //   conditions, le scroll ciblait la mauvaise position. Maintenant chaque
  //   emplacement a sa propre ref et le useEffect scrolle vers celle qui est
  //   actuellement montée.
  const plansWrapRefTop    = React.useRef(null);
  const plansWrapRefBottom = React.useRef(null);
  const plansLayoutTimer   = React.useRef(null);
  const scrollYRef         = React.useRef(0); // offset de scroll courant (pour scroll précis iOS)
  const [scrollToPlansRequested, setScrollToPlansRequested] = useState(false);

  // ═══ v1.12.949 — Scroll vers le tableau des plans, piloté par onLayout ═══
  // Avant : requestAnimationFrame + setTimeout(80) puis measureLayout avec
  //   retries → souvent la cible n'était pas encore mesurée → abandon ou scroll
  //   au mauvais endroit → l'écran ne descendait pas. Maintenant : le scroll est
  //   déclenché par le onLayout du wrapper du tableau (garanti APRÈS rendu+mesure),
  //   donc fiable. On scrolle à chaque passe de layout tant que la demande est
  //   active (la dernière passe = position finale), puis on désarme.
  const scrollToPlansNow = React.useCallback(() => {
    const node = plansWrapRefTop.current || plansWrapRefBottom.current;
    const scrollNode = scrollRef.current;
    if (!node || !scrollNode || !node.measure) return;
    try {
      // v1.12.950 — measure() = position absolue à l'écran (fiable iOS natif,
      // contrairement à measureLayout/getInnerViewNode qui échouait silencieusement).
      // target = offset de scroll courant + position écran du tableau − marge haute.
      node.measure((x, y, w, h, pageX, pageY) => {
        if (typeof pageY !== 'number') return;
        const target = (scrollYRef.current || 0) + pageY - 110;
        scrollNode.scrollTo?.({ y: Math.max(0, target), animated: true });
      });
    } catch {}
  }, []);

  const onPlansLayout = React.useCallback(() => {
    if (!scrollToPlansRequested) return;
    if (plansLayoutTimer.current) clearTimeout(plansLayoutTimer.current);
    plansLayoutTimer.current = setTimeout(scrollToPlansNow, 120);
  }, [scrollToPlansRequested, scrollToPlansNow]);

  React.useEffect(() => {
    if (!scrollToPlansRequested) return;
    // 1er essai immédiat (si onLayout a déjà eu lieu) + désarmement après 1,2 s
    const t0 = setTimeout(scrollToPlansNow, 60);
    const t1 = setTimeout(() => setScrollToPlansRequested(false), 1200);
    return () => { clearTimeout(t0); clearTimeout(t1); };
  }, [scrollToPlansRequested, scrollToPlansNow]);

  // ═══ Wallet cashback CJ ═══
  const [wallet,          setWallet]          = useState(null);
  const [walletHistory,   setWalletHistory]   = useState([]);
  const [loadingWallet,   setLoadingWallet]   = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [paypalEmail,     setPaypalEmail]     = useState('');
  const [withdrawing,     setWithdrawing]     = useState(false);
  // v1.12.847 — UseCreditsModal state
  const [showUseCreditsModal, setShowUseCreditsModal] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  // v1.12.848 — Historique crédits (transactions earn/use/expire/validated)
  const [creditHistory, setCreditHistory] = useState([]);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  // v1.12.967 — Replier l'historique des transactions Mytenue en quittant le menu
  // (collapse par défaut au changement de menu).
  useFocusEffect(React.useCallback(() => {
    return () => setShowCreditHistory(false);
  }, []));
  React.useEffect(() => {
    if (!user?.accessToken) { setCreditBalance(null); setCreditHistory([]); return; }
    (async () => {
      try {
        const token = getValidToken ? await getValidToken() : user.accessToken;
        const [b, h] = await Promise.all([
          getCreditBalance(token),
          getCreditHistory(token, 50),
        ]);
        setCreditBalance(b);
        setCreditHistory(Array.isArray(h) ? h : []);
      } catch {}
    })();
  }, [user?.id, showUseCreditsModal]);

  // v1.12.848 — Nombre d'achats qualifiants = transactions 'earn'
  const purchaseCount = React.useMemo(
    () => creditHistory.filter(t => t.type === 'earn').length,
    [creditHistory]
  );

  // Si l'utilisateur a un abonnement actif, forcer billingPeriod sur sa vraie période
  // (userPlan vient du serveur : 'monthly' ou 'yearly')
  React.useEffect(() => {
    if (userPlan === 'monthly' || userPlan === 'yearly') {
      setBillingPeriod(userPlan);
    }
  }, [userPlan]);

  // ═══ Charger le tier effectif de l'utilisateur ═══
  // v1.7.60 : on ajoute premiumExpiresAt aux deps pour recalculer le tier
  // dès que la date d'expiration est franchie (côté getTier, isExpired check).
  React.useEffect(() => {
    (async () => {
      try {
        const { getTier } = require('../services/PremiumService');
        const t = await getTier(user);
        setCurrentTier(t);
      } catch { setCurrentTier('free'); }
    })();
  }, [user?.id, user?.tier, user?.plan, user?.premiumExpiresAt]);

  // ═══ Timer auto-rebascule en free à l'expiration (v1.7.60) ═══
  // Si l'app reste ouverte au moment où la date d'expiration est franchie,
  // on doit déclencher le recalcul tier+limites SANS attendre que l'user
  // ferme/rouvre l'app ou navigue ailleurs. On programme un setTimeout sur
  // le delta jusqu'à expiresAt + 1 seconde de marge.
  React.useEffect(() => {
    if (!user?.id) return;
    const expiresAt = user?.premiumExpiresAt;
    if (!expiresAt) return;
    const t = new Date(expiresAt).getTime();
    if (isNaN(t)) return;
    const deltaMs = t - Date.now();
    // Si déjà expiré OU expire dans plus de 24h, pas besoin de timer
    // (les autres useEffects géreront). On vise les 24h précédant l'expiration.
    if (deltaMs <= 0 || deltaMs > 24 * 60 * 60 * 1000) return;
    console.log(`[Profile] expiration timer armé pour dans ${Math.round(deltaMs / 1000)}s`);
    const id = setTimeout(async () => {
      console.log('[Profile] ⏰ abonnement vient d\'expirer → rebascule en free');
      try {
        const { saveTierLocally, getLimits } = require('../services/PremiumService');
        const uid = user?.id;
        if (uid) await saveTierLocally(uid, 'free');
        if (updateUserProfile) updateUserProfile({ tier: 'free' });
        setCurrentTier('free');
        setUserPlan('free');
        const newLimits = await getLimits({ ...user, tier: 'free' });
        setUserLimits(newLimits);
        // Notifier les autres écrans (Looks, Wardrobe, Home) pour qu'ils
        // recalculent leurs propres compteurs/limites
        DeviceEventEmitter.emit('tier_changed', { tier: 'free', reason: 'expired' });
      } catch (e) {
        console.warn('[Profile] auto-expire failed:', e?.message);
      }
    }, deltaMs + 1000);
    return () => clearTimeout(id);
  }, [user?.id, user?.premiumExpiresAt, updateUserProfile]);

  // ═══ Charger le wallet cashback CJ (v1.12.621 — throttle 30s) ═══
  // Avant : useFocusEffect dépendait de loadWallet qui dépendait de
  // user.accessToken → à chaque refresh token, loadWallet recréée → focus
  // effect ré-fire → 12 calls /cj/wallet en rafale → Lambda 500.
  // Maintenant : deps stable (user.id seulement) + throttle 30s via ref.
  const walletLastFetchRef = React.useRef(0);
  const loadWallet = useCallback(async (force = false) => {
    if (!user?.id && !user?.sub) return;
    const now = Date.now();
    if (!force && now - walletLastFetchRef.current < 30000) {
      return; // throttle 30s
    }
    walletLastFetchRef.current = now;
    setLoadingWallet(true);
    try {
      const [w, h] = await Promise.all([
        getWallet(user),
        getHistory(user, 20),
      ]);
      setWallet(w);
      setWalletHistory(h || []);
      if (w?.paypal_email) setPaypalEmail(w.paypal_email);
    } catch(e) {
      console.warn('[Profile] loadWallet:', e.message);
    } finally {
      setLoadingWallet(false);
    }
  }, [user?.id, user?.sub]);

  // v1.12.633 — deps [] + ref pour éviter cascade re-fire (loadWallet a
  // déjà son propre throttle 30s en interne).
  const loadWalletRef = React.useRef(loadWallet);
  React.useEffect(() => { loadWalletRef.current = loadWallet; }, [loadWallet]);
  useFocusEffect(useCallback(() => {
    loadWalletRef.current?.();
  }, []));

  // ═══ FIX 2026-04 : reload profil au focus + listener tier_changed ═══
  // v1.12.633 — deps [] + ref pour éviter cascade re-fire. reloadProfileRef
  // déjà défini plus haut (ligne ~329).
  useFocusEffect(useCallback(() => {
    if (reloadProfileRef.current) reloadProfileRef.current();
  }, []));

  // ═══ FIX 2026-04 (v1.7.61) : mise à jour optimistique des compteurs ═══
  // Avant : tier_changed déclenchait juste reloadProfile() qui re-fetch la DB
  //   (~500ms-1s). Pendant ce temps, l'écran affichait encore les ANCIENS
  //   max de compteurs (ex: en upgrade Free→Premium, on voyait encore "3/3 IA"
  //   au lieu de "3/30 IA" tant que la DB n'avait pas répondu).
  // Maintenant : on lit le tier dans le payload de l'event et on met à jour
  //   currentTier + userLimits IMMÉDIATEMENT côté React. Le reloadProfile()
  //   suit en arrière-plan pour resync premiumExpiresAt + autres champs DB.
  // v1.12.685 — FIX RENDER LOOP INFINI : deps [reloadProfile, user] →
  // `user` est un OBJET recréé à chaque setUser (reloadProfile fait
  // setUser(prev => ({...prev}))). Nouvelle ref → ce useEffect cleanup +
  // re-run en boucle → addListener/remove en rafale → re-render cascade
  // gs/hs + re-fire de tous les fetch mount (/wardrobe, /tryon/usage,
  // /admin/is-admin, GA). Fix: deps [] + lecture via refs (userRef,
  // reloadProfileRef déjà déclarés lignes 337-342).
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('tier_changed', async (payload) => {
      const newTier = payload?.tier;
      console.log(`[ProfileScreen] tier_changed event tier=${newTier} → optimistic update`);
      if (newTier && TIERS[newTier]) {
        setCurrentTier(newTier);
        try {
          const newLimits = await getLimits({ ...userRef.current, tier: newTier });
          setUserLimits(newLimits);
        } catch {}
      }
      if (reloadProfileRef.current) reloadProfileRef.current();
    });
    return () => sub.remove();
  }, []);

  // Demander un retrait PayPal
  const handleWithdraw = async () => {
    if (!paypalEmail || !paypalEmail.includes('@')) {
      Alert.alert(t('paypal_email_required'), t('paypal_email_required_msg'));
      return;
    }
    const available = Number(wallet?.cashback_available_eur || 0);
    if (available < WITHDRAWAL_MIN_EUR) {
      Alert.alert(
        'Montant insuffisant',
        `Vous devez avoir au moins ${WITHDRAWAL_MIN_EUR}€ disponibles pour retirer. Actuellement : ${available.toFixed(2)}€.`,
      );
      return;
    }
    setWithdrawing(true);
    try {
      const res = await requestWithdrawal(user, available, paypalEmail);
      if (res.success) {
        Alert.alert(
          '✓ Demande envoyée',
          `Votre demande de retrait de ${available.toFixed(2)}€ a été enregistrée. Vous recevrez le paiement sous 3-5 jours ouvrés.`,
        );
        setShowWithdrawForm(false);
        loadWallet(); // refresh
      } else {
        Alert.alert('Erreur', res.error || 'Demande impossible pour le moment.');
      }
    } catch(e) {
      Alert.alert('Erreur', e.message);
    } finally {
      setWithdrawing(false);
    }
  };

  const loadStats = React.useCallback(async () => {
    try {
      const token  = await (getValidToken ? getValidToken().catch(() => null) : Promise.resolve(null));
      const userId = user?.id || user?.sub || null;
      console.log('[Profile] loadStats - userId:', userId, 'has token:', !!token);

      const wardrobe = (token && userId)
        ? await getWardrobe(userId, token).catch(e => { console.warn('[Profile] getWardrobe:', e.message); return []; })
        : [];
      const arr    = Array.isArray(wardrobe) ? wardrobe : [];
      const pieces = arr.length;

      // Articles déjà analysés par l'IA (vrai compteur)
      const aiAnalyzed = arr.filter(i => i.ai_metadata?.analyzedAt).length;

      // ═══ FIX 2026-04 (v1.7.68) : Looks générés = même valeur que LooksScreen ═══
      // Avant : on faisait generateOutfits(arr, 18, null, 99) (moteur v1 simple).
      //   Résultat différent du compteur "Tout" dans LooksScreen (moteur v2
      //   avec dédup + filtre qualité). Ex: Profile = 87, Looks/Tout = 12.
      // Maintenant : on lit la valeur sauvegardée par LooksScreen dans
      //   AsyncStorage (clé outfit_looks_count_<userId>). Si jamais elle
      //   n'existe pas (1er lancement avant ouverture de Looks), on fallback
      //   sur generateOutfits avec dédup pour rester proche de la vraie valeur.
      let looks = 0;
      if (pieces > 0) {
        let fromLooks = null;
        try {
          const uid = user?.id || user?.sub;
          if (uid) {
            const cached = await AsyncStorage.getItem(`outfit_looks_count_${uid}`);
            if (cached) {
              const parsed = JSON.parse(cached);
              if (parsed && typeof parsed.total === 'number') {
                // Ne fait confiance que si la valeur a été mise à jour récemment
                // (< 7 jours). Sinon le dressing a probablement changé entre temps.
                const age = Date.now() - (parsed.updated_at || 0);
                if (age < 7 * 24 * 3600 * 1000) {
                  fromLooks = parsed.total;
                }
              }
            }
          }
        } catch {}

        if (fromLooks !== null) {
          looks = fromLooks;
        } else {
          // Fallback : génération locale + dédup pour s'approcher du moteur v2
          try {
            const outfits = generateOutfits(arr, 18, null, 99);
            // Dédup basique (même items = même outfit)
            const seen = new Set();
            const unique = (outfits || []).filter(o => {
              const key = (o.items || [])
                .map(i => i.id || `${i.name}|${i.brand}`)
                .sort().join('||');
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            looks = unique.length;
          } catch(e) { looks = 0; }
        }
      }

      // v1.12.148 — Achats = nombre de conversions trackées par cashback (wallet)
      // Avant : count des items wardrobe avec source='manual_confirm'/'purchase' (compte
      //   tout ajout manuel, même sans cashback réel). Maintenant : on lit
      //   total_conversions du wallet CJ — c'est l'achat tracké et confirmé via
      //   le réseau d'affiliation, donnant droit au cashback.
      let purchases = 0;
      if (token && userId) {
        try {
          const wallet = await getWallet(user);
          purchases = Number(wallet?.total_conversions) || 0;
        } catch (e) {
          console.warn('[Profile] getWallet failed:', e?.message);
        }
      }

      // Favoris (AsyncStorage)
      let favorites = 0;
      try {
        const favs = await getFavorites();
        favorites = Array.isArray(favs) ? favs.length : 0;
      } catch {}

      // Voyages (AsyncStorage)
      let trips = 0;
      try {
        const rawTrips = await AsyncStorage.getItem('outfit_trips');
        if (rawTrips) {
          const parsed = JSON.parse(rawTrips);
          trips = Array.isArray(parsed) ? parsed.length : 0;
        }
      } catch {}

      console.log('[Profile] stats:', { pieces, aiAnalyzed, looks, purchases, favorites, trips });
      setStatsData({ looks, pieces, purchases, aiAnalyzed, favorites, trips });

      // Profil de style depuis les ai_metadata
      const profile = computeStyleProfile(arr);
      setStyleProfile(profile);
    } catch(e) {
      console.warn('[Profile] loadStats:', e.message);
    }
  }, [user?.id, user?.sub]);
  // v1.12.633 — Deps stables (user.id/sub) au lieu de [user, getValidToken]
  // → loadStats n'est plus recréé à chaque setUser → useEffect + useFocusEffect
  // ci-dessous ne re-fire plus en boucle. getValidToken lu via ref dans le body.

  useEffect(() => { loadStats(); loadStatsRef.current = loadStats; }, [loadStats]);
  // Recharger à chaque affichage du profil (si on revient du dressing après analyse)
  // Throttle via loadStatsLastRef pour éviter re-fetch trop fréquent.
  const loadStatsLastRef = React.useRef(0);
  useFocusEffect(useCallback(() => {
    const now = Date.now();
    if (now - loadStatsLastRef.current < 30000) return;
    loadStatsLastRef.current = now;
    loadStatsRef.current?.();
  }, []));
  // Écouter les events du dressing pour rafraîchir le compteur IA
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('wardrobe_updated', () => {
      console.log('[Profile] wardrobe_updated event → reloading stats');
      loadStats();
    });
    return () => sub.remove();
  }, [loadStats]);
  const [weather, setWeather] = useState(true);
  const langInfo = allLanguages[lang] || allLanguages['en'];

  return (
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>
      
      <View style={[s.topbar, { backgroundColor: theme.bg }]}>
        <View>
          <Text style={s.topbarBadge}>👤 {t('my_account')}</Text>
          <Text style={[s.title, { color: theme.text }]}>{t('my_profile')}</Text>
        </View>
      </View>
      <Divider />

      <ScrollView ref={scrollRef} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 40 }} onScroll={(e) => { scrollYRef.current = e?.nativeEvent?.contentOffset?.y || 0; }} scrollEventThrottle={16} refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}>

        {/* ═══ HERO PROFIL Premium ═══ */}
        <View style={s.heroProfile}>
          <View style={s.heroProfileBg} />
          <View style={s.heroProfileContent}>
            {profile.avatar
              ? <Image source={{ uri: profile.avatar }} style={s.avatarHero} />
              : <View style={s.avatarHeroPlaceholder}><Text style={s.avatarHeroText}>{profile.initials}</Text></View>
            }
            <Text style={[s.heroProfileName, { color: theme.text }]} numberOfLines={1} ellipsizeMode="tail">{getFullName(profile, getFirstName(profile, t('my_profile')))}</Text>
            {/* Email : on n'affiche pas l'alias relais Apple privaterelay.appleid.com */}
            {(() => {
              const displayEmail = getDisplayEmail(profile);
              if (!displayEmail) return null;
              return (
                <Text style={s.heroProfileEmail} numberOfLines={1} ellipsizeMode="middle">{displayEmail}</Text>
              );
            })()}
            {profile.city && (
              <View style={s.heroProfileCityRow}>
                <Text style={s.heroProfileCityIcon}>📍</Text>
                {/* v1.12.563 — Ville traduite via Nominatim API selon lang du profil */}
                <Text style={s.heroProfileCity}>{cityLocalized || profile.city}</Text>
              </View>
            )}

            {/* Badges */}
            <View style={[s.heroBadges, { flexWrap: 'wrap', justifyContent: 'center' }]}>
              {(() => {
                const badge = getTierBadge(currentTier);
                const bgMap = {
                  free:    { bg: '#F5F3EE', border: '#E8E0C8', text: '#8B7500' },
                  basic:   { bg: '#EEF4FB', border: '#BAD2EC', text: '#2F6FB0' },
                  premium: { bg: '#FFF9E6', border: '#F5DEB3', text: '#B8860B' },
                  gold:    { bg: '#FFF4CC', border: '#D4AF37', text: '#8B6914' },
                };
                const colors = bgMap[currentTier] || bgMap.free;
                return (
                  <View style={[s.heroBadge, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                    <Text style={[s.heroBadgeText, { color: colors.text }]}>
                      {badge.emoji ? badge.emoji + ' ' : ''}{badge.text}
                    </Text>
                  </View>
                );
              })()}
              {profile.gender && ['M', 'F', 'N'].includes(profile.gender) ? (
                <View style={[s.heroBadge, {
                  backgroundColor: profile.gender === 'M' ? '#E8F0FE' : profile.gender === 'N' ? '#F5F5F5' : '#FEE8F0',
                  borderColor:     profile.gender === 'M' ? '#BBD0F0' : profile.gender === 'N' ? '#DDD' : '#F0BBD0',
                }]}>
                  <Text style={[s.heroBadgeText, {
                    color: profile.gender === 'M' ? '#1565C0' : profile.gender === 'N' ? '#666' : '#C2185B',
                  }]}>
                    {profile.gender === 'M' ? t('badge_man') : profile.gender === 'N' ? t('badge_all_genders') : t('badge_woman')}
                  </Text>
                </View>
              ) : (
                // Gender pas défini → CTA pour compléter le profil
                <TouchableOpacity
                  onPress={() => {
                    // Déclenche l'onboarding via event global écouté par App.js
                    DeviceEventEmitter.emit('force_onboarding');
                  }}
                  style={[s.heroBadge, {
                    backgroundColor: '#FFF3E0',
                    borderColor: '#FFB74D',
                  }]}
                  activeOpacity={0.8}
                >
                  <Text style={[s.heroBadgeText, { color: '#E65100' }]}>
                    {t('complete_profile_caps')}
                  </Text>
                </TouchableOpacity>
              )}
              {profile.provider && (
                <View style={[s.heroBadge, {
                  backgroundColor: profile.provider === 'apple' ? '#1C1C1E' : '#FFFFFF',
                  borderColor:     profile.provider === 'apple' ? '#555' : '#4285F4',
                }]}>
                  <Text style={[s.heroBadgeText, { color: profile.provider === 'apple' ? '#FFF' : '#4285F4' }]}>
                    {profile.provider === 'apple' ? ' APPLE' : 'G GOOGLE'}
                  </Text>
                </View>
              )}
            </View>

            {/* Membre depuis */}
            {(profile.since || profile.memberSince) && (
              <Text style={s.heroProfileSince}>
                {profile.loginCount > 1
                  ? t('member_since_with_logins')
                      .replace('{date}', formatDate(profile.memberSince || profile.since))
                      .replace('{count}', profile.loginCount)
                  : t('member_since_count').replace('{date}', formatDate(profile.memberSince || profile.since))}
              </Text>
            )}
          </View>
        </View>

        {/* ═══ Stats banner premium ═══ */}
        <View style={s.statsRowNew}>
          <View style={s.statCellNew}>
            <Text style={s.statNumNew}>{statsData.looks}</Text>
            <Text style={s.statLblNew}>{t('looks_created')}</Text>
          </View>
          <View style={s.statDivNew} />
          <View style={s.statCellNew}>
            <Text style={s.statNumNew}>{statsData.purchases}</Text>
            <Text style={s.statLblNew}>{t('purchases')}</Text>
          </View>
          <View style={s.statDivNew} />
          <View style={s.statCellNew}>
            <Text style={[s.statNumNew, { color: '#B8860B' }]}>✨ {statsData.pieces}</Text>
            <Text style={s.statLblNew}>{t('pieces')}</Text>
          </View>
        </View>

        {/* ═══ MON STYLE IA ═══ */}
        {styleProfile && !styleProfile.empty && (
          <View style={s.section}>
            <Text style={s.sectionHeader}>🎨 {t('my_style_ai')}</Text>
            <View style={s.styleCard}>
              {/* Tagline principale — v1.12.562 traduit FR→EN */}
              {styleProfile.tagline && (() => {
                let displayed = styleProfile.tagline;
                if (lang === 'en') {
                  const TAGLINE_FR_EN = {
                    'Sportwear décontracté': 'Casual sportswear',
                    'Casual relax': 'Casual relax',
                    'Casual chic': 'Casual chic',
                    'Chic moderne': 'Modern chic',
                    'Ultra chic / soirée': 'Ultra chic / evening',
                    'palette monochrome': 'monochrome palette',
                    'tons neutres': 'neutral tones',
                    'couleurs froids': 'cool colors',
                    'couleurs naturels': 'natural colors',
                    'couleurs chauds': 'warm colors',
                    'couleurs chics': 'chic colors',
                    'couleurs précieux': 'precious colors',
                    'couleurs variés': 'varied colors',
                  };
                  for (const [fr, en] of Object.entries(TAGLINE_FR_EN)) {
                    displayed = displayed.replace(fr, en);
                  }
                }
                return (
                  <View style={s.styleTaglineRow}>
                    <Text style={s.styleTaglineEmoji}>{styleProfile.formalityEmoji || '✨'}</Text>
                    <Text style={s.styleTagline}>{displayed}</Text>
                  </View>
                );
              })()}

              <View style={s.styleDivider} />

              {/* Formality score visuel */}
              {styleProfile.formality != null && (() => {
                // v1.12.119 — clamp défensif côté affichage : si jamais une
                // valeur >5 remontait du backend / cache, on la borne à 5.
                const f = Math.max(0, Math.min(5, styleProfile.formality));
                const fDisplay = Math.round(f * 10) / 10;
                return (
                  <View style={s.styleRow}>
                    <Text style={s.styleRowLabel}>{t('formality')}</Text>
                    <View style={s.styleRowValue}>
                      <View style={s.formalityBar}>
                        <View style={[s.formalityFill, { width: `${(f / 5) * 100}%` }]} />
                      </View>
                      <Text style={s.formalityText}>{fDisplay} / 5</Text>
                    </View>
                  </View>
                );
              })()}

              {/* Couleurs dominantes */}
              {styleProfile.topColors && styleProfile.topColors.length > 0 && (
                <View style={s.styleRowColumn}>
                  <Text style={s.styleRowLabel}>{t('dominant_palette')}</Text>
                  <View style={s.colorPalette}>
                    {styleProfile.topColors.map((c, i) => {
                      // v1.12.267 — Traduit le nom de couleur FR vers EN si lang='en'
                      const COLOR_FR_EN = { 'noir':'Black','blanc':'White','gris':'Gray','rouge':'Red','bleu':'Blue','vert':'Green','jaune':'Yellow','orange':'Orange','violet':'Purple','rose':'Pink','marron':'Brown','doré':'Gold','argenté':'Silver','beige':'Beige','écru':'Ecru','crème':'Cream','bordeaux':'Burgundy','kaki':'Khaki','turquoise':'Turquoise','indigo':'Indigo','corail':'Coral','lavande':'Lavender','ivoire':'Ivory','nude':'Nude','taupe':'Taupe','marine':'Navy','camel':'Camel','olive':'Olive','moutarde':'Mustard','prune':'Plum','saumon':'Salmon','menthe':'Mint','écossais':'Tartan','multicolore':'Multicolor' };
                      const colorName = lang === 'en' && c.name ? (COLOR_FR_EN[c.name.toLowerCase()] || c.name) : c.name;
                      return (
                        <View key={`col_${i}`} style={s.colorItem}>
                          <View style={[s.colorSwatch, { backgroundColor: c.hex || '#DDD', borderColor: c.hex === '#FFF' ? '#DDD' : c.hex }]} />
                          <Text style={s.colorName}>{colorName}</Text>
                          <Text style={s.colorPct}>{c.percent}%</Text>
                        </View>
                      );
                    })}
                  </View>
                </View>
              )}

              {/* Styles détectés */}
              {styleProfile.styles && styleProfile.styles.length > 0 && (
                <View style={s.styleRowColumn}>
                  <Text style={s.styleRowLabel}>{t('detected_styles')}</Text>
                  <View style={s.styleTags}>
                    {styleProfile.styles.map(st => {
                      // v1.12.267 — Traduit les styles FR (chic, casual, minimaliste...) via clés style_*
                      const STYLE_KEY = { 'chic':'style_chic','casual':'style_casual','minimaliste':'style_minimal','bohème':'style_boho','boheme':'style_boho','soirée':'style_evening','sport':'style_sport','bureau':'style_office','élégant':'style_elegant','elegant':'style_elegant' };
                      const k = STYLE_KEY[st?.toLowerCase()];
                      const label = k ? t(k) : st;
                      return (
                        <View key={st} style={s.styleTag}>
                          <Text style={s.styleTagText}>{label}</Text>
                        </View>
                      );
                    })}
                  </View>
                </View>
              )}

              {/* Stats complémentaires */}
              <View style={s.styleFooter}>
                <View style={s.styleFooterCell}>
                  <Text style={s.styleFooterNum}>{(() => {
                    // v1.12.562 — Traduire la catégorie selon la langue
                    const CAT_KEY = {
                      Hauts: 'cat_tops', Bas: 'cat_bottoms', Robes: 'cat_dresses',
                      Vestes: 'cat_jackets', Manteaux: 'cat_coats', Chaussures: 'cat_shoes',
                      Sacs: 'cat_bags', Accessoires: 'cat_accessories', Bijoux: 'cat_jewelry',
                      Maillots: 'cat_swimwear',
                    };
                    const k = CAT_KEY[styleProfile.topCategory];
                    return (k && t(k)) || styleProfile.topCategory;
                  })()}</Text>
                  <Text style={s.styleFooterLbl}>{t('top_category')} ({styleProfile.topCategoryCount})</Text>
                </View>
                {styleProfile.avgPrice != null && (
                  <View style={s.styleFooterCell}>
                    <Text style={s.styleFooterNum}>{styleProfile.avgPrice}€</Text>
                    <Text style={s.styleFooterLbl}>{t('avg_price')}</Text>
                  </View>
                )}
                <View style={s.styleFooterCell}>
                  <Text style={s.styleFooterNum}>{styleProfile.diversityScore}</Text>
                  <Text style={s.styleFooterLbl}>{lang === 'en' ? 'Diversity' : 'Diversité'}</Text>
                </View>
              </View>

              {/* Tag IA */}
              <View style={s.styleAiTag}>
                <Text style={s.styleAiTagText}>
                  🤖 {lang === 'en' ? `Analyzed by AI · ${styleProfile.analyzedCount}/${styleProfile.totalCount} items` : `Analysé par IA · ${styleProfile.analyzedCount}/${styleProfile.totalCount} articles`}
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* État vide : pas encore d'analyse */}
        {styleProfile && styleProfile.empty && (
          <View style={s.section}>
            <Text style={s.sectionHeader}>🎨 {t('my_style_ai')}</Text>
            <TouchableOpacity
              style={s.styleEmptyCard}
              onPress={() => navigation.navigate('Garde-robe')}
              activeOpacity={0.85}
            >
              <Text style={s.styleEmptyEmoji}>🎨</Text>
              <Text style={s.styleEmptyTitle}>{t('style_empty_title')}</Text>
              <Text style={s.styleEmptyDesc}>
                {t('style_empty_desc')}
              </Text>
              <View style={s.styleEmptyCta}>
                <Text style={s.styleEmptyCtaText}>{t('go_to_wardrobe')}</Text>
              </View>
            </TouchableOpacity>
          </View>
        )}

        {/* ═══ Bouton "Modifier mon profil" — accessible à tout moment ═══ */}
        <TouchableOpacity
          style={s.editProfileBtn}
          onPress={() => navigation.navigate('CompleteProfile')}
          activeOpacity={0.85}
        >
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Text style={{ fontSize: 20 }}>✏️</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.editProfileBtnTitle}>{t('edit_profile_title')}</Text>
              <Text style={s.editProfileBtnSub}>{lang === 'en' ? 'Identity, body shape, sizes…' : 'Identité, morphologie, tailles…'}</Text>
            </View>
          </View>
          <Text style={s.editProfileBtnArrow}>›</Text>
        </TouchableOpacity>

        {/* ═══ Accès rapides — Préférences, Favoris, Cashback ═══ */}
        <View style={s.quickSection}>
          <Text style={s.sectionHeader}>✨ {t('quick_access')}</Text>
          <View style={s.quickGridProfile}>
            <TouchableOpacity
              style={[s.quickCardProfile, { backgroundColor: '#FFF7E0', borderColor: '#D4AF72' }]}
              onPress={() => {
                // Émet l'event écouté par App.js qui ouvre le popup
                const { DeviceEventEmitter } = require('react-native');
                DeviceEventEmitter.emit('open_daily_prefs');
              }}
              activeOpacity={0.85}
            >
              <Text style={s.quickCardProfileIcon}>✨</Text>
              <Text style={s.quickCardProfileLabel}>{t('my_preferences')}</Text>
              <Text style={s.quickCardProfileSub}>Style, occasion, budget</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.quickCardProfile, { backgroundColor: '#FEF2F2', borderColor: '#FECACA' }]}
              onPress={() => navigation.navigate('Favorites')}
              activeOpacity={0.85}
            >
              <Text style={s.quickCardProfileIcon}>❤️</Text>
              <Text style={s.quickCardProfileLabel}>{t('favorites')}</Text>
              <Text style={s.quickCardProfileSub}>{t('saved_items')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.quickCardProfile, { backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }]}
              onPress={() => navigation.navigate('Earnings')}
              activeOpacity={0.85}
            >
              <Text style={s.quickCardProfileIcon}>💰</Text>
              <Text style={s.quickCardProfileLabel}>{t('cashback')}</Text>
              <Text style={s.quickCardProfileSub}>{t('cashback_earnings')}</Text>
            </TouchableOpacity>
          </View>

          {/* ═══ v1.12.608 — Carte Boutique add-ons (visible pour tous) ═══ */}
          <TouchableOpacity
            style={s.shopBanner}
            onPress={() => navigation.navigate('Shop')}
            activeOpacity={0.85}
          >
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Text style={{ fontSize: 22 }}>🛍️</Text>
              <View>
                <Text style={s.shopBannerTitle}>
                  {lang === 'en' ? 'Add-on shop' : 'Boutique add-ons'}
                </Text>
                <Text style={s.shopBannerSub}>
                  {lang === 'en'
                    ? 'Extra Try-Ons, Scans, Wardrobe slots'
                    : 'Try-Ons, Scans, slots Dressing en plus'}
                </Text>
              </View>
            </View>
            <Text style={s.shopBannerArrow}>›</Text>
          </TouchableOpacity>

          {/* ═══ Carte Admin — visible seulement si is_admin ═══ */}
          {isAdmin && (
            <TouchableOpacity
              style={s.adminBanner}
              onPress={() => navigation.navigate('AdminDashboard')}
              activeOpacity={0.85}
            >
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <Text style={{ fontSize: 22 }}>🔐</Text>
                <View>
                  <Text style={s.adminBannerTitle}>Dashboard Admin</Text>
                  <Text style={s.adminBannerSub}>Stats, retraits & utilisateurs</Text>
                </View>
              </View>
              <Text style={s.adminBannerArrow}>›</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* ═══ Section Langue & Région supprimée en v1.7.45 ═══ */}
        {/* La région et la langue sont gérées automatiquement à partir des */}
        {/* préférences système du téléphone et n'ont plus besoin d'une entrée */}
        {/* dédiée dans le Profil. L'écran RegionLanguageScreen reste accessible */}
        {/* via navigation.navigate('Region') si besoin futur. */}

        {/* ═══ Paramètres ═══ */}
        <View style={s.section}>
          <Text style={s.sectionHeader}>⚙️ {t('settings_caps')}</Text>

          {/* v1.12.259 — Sélecteur de langue (FR / EN) */}
          <View style={[s.settingsCard, { marginBottom: 12, padding: 14 }]}>
            <Text style={{ fontSize: 11, fontWeight: '700', color: '#7A7268', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>
              🌐 {lang === 'en' ? 'Language' : 'Langue'}
            </Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {[
                { code: 'fr', label: 'Français', flag: '🇫🇷' },
                { code: 'en', label: 'English',  flag: '🇬🇧' },
              ].map(opt => {
                const active = lang === opt.code;
                return (
                  <TouchableOpacity
                    key={opt.code}
                    onPress={() => setLang(opt.code)}
                    activeOpacity={0.85}
                    style={{
                      flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                      gap: 6, paddingVertical: 10, paddingHorizontal: 12,
                      borderRadius: 10,
                      backgroundColor: active ? '#1A1814' : '#FFFFFF',
                      borderWidth: 1, borderColor: active ? '#D4AF72' : '#EAE6E0',
                    }}>
                    <Text style={{ fontSize: 18 }}>{opt.flag}</Text>
                    <Text style={{
                      fontSize: 13, fontWeight: '700',
                      color: active ? '#D4AF72' : '#1A1814',
                      letterSpacing: 0.3,
                    }}>{opt.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          <View style={s.settingsCard}>
            {[
              { label: '🔔  ' + t('push_notifications'),  val: notifs,  setter: setNotifs  },
              { label: '🌤  ' + t('integrated_weather'),  val: weather, setter: setWeather },
              // v1.12.294 — mode sombre caché temporairement (le rendu n'est pas finalisé partout)
              // { label: '🌙  ' + t('dark_mode'),           val: darkMode,setter: setDarkMode},
              // v1.12.250 — Rappels calendrier (tous les tiers, free inclus)
              {
                label: t('daily_reminder_at').replace('{time}', `${String(calReminderHour).padStart(2,'0')}h00`),
                val: calReminderEnabled,
                setter: toggleCalReminder,
              },
            ].map((setting, i) => (
              <View key={i} style={[s.toggleRow, i > 0 && s.toggleRowBorder]}>
                <Text style={s.toggleLabel}>{setting.label}</Text>
                <Switch
                  value={setting.val}
                  onValueChange={setting.setter}
                  trackColor={{ false: colors.bg.border, true: '#D4AF37' }}
                  thumbColor={setting.val ? '#FFF' : '#F5F5F5'}
                  ios_backgroundColor={colors.bg.border}
                />
              </View>
            ))}
          </View>
        </View>

        {/* ═══ Mes gains (CJ cashback) ═══ */}
        <View style={s.section}>
          <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginBottom: 12 }}>
            <Text style={s.sectionHeader}>💰 {t('my_earnings')}</Text>
            {/* v1.12.313 — badge "Cashback X%" retiré (pas d'affichage des pourcentages) */}
          </View>

          {loadingWallet ? (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <ActivityIndicator color="#D4AF72" />
            </View>
          ) : !wallet ? (
            <View style={{ padding: 16, backgroundColor: '#F5F5F5', borderRadius: 10, alignItems: 'center' }}>
              <Text style={{ fontSize: 12, color: '#999' }}>{t('login_to_see_earnings')}</Text>
            </View>
          ) : (
            <>
              {/* v1.12.848 — Carte stats : nb achats qualifiants (depuis credit_transactions type='earn') */}
              <View style={{ marginBottom: 12 }}>
                <View style={{ backgroundColor: '#F8F5EE', borderRadius: 12, padding: 12, borderWidth: 1, borderColor: '#EFE8D8' }}>
                  <Text style={{ fontSize: 9, letterSpacing: 1.5, color: '#B8860B', fontWeight: '700' }}>{t('purchases_caps')}</Text>
                  <Text style={{ fontSize: 22, fontWeight: '300', color: '#111', marginTop: 4 }}>
                    {purchaseCount}
                  </Text>
                  <Text style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{t('confirmed_orders')}</Text>
                </View>
              </View>

              {/* Gains cashback */}
              <View style={{
                backgroundColor: '#1A1A1A',
                borderRadius: 14,
                padding: 16,
                borderWidth: 1,
                borderColor: '#D4AF72',
                marginBottom: 12,
              }}>
                <Text style={{ fontSize: 9, letterSpacing: 2, color: '#D4AF72', fontWeight: '700', marginBottom: 10 }}>
                  ✨ {lang === 'en' ? 'MYTENUE CREDITS' : 'CRÉDIT MYTENUE'}
                </Text>

                {/* v1.12.847 — Lit depuis /credits/balance (creditBalance) au lieu de wallet legacy */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Text style={{ fontSize: 11, color: '#999' }}>{lang === 'en' ? 'Active (usable)' : 'Actif (utilisable)'}</Text>
                  <Text style={{ fontSize: 28, fontWeight: '300', color: '#D4AF72', letterSpacing: 0.5 }}>
                    {Number(creditBalance?.active || 0).toFixed(2)} €
                  </Text>
                </View>

                <View style={{ height: 1, backgroundColor: '#333', marginVertical: 10 }} />

                {/* v1.12.847 — 3 statuts depuis /credits/balance */}
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 9, color: '#888', letterSpacing: 0.5 }}>{lang === 'en' ? 'PENDING' : 'EN ATTENTE'}</Text>
                    <Text style={{ fontSize: 13, color: '#fff', marginTop: 2, fontWeight: '500' }}>
                      {Number(creditBalance?.pending || 0).toFixed(2)} €
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 9, color: '#888', letterSpacing: 0.5 }}>{lang === 'en' ? 'USED' : 'UTILISÉS'}</Text>
                    <Text style={{ fontSize: 13, color: '#fff', marginTop: 2, fontWeight: '500' }}>
                      {Number(creditBalance?.used || 0).toFixed(2)} €
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 9, color: '#888', letterSpacing: 0.5 }}>{lang === 'en' ? 'EXPIRED' : 'EXPIRÉS'}</Text>
                    <Text style={{ fontSize: 13, color: '#fff', marginTop: 2, fontWeight: '500' }}>
                      {Number(creditBalance?.expired || 0).toFixed(2)} €
                    </Text>
                  </View>
                </View>
              </View>

              {/* v1.12.844 — Crédit Mytenue (programme de fidélité L.521-1) :
                  retrait monétaire désactivé. Crédits utilisables uniquement dans l'app
                  (renouvellement abo, upgrade tier, add-ons). */}
              <View style={{
                backgroundColor: '#FBF8F0', borderRadius: 10, padding: 14,
                borderWidth: 1, borderColor: '#E8DCC2',
              }}>
                <Text style={{ fontSize: 12, fontWeight: '700', color: '#1A1A1A', letterSpacing: 0.5, marginBottom: 4 }}>
                  ✨ {lang === 'en' ? 'Mytenue Credits' : 'Crédit Mytenue'}
                </Text>
                <Text style={{ fontSize: 10, color: '#7A6E55', lineHeight: 14, marginBottom: 10 }}>
                  {lang === 'en'
                    ? 'Your credits are usable within Mytenue only: extend subscription, upgrade tier, buy add-ons. Cannot be exchanged for cash.'
                    : 'Vos crédits sont utilisables uniquement dans Mytenue : prolonger votre abonnement, upgrader, acheter des add-ons. Non convertibles en argent.'}
                </Text>
                {/* v1.12.847 — Bouton utiliser mes crédits */}
                <TouchableOpacity
                  onPress={() => setShowUseCreditsModal(true)}
                  activeOpacity={0.85}
                  style={{
                    backgroundColor: '#1A1A1A', borderColor: '#D4AF72', borderWidth: 1,
                    paddingVertical: 10, borderRadius: 8, alignItems: 'center',
                  }}>
                  <Text style={{ fontSize: 11, color: '#D4AF72', fontWeight: '700', letterSpacing: 1 }}>
                    ✨ {lang === 'en' ? 'USE MY CREDITS' : 'UTILISER MES CRÉDITS'}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* v1.12.848 — Historique transactions crédits */}
              {creditHistory.length > 0 && (
                <View style={{ marginTop: 12, backgroundColor: '#FFF', borderRadius: 10, borderWidth: 1, borderColor: '#EFE8D8' }}>
                  <TouchableOpacity
                    onPress={() => setShowCreditHistory(v => !v)}
                    activeOpacity={0.8}
                    style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12 }}>
                    <Text style={{ fontSize: 11, fontWeight: '700', color: '#1A1A1A', letterSpacing: 0.5 }}>
                      {lang === 'en' ? 'CREDIT HISTORY' : 'HISTORIQUE DES CRÉDITS'} ({creditHistory.length})
                    </Text>
                    <Text style={{ fontSize: 14, color: '#7A6E55' }}>{showCreditHistory ? '−' : '+'}</Text>
                  </TouchableOpacity>
                  {showCreditHistory && (
                    <View style={{ borderTopWidth: 1, borderTopColor: '#F0E8D8' }}>
                      {creditHistory.slice(0, 30).map((tx, idx) => {
                        const amt = Number(tx.amount || 0);
                        const isPositive = amt > 0;
                        const isEarn = tx.type === 'earn';
                        const isUse = tx.type === 'use';
                        const isValidated = tx.type === 'partner_validated';
                        const isExpire = tx.type === 'expire';
                        const meta = tx.metadata || {};
                        const usage = meta.usage || {};
                        let title;
                        let subtitle;
                        if (isEarn) {
                          const purchase = Number(meta.purchaseAmount || 0);
                          const tier = meta.userTier || 'free';
                          title = lang === 'en'
                            ? `Purchase ${purchase.toFixed(2)} € (${tier})`
                            : `Achat ${purchase.toFixed(2)} € (${tier})`;
                          subtitle = lang === 'en' ? 'Pending partner validation' : 'En attente validation partenaire';
                        } else if (isValidated) {
                          title = lang === 'en' ? 'Validated by partner' : 'Validé par le partenaire';
                          subtitle = lang === 'en' ? 'Now usable' : 'Désormais utilisable';
                        } else if (isUse) {
                          title = usage.itemName || usage.itemId || (lang === 'en' ? 'In-app purchase' : 'Achat in-app');
                          subtitle = usage.type === 'subscription_renewal' ? (lang === 'en' ? 'Subscription' : 'Abonnement')
                                  : usage.type === 'tier_upgrade' ? (lang === 'en' ? 'Tier upgrade' : 'Upgrade tier')
                                  : usage.type === 'addon_purchase' ? (lang === 'en' ? 'Add-on' : 'Add-on')
                                  : '';
                        } else if (isExpire) {
                          title = lang === 'en' ? 'Expired' : 'Expiré';
                          subtitle = lang === 'en' ? '12 months elapsed' : '12 mois écoulés';
                        } else {
                          title = tx.description || tx.type;
                          subtitle = '';
                        }
                        const dateStr = tx.created_at
                          ? new Date(tx.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })
                          : '';
                        return (
                          <View key={tx.id || idx} style={{ padding: 10, borderBottomWidth: idx < Math.min(creditHistory.length, 30) - 1 ? 1 : 0, borderBottomColor: '#F5EFE0', flexDirection: 'row', alignItems: 'center' }}>
                            <View style={{ flex: 1 }}>
                              <Text style={{ fontSize: 11, fontWeight: '600', color: '#1A1A1A' }}>{title}</Text>
                              {!!subtitle && <Text style={{ fontSize: 9, color: '#999', marginTop: 1 }}>{subtitle}</Text>}
                              <Text style={{ fontSize: 9, color: '#B8860B', marginTop: 1 }}>{dateStr}</Text>
                            </View>
                            <Text style={{
                              fontSize: 12, fontWeight: '700',
                              color: isPositive ? '#2C8C3F' : isExpire ? '#999' : '#B8410B',
                            }}>
                              {isPositive ? '+' : ''}{amt.toFixed(2)} €
                            </Text>
                          </View>
                        );
                      })}
                      {creditHistory.length > 30 && (
                        <Text style={{ padding: 10, fontSize: 9, color: '#999', textAlign: 'center', fontStyle: 'italic' }}>
                          {lang === 'en' ? `+ ${creditHistory.length - 30} older transactions` : `+ ${creditHistory.length - 30} transactions plus anciennes`}
                        </Text>
                      )}
                    </View>
                  )}
                </View>
              )}
              {false && !showWithdrawForm ? (
                <TouchableOpacity
                  style={{
                    backgroundColor: Number(wallet.cashback_available_eur || 0) >= WITHDRAWAL_MIN_EUR ? '#D4AF72' : '#E8E2D0',
                    paddingVertical: 14, borderRadius: 10, alignItems: 'center',
                  }}
                  disabled={Number(wallet.cashback_available_eur || 0) < WITHDRAWAL_MIN_EUR}
                  onPress={() => setShowWithdrawForm(true)}
                  activeOpacity={0.85}>
                  <Text style={{
                    fontSize: 12, fontWeight: '700',
                    color: Number(wallet.cashback_available_eur || 0) >= WITHDRAWAL_MIN_EUR ? '#1A1A1A' : '#999',
                    letterSpacing: 1,
                  }}>
                    {Number(wallet.cashback_available_eur || 0) >= WITHDRAWAL_MIN_EUR
                      ? '💳 RETIRER VIA PAYPAL'
                      : t('min_to_withdraw').replace('{amount}', String(WITHDRAWAL_MIN_EUR))
                    }
                  </Text>
                </TouchableOpacity>
              ) : false && (
                <View style={{
                  backgroundColor: '#F8F5EE', borderRadius: 12, padding: 14, gap: 10,
                  borderWidth: 1, borderColor: '#D4AF72',
                }}>
                  <Text style={{ fontSize: 12, fontWeight: '700', color: '#111', letterSpacing: 0.5 }}>
                    {t('withdrawal_of').replace('{amount}', Number(wallet.cashback_available_eur || 0).toFixed(2))}
                  </Text>
                  <Text style={{ fontSize: 11, color: '#666' }}>
                    {t('paypal_email_prompt')}
                  </Text>
                  <View style={{
                    backgroundColor: '#fff', borderWidth: 1, borderColor: '#ddd',
                    borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
                  }}>
                    <Text style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>{t('paypal_email_label')}</Text>
                    <TextInput
                      style={{ fontSize: 14, color: '#111', padding: 0 }}
                      placeholder="vous@example.com"
                      placeholderTextColor="#bbb"
                      value={paypalEmail}
                      onChangeText={setPaypalEmail}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      autoComplete="email"
                      textContentType="emailAddress"
                      contextMenuHidden={false}
                      importantForAutofill="yes"
                    />
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <TouchableOpacity
                      style={{
                        flex: 1, backgroundColor: '#F5F5F5',
                        paddingVertical: 12, borderRadius: 8, alignItems: 'center',
                      }}
                      onPress={() => setShowWithdrawForm(false)}
                      disabled={withdrawing}
                      activeOpacity={0.85}>
                      <Text style={{ fontSize: 11, fontWeight: '600', color: '#666' }}>{t('cancel')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={{
                        flex: 2, backgroundColor: '#1A1A1A',
                        paddingVertical: 12, borderRadius: 8, alignItems: 'center',
                        borderWidth: 1, borderColor: '#D4AF72',
                        opacity: withdrawing ? 0.5 : 1,
                      }}
                      onPress={handleWithdraw}
                      disabled={withdrawing}
                      activeOpacity={0.85}>
                      {withdrawing
                        ? <ActivityIndicator size="small" color="#D4AF72" />
                        : <Text style={{ fontSize: 11, fontWeight: '700', color: '#D4AF72', letterSpacing: 1 }}>
                            {t('confirm_withdrawal')}
                          </Text>
                      }
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              {/* v1.12.292 — ligne "Votre taux de cashback actuel est..." retirée à la demande UX */}

              {/* Historique des conversions */}
              {walletHistory.length > 0 && (
                <View style={{ marginTop: 16 }}>
                  <Text style={{ fontSize: 11, fontWeight: '700', color: '#111', letterSpacing: 0.5, marginBottom: 8 }}>
                    {t('recent_orders_caps')} ({walletHistory.length})
                  </Text>
                  {walletHistory.slice(0, 5).map((conv, idx) => {
                    const statusColors = {
                      pending:  { bg: '#FFF9E6', color: '#B8860B', label: t('pending') },
                      locked:   { bg: '#E3F2FD', color: '#1565C0', label: t('validated') },
                      paid:     { bg: '#E8F5E9', color: '#2E7D32', label: t('status_paid') },
                      rejected: { bg: '#FFEBEE', color: '#C62828', label: t('status_cancelled') },
                    };
                    const st = statusColors[conv.status] || statusColors.pending;
                    return (
                      <View key={conv.commission_id || idx} style={{
                        flexDirection: 'row', alignItems: 'center', gap: 10,
                        paddingVertical: 10,
                        borderBottomWidth: idx < walletHistory.length - 1 ? 1 : 0,
                        borderBottomColor: '#F0F0F0',
                      }}>
                        {conv.product_image ? (
                          <Image source={{ uri: conv.product_image }}
                            style={{ width: 40, height: 40, borderRadius: 6, backgroundColor: '#F5F5F5' }} />
                        ) : (
                          <View style={{ width: 40, height: 40, borderRadius: 6, backgroundColor: '#F5F5F5', alignItems:'center', justifyContent:'center' }}>
                            <Text style={{ fontSize: 18 }}>🛍️</Text>
                          </View>
                        )}
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 12, color: '#111', fontWeight: '500' }} numberOfLines={1}>
                            {conv.product_name || conv.advertiser_name || 'Commande'}
                          </Text>
                          <Text style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                            {conv.partner || ''}
                          </Text>
                        </View>
                        <View style={{ alignItems:'flex-end', gap: 2 }}>
                          <Text style={{ fontSize: 13, fontWeight: '700', color: '#111' }}>
                            +{Number(conv.commission_usd || conv.commission_amount_eur || 0).toFixed(2)}€
                          </Text>
                          <View style={{ paddingHorizontal: 6, paddingVertical: 2, backgroundColor: st.bg, borderRadius: 4 }}>
                            <Text style={{ fontSize: 8, fontWeight: '700', color: st.color, letterSpacing: 0.3 }}>
                              {st.label}
                            </Text>
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              )}
            </>
          )}
        </View>

        {/* ═══ Abonnement ═══ */}
        <View style={s.section}>
          <Text style={s.sectionHeader}>👑 {t('subscription')}</Text>

          {/* ═══ FIX 2026-04 (v1.7.69) : afficher carte si état Premium (même expiré) ═══ */}
          {/* Avant : ternaire isPremiumUser ? Premium : Free. Si l'expiration */}
          {/*   était détectée (date passée), on tombait sur le bloc Free et le */}
          {/*   bouton "Se désabonner" disparaissait → user bloqué sans pouvoir */}
          {/*   éviter le renouvellement automatique de son abo Apple/Google. */}
          {/* Maintenant : on affiche la carte Premium dès qu'un état payant est */}
          {/*   détecté (hasPremiumState). Le badge "PLAN ACTIF" ou "EXPIRÉ" est */}
          {/*   géré dans la carte selon isPremiumUser. Le bouton "Se désabonner" */}
          {/*   reste accessible tant que le user n'est pas annulé. */}
          {hasPremiumState ? (
            /* ── Premium (actif ou expiré) ── */
            <>
            <View style={s.premiumCard}>
              <View style={s.premiumTopRow}>
                <View style={{ flex: 1 }}>
                  {/* ═══ v1.7.69 : badge dynamique selon état réel ═══ */}
                  {/* Si la date d'expiration est passée → "EXPIRÉ" (orange/rouge) */}
                  {/* Sinon → "PREMIUM" (doré standard) */}
                  {(() => {
                    const expiresAt = user?.premiumExpiresAt;
                    const isExpired = !isPremiumUser && expiresAt &&
                      new Date(expiresAt).getTime() < Date.now();
                    if (isExpired) {
                      return (
                        <View style={[s.premiumBadge, { backgroundColor: 'rgba(220,38,38,0.15)' }]}>
                          <Text style={[s.premiumBadgeTxt, { color: '#FCA5A5' }]}>{t('expired_caps')}</Text>
                        </View>
                      );
                    }
                    // Badge dynamique selon le tier réel (basic/premium/gold).
                    // Avant : hardcodé "PREMIUM" → un user Gold voyait "PREMIUM",
                    // un Basic aussi. Maintenant : label du tier en DB.
                    const tierLabel = (TIERS[currentTier]?.label || 'Premium').toUpperCase();
                    return (
                      <View style={s.premiumBadge}>
                        <Text style={s.premiumBadgeTxt}>👑  {tierLabel}</Text>
                      </View>
                    );
                  })()}
                  <Text style={s.premiumPlanTxt}>{currentPlanLabel || ((TIERS[currentTier]?.label || 'Premium') + (userPlan === 'yearly' ? ' · Annuel' : ' · Mensuel'))}</Text>
                  {/* ═══ v1.7.80 : Date renouvellement / expiration / fallback Free ═══ */}
                  {/* Logique :                                                          */}
                  {/* - Abonné actif (non annulé) + premiumExpiresAt → date de renouvellement */}
                  {/* - Désabonné + date future → date d'expiration                       */}
                  {/* - Date dépassée → tier sera ré-évalué automatiquement par AuthContext */}
                  {/*   (la carte Premium ne s'affichera plus, l'user est en Free)        */}
                  {(() => {
                    const expiresAt = user?.premiumExpiresAt;
                    if (!expiresAt) return null;
                    const d = new Date(expiresAt);
                    if (isNaN(d.getTime())) return null;
                    const now = new Date();
                    const months = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
                    const dateStr = `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
                    const isCancelled = user?.subscriptionCancelled;
                    const isExpired = d.getTime() < now.getTime();

                    // v1.12.665 — Si pending switch (monthly→yearly prepaid OU downgrade),
                    // afficher info claire "Passage à X annuel le DD/MM/YYYY"
                    const pendingTier = user?.subscriptionPendingDowngradeTier || user?.pending_downgrade_tier;
                    const pendingPeriod = user?.subscriptionPendingDowngradePeriod || user?.pending_downgrade_period;
                    const pendingAt = user?.subscriptionPendingDowngradeAt || user?.pending_downgrade_at;
                    if (pendingTier && pendingAt) {
                      const pd = new Date(pendingAt);
                      const monthsEn = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const pendingDateStr = isFR
                        ? `${pd.getDate()} ${months[pd.getMonth()]} ${pd.getFullYear()}`
                        : `${monthsEn[pd.getMonth()]} ${pd.getDate()}, ${pd.getFullYear()}`;
                      const pendingTierLabel = pendingTier.charAt(0).toUpperCase() + pendingTier.slice(1);
                      const pendingPeriodLabel = isFR
                        ? (pendingPeriod === 'yearly' ? 'annuel' : 'mensuel')
                        : (pendingPeriod === 'yearly' ? 'yearly' : 'monthly');
                      return (
                        <View>
                          <Text style={[s.premiumRenewsTxt, { color: '#A78BFA' }]}>
                            {isFR
                              ? `✓ Passage à ${pendingTierLabel} ${pendingPeriodLabel} le ${pendingDateStr} (déjà payé)`
                              : `✓ Switching to ${pendingTierLabel} ${pendingPeriodLabel} on ${pendingDateStr} (already paid)`}
                          </Text>
                          <TouchableOpacity
                            style={{ marginTop: 6, alignSelf: 'flex-start' }}
                            onPress={async () => {
                              const idx = await showAppAlert(
                                isFR ? 'Annuler le changement programmé' : 'Cancel pending change',
                                isFR ? `Annuler le passage à ${pendingTierLabel} ${pendingPeriodLabel} ?\n\nSi vous aviez prépayé un abonnement annuel, vous serez remboursé.`
                                     : `Cancel switch to ${pendingTierLabel} ${pendingPeriodLabel}?\n\nIf you prepaid a yearly subscription, you will be refunded.`,
                                [
                                  { text: isFR ? 'Garder' : 'Keep', style: 'cancel' },
                                  { text: isFR ? 'Annuler' : 'Cancel', style: 'destructive' },
                                ]
                              );
                              if (idx !== 1) return;
                              try {
                                const { showProcessing, hideProcessing } = require('../components/ProcessingOverlay');
                                if (Platform.OS === 'web') {
                                  // Web : annulation via Stripe (rembourse un éventuel prépaiement annuel).
                                  const { cancelPendingChange } = require('../services/StripeService');
                                  showProcessing(
                                    isFR ? 'Annulation en cours…' : 'Cancelling…',
                                    isFR ? 'Annulation du changement programmé et traitement du remboursement.' : 'Cancelling pending change and processing refund.'
                                  );
                                  const r = await cancelPendingChange();
                                  hideProcessing();
                                  const rmsg = r?.refunded ? (isFR ? ` Remboursement : ${r.refunded} €.` : ` Refund: ${r.refunded} €.`) : '';
                                  showAppAlert(isFR ? 'Changement annulé' : 'Pending cancelled', (isFR ? 'Annulé.' : 'Cancelled.') + rmsg);
                                } else {
                                  // v1.12.951 — Mobile : StripeService est web-only (throw). On annule le
                                  // downgrade programmé en RE-CONFIRMANT le plan COURANT via IAP
                                  // (RC.purchasePackage) → Apple annule le changement planifié, et notre
                                  // activation ré-active le tier courant en effaçant le pending en base.
                                  const result = await purchaseSubscription(currentTier, currentPeriod || 'monthly', user);
                                  if (result?.cancelled) return;
                                  if (!result?.success) throw new Error('purchase_failed');
                                  const _tl = (currentTier || '').charAt(0).toUpperCase() + (currentTier || '').slice(1);
                                  showAppAlert(
                                    isFR ? 'Changement annulé' : 'Pending cancelled',
                                    isFR ? `Vous restez sur ${_tl}. Le passage programmé est annulé.`
                                         : `You stay on ${_tl}. The scheduled change is cancelled.`
                                  );
                                }
                                if (reloadProfile) [500, 2000, 5000, 9000].forEach(ms => setTimeout(() => reloadProfile().catch(() => {}), ms));
                              } catch (e) {
                                try { require('../components/ProcessingOverlay').hideProcessing(); } catch {}
                                showAppAlert(isFR ? 'Erreur' : 'Error', e?.message || '');
                              }
                            }}
                          >
                            <Text style={{ color: '#FCA5A5', fontSize: 12, textDecorationLine: 'underline' }}>
                              {isFR ? '✕ Annuler ce changement' : '✕ Cancel this change'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      );
                    }

                    // Crédit Mytenue — abo offert par crédit : pas de renouvellement auto,
                    // on affiche la date d'expiration (le cron repassera en free ensuite).
                    if (user?.isCreditComp && !isExpired) {
                      return (
                        <Text style={s.premiumRenewsTxt}>
                          {isFR
                            ? `🎁 Offert par Crédit Mytenue · Expire le ${dateStr} · sans renouvellement`
                            : `🎁 Gift via Mytenue Credit · Expires ${dateStr} · no auto-renewal`}
                        </Text>
                      );
                    }
                    if (isExpired) {
                      return (
                        <Text style={[s.premiumCancelledTxt, { color: '#FCA5A5' }]}>
                          {t('expired_on').replace('{date}', dateStr)}
                        </Text>
                      );
                    }
                    if (isCancelled) {
                      return (
                        <Text style={s.premiumCancelledTxt}>
                          {t('cancelled_expires_on').replace('{date}', dateStr)}
                        </Text>
                      );
                    }
                    return (
                      <Text style={s.premiumRenewsTxt}>
                        {t('renewal_on').replace('{date}', dateStr)}
                      </Text>
                    );
                  })()}
                </View>
                <Text style={s.premiumCheck}>✓</Text>
              </View>

              <View style={s.premiumDivider} />

              <View style={s.premiumPerks}>
                {(() => {
                  // ═══ FIX 2026-04 (v1.7.86) : perks dérivés du tier réel ═══
                  // Avant : bloc hardcodé "Dressing illimité / IA illimitée / Looks
                  //   illimités / Voyages illimités / Suggestions exclusives" affiché
                  //   pour TOUS les abonnés payants → un Premium voyait "Dressing
                  //   illimité" alors qu'il est limité à 100 articles.
                  // Maintenant : on lit TIERS[currentTier] et on formate les valeurs
                  //   exactes du tier de l'utilisateur. Les libellés "illimité(s)" ne
                  //   sont affichés que si la valeur réelle est Infinity.
                  const tier = TIERS[currentTier] || TIERS.premium;
                  const wardrobeLabel = tier.wardrobe_items === Infinity
                    ? t('feat_unlimited_wardrobe')
                    : t('feat_wardrobe_count').replace('{count}', String(tier.wardrobe_items));
                  const aiLabel = tier.ai_requests === Infinity
                    ? t('feat_unlimited_ai')
                    : t('feat_ai_per_day').replace('{count}', String(tier.ai_requests));
                  const looksLabel = tier.looks_per_day === Infinity
                    ? t('feat_unlimited_looks')
                    : t('feat_looks_per_day').replace('{count}', String(tier.looks_per_day));
                  // ═══ v1.10.0 : Perk Try-on virtuel ═══
                  // v1.10.17 : Free = 1/sem, abonnés = X/mois
                  let tryonsLabel;
                  if (tier.tryons_per_month === Infinity) {
                    tryonsLabel = t('feat_unlimited_tryons');
                  } else if (currentTier === 'free') {
                    tryonsLabel = t('feat_tryons_per_week').replace('{count}', String(tier.tryons_per_month));
                  } else {
                    tryonsLabel = t('feat_tryons_per_month').replace('{count}', String(tier.tryons_per_month));
                  }
                  // v1.12.911 — Voyage masqué (feature reportée pour après le lancement)
                  const tripsLabel = null;
                  // v1.12.816 — Aligné sur PlanComparisonTable (tier.cashback.displayMaxEurPer100)
                  //   Avant : getTierRewardExamples(commission 10%) → 1€/1.50€/2.50€/4€
                  //   Maintenant : displayMax (commission 12%) → 1.20€/1.80€/3€/4.80€
                  const _maxEur = tier.cashback?.displayMaxEurPer100 ?? 0;
                  const _exStr = lang === 'en'
                    ? `€${_maxEur.toFixed(2)}`
                    : `${_maxEur.toFixed(2).replace('.', ',')} €`;
                  // v1.12.921 — Texte raccourci (s'affichait tronqué sur mobile)
                  const cashbackLabel = lang === 'en'
                    ? `${_exStr} / €100 reward`
                    : `${_exStr} / 100€ récompensé`;

                  const perks = [
                    ['🧥', wardrobeLabel],
                    ['🤖', aiLabel],
                    ['✨', looksLabel],
                    ['👗', tryonsLabel],  // v1.10.0 : nouvelle ligne essayages
                  ];
                  if (tripsLabel) perks.push(['✈️', tripsLabel]);
                  perks.push(['💰', cashbackLabel]);
                  if (currentTier === 'gold') perks.push(['👑', t('feat_vip_support')]);

                  return perks.map(([icon, label]) => (
                    <View key={label} style={s.premiumPerkRow}>
                      <Text style={s.premiumPerkIcon}>{icon}</Text>
                      <Text style={s.premiumPerkTxt}>{label}</Text>
                    </View>
                  ));
                })()}
              </View>

              <View style={s.premiumDivider} />

              {/* ═══ v1.9.0 : si abonnement annulé, le bouton "Passer mensuel/annuel" ═══
                   doit déclencher une réactivation (re-purchase IAP), pas juste un
                   changePlan qui ne change rien côté Apple. */}
              {userPlan === 'monthly' ? (
                <TouchableOpacity
                  style={s.premiumSwitchBtn}
                  onPress={() => {
                    if (user?.subscriptionCancelled) {
                      // Abo annulé → ouvrir UpgradeModal pour réactiver via IAP
                      setUpgradeModalFeature('reactivate');
                      setShowUpgradeModal(true);
                    } else {
                      handleChangePlan('yearly');
                    }
                  }}
                >
                  {(() => {
                    const tier = TIERS[currentTier] || TIERS.premium;
                    const yearlyMonth = (tier.priceYearlyMonth || tier.priceYearly / 12).toFixed(2).replace('.', ',');
                    const yearlyTotal = Number(tier.priceYearly).toFixed(2).replace('.', ',');
                    const fullYear = (tier.priceMonthly * 12).toFixed(2);
                    const pct = Math.round((1 - (tier.priceYearly / (tier.priceMonthly * 12))) * 100);
                    return (
                      <>
                        <View>
                          <Text style={s.premiumSwitchTxt}>{t('switch_to_yearly')}</Text>
                          <Text style={s.premiumSwitchSub}>{t('switch_to_yearly_sub').replace('{yearly}', yearlyTotal).replace('{price}', yearlyMonth).replace('{pct}', pct)}</Text>
                        </View>
                      </>
                    );
                  })()}
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[s.premiumSwitchBtn, {backgroundColor:'#F8F8F8'}]}
                  onPress={() => {
                    if (user?.subscriptionCancelled) {
                      // Abo annulé → ouvrir UpgradeModal pour réactiver via IAP
                      setUpgradeModalFeature('reactivate');
                      setShowUpgradeModal(true);
                    } else {
                      handleChangePlan('monthly');
                    }
                  }}
                >
                  {(() => {
                    const tier = TIERS[currentTier] || TIERS.premium;
                    const monthly = tier.priceMonthly.toFixed(2).replace('.', ',');
                    return (
                      <View>
                        <Text style={[s.premiumSwitchTxt, {color:'#555'}]}>{t('switch_to_monthly')}</Text>
                        <Text style={s.premiumSwitchSub}>{t('switch_to_monthly_sub').replace('{price}', monthly)}</Text>
                      </View>
                    );
                  })()}
                </TouchableOpacity>
              )}

              {/* ═══ FIX 2026-04 : bouton "Changer de formule" mis en évidence ═══ */}
              {/* ═══ v1.9.0 : si abo annulé, ouvre UpgradeModal directement ═══ */}
              {/*   Avant : scroll vers tableau qui peut échouer, l'user voit pas */}
              {/*   les plans. Maintenant : popup UpgradeModal s'ouvre direct avec */}
              {/*   tous les plans + bouton S'abonner par plan (IAP réel). */}
              <TouchableOpacity
                style={s.premiumChangeFormulaBtn}
                onPress={() => {
                  if (user?.subscriptionCancelled) {
                    // Abo annulé → ouvrir UpgradeModal pour réactiver via IAP
                    setUpgradeModalFeature('reactivate');
                    setShowUpgradeModal(true);
                  } else {
                    // Abo actif → afficher le tableau et scroll
                    setShowAllPlans(true);
                    setScrollToPlansRequested(true);
                  }
                }}
                activeOpacity={0.85}
              >
                <View style={{ flex: 1 }}>
                  <Text style={s.premiumChangeFormulaTxt}>
                    {user?.subscriptionCancelled ? t('resubscribe') : t('change_plan')}
                  </Text>
                  <Text style={s.premiumChangeFormulaSub}>
                    {user?.subscriptionCancelled
                      ? t('resubscribe_sub')
                      : t('change_plan_sub')}
                  </Text>
                </View>
                <Text style={{ fontSize: 18, color: '#B8860B' }}>›</Text>
              </TouchableOpacity>

              {/* ═══ FIX 2026-04 : bouton conditionnel selon état annulation ═══ */}
              {/* Si déjà annulé : on n'affiche plus "Se désabonner" mais un texte */}
              {/* informatif rappelant la date de fin d'accès. Le user peut toujours */}
              {/* "Changer de formule" ci-dessus pour réactiver. */}
              {/*                                                                     */}
              {/* Sur web : on remplace "Se désabonner" par "Gérer mon abonnement"   */}
              {/* qui ouvre le portail client Stripe (annulation, changement de CB,  */}
              {/* factures, etc.). Le portail Stripe gère tout en self-service.      */}
              {user?.isCreditComp ? (
                <View style={s.premiumCancelBtn}>
                  <Text style={[s.premiumCancelTxt, { textDecorationLine: 'none', color: 'rgba(255,255,255,0.45)' }]}>
                    {isFR ? 'Offert par Crédit Mytenue — aucun renouvellement' : 'Gift via Mytenue Credit — no renewal'}
                  </Text>
                </View>
              ) : user?.subscriptionCancelled ? (
                <View style={s.premiumCancelBtn}>
                  <Text style={[s.premiumCancelTxt, { textDecorationLine: 'none', color: 'rgba(255,255,255,0.45)' }]}>
                    {t('subscription_cancelled_access_until')}
                  </Text>
                </View>
              ) : Platform.OS === 'web' ? (
                <TouchableOpacity style={s.premiumCancelBtn} onPress={async () => {
                  try {
                    const { openCustomerPortal } = require('../services/StripeService');
                    await openCustomerPortal();
                  } catch (e) {
                    showAppAlert(t('portal_unavailable'), e?.message || t('stripe_error'));
                  }
                }}>
                  <Text style={s.premiumCancelTxt}>{t('manage_subscription')}</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity style={s.premiumCancelBtn} onPress={handleUnsubscribe}>
                  <Text style={s.premiumCancelTxt}>{t('cancel_subscription')}</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* ═══ Carte utilisation pour abonnés payants ═══ */}
            {/* Permet à l'user de voir combien il a consommé aujourd'hui et sa limite */}
            {/* v1.7.63 : on borne statsData.looks à looks_per_day pour cohérence */}
            {/*   (sinon on affiche "87 / 30" pour Premium qui aurait beaucoup de */}
            {/*   combinaisons possibles). */}
            <View style={[s.usageCard, { marginTop: 12 }]}>
              <Text style={s.usageCardTitle}>{t('usage_today')}</Text>

              {(() => {
                const looksLimit = (userLimits?.looks_per_day) ?? FREE_LIMITS.looks_per_day;
                const looksUsed = isFinite(looksLimit)
                  ? Math.min(statsData.looks, looksLimit)
                  : statsData.looks;
                // v1.12.619 — Pour Free, le try-on est one-shot (bonus onboarding ou
                // add-ons à TTL). Pas de notion de "/week" ni "/month" ni renew.
                // Singulier "try-on" si 1 dispo, pluriel "try-ons" sinon.
                // Pour Basic+, c'est mensuel rolling (anniversaire d'abonnement).
                const tryonAvailableFreeCount = addonsBalance?.tryons || 0;
                const tryonUnit = (currentTier === 'free')
                  ? (tryonAvailableFreeCount === 1 ? 'try-on' : 'try-ons')
                  : (tryonUsage.isPaidTier ? t('usage_unit_per_month') : t('usage_unit_per_week'));
                // v1.12.602 — V5.4 : intégration addons bonus dans la barre quota
                // Wardrobe = tier + boost +50 (45j) + subs (Plus +100 / Max +250)
                const wardrobeTier = (userLimits?.wardrobe_items) ?? FREE_LIMITS.wardrobe_items;
                const wardrobeBonus = addonsBalance?.wardrobeQuota?.bonusQuota || 0;
                const wardrobeMax = wardrobeTier + wardrobeBonus;
                // AI scans = tier + addons scans actifs (TTL 24h ou 7j)
                const aiTier = (userLimits?.ai_requests) ?? FREE_LIMITS.ai_requests;
                const aiBonus = addonsBalance?.scans || 0;
                const aiMax = aiTier + aiBonus;
                // Try-on = tier + addons tryons actifs (TTL 30j)
                // v1.12.617 — Pour Free, tryonTier RÉEL = 0 (pas 1 via fallback).
                // Le max affiché = juste le bonus (addonsBalance.tryons). Permet
                // d'afficher la barre "0 / 1" si bonus onboarding actif, ou
                // "0 / N" si l'user a acheté des add-ons try-ons.
                const isFreeTier = currentTier === 'free';
                // v1.12.678 — tryonUsage.limit du backend INCLUT DÉJÀ les bonus
                // addons (cf /tryon/usage: limit = tierLimit + addonBonus).
                // Donc NE PAS ajouter tryonBonus une 2ème fois.
                const tryonBonus = addonsBalance?.tryons || 0;
                const tryonMax = isFreeTier
                  ? tryonBonus  // Free: que les bonus
                  : (tryonUsage.limit || ((userLimits?.tryons_per_month) ?? 1) + tryonBonus);
                return [
                  { icon:'🧥', label:t('usage_wardrobe'), value: statsData.pieces, max: wardrobeMax, unit:t('usage_unit_pieces'),
                    isWardrobe: true,
                    bonusInfo: wardrobeBonus > 0 ? `+${wardrobeBonus} bonus (boost/abo)` : null },
                  { icon:'🤖', label:t('usage_ai'), value: aiUsed, max: aiMax, unit:t('usage_unit_per_day'),
                    isAI: true, // v1.12.814 — flag pour message limite correct (reset quotidien minuit)
                    bonusInfo: aiBonus > 0 ? `+${aiBonus} scans bonus actifs` : null },
                  { icon:'✨', label:t('usage_looks_gen'), value: looksUsed, max: looksLimit, unit:t('usage_unit_looks'), isLooks: true },
                  { icon:'🪞', label:t('usage_tryons'), value: tryonUsage.count || 0, max: tryonMax, unit: tryonUnit, isTryon: true,
                    // v1.12.617 — Pour Free, le bonus inclut le try-on offert de l'onboarding
                    bonusInfo: tryonBonus > 0
                      ? (isFreeTier
                          ? (tryonBonus === 1
                              ? '🎁 1 try-on offert'
                              : `🎁 ${tryonBonus} try-ons disponibles`)
                          : `+${tryonBonus} try-ons bonus actifs`)
                      : null },
                ];
              })().map(item => {
                const isInf = item.max === Infinity || !isFinite(item.max);
                const pct = isInf ? 0 : Math.min(100, Math.round((item.value / item.max) * 100));
                const over = !isInf && item.value >= item.max;
                // v1.12.196 : pour Try-ons free, on remplace l'affichage classique
                // par un message élégant "Réservé aux abonnés" (pas de progress
                // bar, pas de "limite atteinte" qui sonne mal).
                // v1.12.586 — bugfix : se baser sur currentTier (à jour via reloadProfile)
                // au lieu de tryonUsage qui peut être stale au moment du render.
                // v1.12.617 — EXCLURE ce message si l'user Free a des try-ons
                // disponibles (bonus onboarding ou add-ons achetés). Dans ce cas
                // on affiche la barre de progression normale pour qu'il puisse
                // voir combien il en a et combien il a consommé.
                const tryonsAvailable = (addonsBalance?.tryons || 0);
                const isTryonFree = item.isTryon
                  && (currentTier === 'free' || ((userLimits?.tryons_per_month) ?? 0) === 0)
                  && tryonsAvailable === 0;
                if (isTryonFree) {
                  return (
                    <View key={item.label} style={s.usageItem}>
                      <View style={s.usageItemTop}>
                        <View style={s.usageItemLeft}>
                          <Text style={s.usageItemIcon}>{item.icon}</Text>
                          <Text style={s.usageItemLabel}>{item.label}</Text>
                        </View>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#B8860B', letterSpacing: 0.5 }}>
                          ✨ PREMIUM
                        </Text>
                      </View>
                      <View style={{
                        flexDirection: 'row', alignItems: 'center', gap: 8,
                        marginTop: 8, padding: 10,
                        backgroundColor: '#FFF7E0',
                        borderWidth: 1, borderColor: '#F0DCA0', borderRadius: 8,
                      }}>
                        <Text style={{ fontSize: 16 }}>🪞</Text>
                        <Text style={{ flex: 1, fontSize: 12, color: '#8B6F3D', lineHeight: 17 }}>
                          {lang === 'en'
                            ? 'Virtual try-on on a model is reserved for subscribers (Basic, Premium or Gold). Choose a plan to use it.'
                            : 'L\'essayage virtuel sur mannequin est réservé aux abonnés (Basic, Premium ou Gold). Choisissez une formule pour l\'utiliser.'}
                        </Text>
                      </View>
                    </View>
                  );
                }
                // v1.10.52 : Pour Try-ons, afficher la date de reset avec heure/minute
                // basée sur l'anniversaire d'abonnement (timestamp complet TIMESTAMPTZ).
                // v1.12.619 — Free n'a PAS de renewal (try-on one-shot/TTL).
                // Le message "Renewal on..." n'est pertinent que pour Basic+.
                let tryonResetMsg = null;
                if (item.isTryon && tryonUsage.nextResetDate && currentTier !== 'free') {
                  tryonResetMsg = formatRenewalTime(tryonUsage.nextResetDate, lang, t);
                }
                return (
                  <View key={item.label} style={s.usageItem}>
                    <View style={s.usageItemTop}>
                      <View style={s.usageItemLeft}>
                        <Text style={s.usageItemIcon}>{item.icon}</Text>
                        <Text style={s.usageItemLabel}>{item.label}</Text>
                      </View>
                      <Text style={[s.usageItemVal, over && s.usageItemValOver]}>
                        {item.value} <Text style={s.usageItemMax}>{isInf ? `/ ∞ ${item.unit}` : `/ ${item.max} ${item.unit}`}</Text>
                      </Text>
                    </View>
                    <View style={s.usageItemBar}>
                      <View style={[s.usageItemBarFill, { width: (isInf ? Math.min(15, item.value * 2) : pct)+'%' }, over && s.usageItemBarOver]} />
                    </View>
                    {/* v1.12.602 — V5.4 : info bonus addons actifs (boost/abo/pack) */}
                    {item.bonusInfo && (
                      <Text style={{ fontSize: 11, color: '#B8860B', marginTop: 4, fontWeight: '600' }}>
                        ✨ {item.bonusInfo}
                      </Text>
                    )}
                    {item.isTryon && tryonResetMsg && !over && (
                      <Text style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                        {tryonResetMsg}
                      </Text>
                    )}
                    {over && !item.isLooks && (
                      <Text style={{ fontSize: 11, color: '#D32F2F', marginTop: 4 }}>
                        {lang === 'en'
                          ? (item.isWardrobe
                              ? 'Limit reached. Buy a +50 Boost or upgrade to a higher plan to add more items.'
                              : item.isTryon && tryonResetMsg
                                ? `Limit reached. ${tryonResetMsg.replace('🔄 ', '')} or upgrade to a higher plan.`
                                : `Limit reached. Resets in ${aiResetCountdown || '...'} or upgrade to a higher plan.`)
                          : (item.isWardrobe
                              ? 'Limite atteinte. Achetez un Boost +50 ou passez à un plan supérieur pour ajouter plus d\'articles.'
                              : item.isTryon && tryonResetMsg
                                ? `Limite atteinte. ${tryonResetMsg.replace('🔄 ', '')} ou passez à un plan supérieur.`
                                : `Limite atteinte. Reset dans ${aiResetCountdown || '...'} ou passez à un plan supérieur.`)}
                      </Text>
                    )}
                    {over && item.isLooks && (
                      <Text style={{ fontSize: 11, color: '#888', marginTop: 4, fontStyle: 'italic' }}>
                        {lang === 'en'
                          ? `Capped at ${item.max} looks per your plan. Upgrade for more.`
                          : `Plafonné à ${item.max} looks selon votre formule. Passez à un plan supérieur pour en voir plus.`}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>

            {/* ═══ FIX 2026-04 (v1.7.59) : afficher les autres formules pour les abonnés ═══ */}
            {/* Avant : le bloc plans (PlanComparisonTable + toggle + plansRow) était */}
            {/*   uniquement dans le else du ternaire isPremiumUser → quand un user   */}
            {/*   abonné cliquait "Changer de formule", showAllPlans devenait true    */}
            {/*   mais aucun rendu n'apparaissait car on était dans la branche Premium. */}
            {/* Maintenant : on duplique ces 3 sections dans le bloc Premium, gardées */}
            {/*   visibles uniquement si showAllPlans=true. Quand l'user cliquez le    */}
            {/*   bouton "+ Changer de formule" doré dans la carte Premium, ça révèle */}
            {/*   directement les autres formules disponibles juste en dessous.       */}
            {showAllPlans && (
              <View style={{ marginTop: 16 }}>
                {/* Tableau comparatif */}
                <View ref={plansWrapRefTop} onLayout={onPlansLayout} style={{ marginBottom: 14 }} collapsable={false}>
                  <PlanComparisonTable
                    currentTier={currentTier}
                    showHeader={false}
                    compact
                  />
                </View>

                {/* Toggle Mensuel/Annuel */}
                <View style={s.billingToggle}>
                  <TouchableOpacity
                    style={[s.billingTab, billingPeriod === 'monthly' && s.billingTabActive]}
                    onPress={() => setBillingPeriod('monthly')}
                    activeOpacity={0.85}
                  >
                    <Text style={[s.billingTabText, billingPeriod === 'monthly' && s.billingTabTextActive]}>
                      Mensuel
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[s.billingTab, billingPeriod === 'yearly' && s.billingTabActive]}
                    onPress={() => setBillingPeriod('yearly')}
                    activeOpacity={0.85}
                  >
                    <Text style={[s.billingTabText, billingPeriod === 'yearly' && s.billingTabTextActive]}>
                      Annuel
                    </Text>
                    <View style={s.billingDiscountBadge}>
                      <Text style={s.billingDiscountText}>{isFR ? '2 mois offerts' : '2 months free'}</Text>
                    </View>
                  </TouchableOpacity>
                </View>

                {/* Cartes des plans (Basic, Premium, Gold) */}
                <View style={s.plansRow}>
                  {(billingPeriod === 'yearly' ? PREMIUM_PLANS_YEARLY : PREMIUM_PLANS).map(plan => {
                    const planTier = plan.tier || plan.id.replace('_yearly', '');
                    const planPeriod = plan.id.includes('_yearly') ? 'yearly' : 'monthly';
                    const isActive = planTier === currentTier && planPeriod === currentPeriod;
                    const isSameTierDiffPeriod = planTier === currentTier && currentPeriod && planPeriod !== currentPeriod;
                    let yearlyDiscount = null;
                    if (planPeriod === 'yearly' && plan.yearly) {
                      const monthlyPlan = PREMIUM_PLANS.find(p => p.tier === planTier);
                      if (monthlyPlan) {
                        const fullPriceYear = monthlyPlan.price * 12;
                        const discountPct = Math.round((1 - plan.yearly / fullPriceYear) * 100);
                        if (discountPct > 0) yearlyDiscount = discountPct;
                      }
                    }
                    return (
                      <TouchableOpacity
                        key={plan.id}
                        style={[s.planCard, plan.popular && s.planCardPopular, isActive && s.planCardActive]}
                        onPress={() => !isActive && handleSubscribe(plan.id)}
                        activeOpacity={isActive ? 1 : 0.8}
                      >
                        {plan.popular && (
                          <View style={s.planBadgeTop}>
                            <Text style={s.planBadgeTopTxt}>{plan.badge || t('popular')}</Text>
                          </View>
                        )}
                        <Text style={[s.planCardLabel, plan.popular && s.planCardLabelPop]}>{plan.label}</Text>
                        {planPeriod === 'yearly' && plan.yearly ? (
                          <>
                            <View style={s.planCardPriceRow}>
                              <Text style={[s.planCardPrice, plan.popular && s.planCardPricePop]}>{plan.yearly}</Text>
                              <Text style={s.planCardCurrency}>€/an</Text>
                            </View>
                            <Text style={s.planCardAnnual}>{isFR ? `soit ${plan.price}€/mois` : `i.e. €${plan.price}/mo`}</Text>
                          </>
                        ) : (
                          <View style={s.planCardPriceRow}>
                            <Text style={[s.planCardPrice, plan.popular && s.planCardPricePop]}>{plan.price}</Text>
                            <Text style={s.planCardCurrency}>€/mois</Text>
                          </View>
                        )}
                        <View style={[s.planCardBtn, plan.popular && s.planCardBtnPop, isActive && s.planCardBtnActive]}>
                          {/* ═══ FIX 2026-04 (v1.7.71) : utilise purchasing au lieu de processingPlan ═══ */}
                          {/* Avant : référence à processingPlan qui n'existe pas comme state */}
                          {/*   → ReferenceError sur iOS (strict mode) → écran blanc.    */}
                          {/* Maintenant : purchasing (state global) qui est true pendant */}
                          {/*   un achat. Le spinner s'affiche sur la carte ACTIVE seulement. */}
                          {purchasing && !isActive
                            ? <ActivityIndicator size="small" color={plan.popular ? '#fff' : '#111'} />
                            : <Text style={[s.planCardBtnTxt, plan.popular && s.planCardBtnTxtPop, isActive && s.planCardBtnTxtActive]}>
                                {isActive
                                  ? t('current_plan_btn')
                                  : isSameTierDiffPeriod
                                    ? (planPeriod === 'yearly' ? t('switch_to_yearly_btn') : t('switch_to_monthly_btn'))
                                    : t('choose')}
                              </Text>}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                <Text style={s.planLegal}>{t('no_engagement')}</Text>
                {/* v1.12.934 — Apple Guideline 3.1.2(c) : disclosure auto-renouvellement
                    + liens EULA/Privacy DANS le flux d'achat (cartes d'abonnement). */}
                <Text style={[s.planLegal, { marginTop: 6 }]}>
                  {lang === 'fr'
                    ? "Abonnement auto-renouvelable. Le paiement est débité lors de la confirmation. Le renouvellement est automatique à la même période et au même tarif, sauf annulation au moins 24 h avant la fin de la période en cours. Gérez ou résiliez votre abonnement dans les réglages de votre compte Apple ID."
                    : "Auto-renewable subscription. Payment is charged upon confirmation of purchase. It renews automatically at the same price and duration unless canceled at least 24 hours before the end of the current period. Manage or cancel your subscription anytime in your Apple ID account settings."}
                </Text>
                <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  <Text onPress={() => openLegalUrl('terms', lang)} style={{ fontSize: 11, color: '#666', textDecorationLine: 'underline' }}>
                    {lang === 'fr' ? "Conditions d'utilisation (EULA)" : 'Terms of Use (EULA)'}
                  </Text>
                  <Text style={{ fontSize: 11, color: '#BDBDBD' }}>{'    ·    '}</Text>
                  <Text onPress={() => openLegalUrl('privacy', lang)} style={{ fontSize: 11, color: '#666', textDecorationLine: 'underline' }}>
                    {t('privacy_policy')}
                  </Text>
                </View>
              </View>
            )}
            </>

          ) : (
            /* ── Version gratuite ── */
            <View>
              {/* Utilisation */}
              <View style={s.usageCard}>
                <Text style={s.usageCardTitle}>{t('usage_today')}</Text>

                {/* ═══ FIX 2026-04 (v1.7.63) : compteur "Looks générés" ajouté ═══ */}
                {/* Avant : le bloc Free n'affichait que Dressing + Analyses IA. */}
                {/*   Conséquence : l'user Free ne voyait pas qu'il avait une limite */}
                {/*   sur les looks/jour et ne savait pas combien il en avait déjà */}
                {/*   généré → frustration "pourquoi ça ne génère plus de looks ?". */}
                {/* Maintenant : on affiche les 3 mêmes compteurs (Dressing / IA / Looks) */}
                {/*   dans tous les tiers, avec les limites correspondantes au tier */}
                {/*   actuel (auto via userLimits qui dérive de getTier()). */}
                {/* Note : statsData.looks donne le nb TOTAL de combinaisons possibles */}
                {/*   depuis le dressing. On le borne à la limite du tier pour que */}
                {/*   "87 / 3" ne s'affiche jamais (sans sens). */}
                {(() => {
                  const looksLimit = (userLimits?.looks_per_day) ?? FREE_LIMITS.looks_per_day;
                  // v1.12.119 — Looks générés = vrai count en cache LooksScreen
                  // capé par la limite du tier (Free=3, Basic=10, Premium/Gold=∞)
                  const looksUsed = isFinite(looksLimit)
                    ? Math.min(statsData.looks, looksLimit)
                    : statsData.looks;
                  // v1.10.19 : Try-on : Free = /sem, abonné = /mois (depuis backend)
                  // v1.12.619 — Pour Free, le try-on est one-shot (bonus onboarding ou
                // add-ons à TTL). Pas de notion de "/week" ni "/month" ni renew.
                // Singulier "try-on" si 1 dispo, pluriel "try-ons" sinon.
                // Pour Basic+, c'est mensuel rolling (anniversaire d'abonnement).
                const tryonAvailableFreeCount = addonsBalance?.tryons || 0;
                const tryonUnit = (currentTier === 'free')
                  ? (tryonAvailableFreeCount === 1 ? 'try-on' : 'try-ons')
                  : (tryonUsage.isPaidTier ? t('usage_unit_per_month') : t('usage_unit_per_week'));
                  // v1.12.617 — Intégrer addonsBalance pour user Free : bonus
                  // onboarding (1 try-on offert) + add-ons achetés doivent
                  // s'afficher dans la barre de progression.
                  const tryonBonusFree = addonsBalance?.tryons || 0;
                  const tryonMaxFree = tryonBonusFree; // Free = 0 + bonus
                  return [
                    { icon:'🧥', label:t('usage_wardrobe'), value: statsData.pieces, max: (addonsBalance?.wardrobeQuota?.totalAvailable) ?? ((userLimits?.wardrobe_items) ?? FREE_LIMITS.wardrobe_items), unit:t('usage_unit_pieces'), isWardrobe: true,
                      // v1.12.955 — Free : inclure le boost +50 / abos wardrobe dans la limite affichée
                      bonusInfo: (addonsBalance?.wardrobeQuota?.bonusQuota || 0) > 0 ? `+${addonsBalance.wardrobeQuota.bonusQuota} slots bonus actifs` : null },
                    { icon:'🤖', label:t('usage_ai'), value: aiUsed, max: ((userLimits?.ai_requests) ?? FREE_LIMITS.ai_requests) + (addonsBalance?.scans || 0), unit:t('usage_unit_per_day'), isAI: true,
                      // v1.12.954 — Free : inclure le bonus add-on scans (packs IAP) dans la limite affichée
                      bonusInfo: (addonsBalance?.scans || 0) > 0 ? `+${addonsBalance.scans} scans bonus actifs` : null },
                    { icon:'✨', label:t('usage_looks_gen'), value: looksUsed, max: looksLimit, unit:t('usage_unit_looks'), isLooks: true },
                    { icon:'🪞', label:t('usage_tryons'), value: tryonUsage.count || 0, max: tryonMaxFree, unit: tryonUnit, isTryon: true,
                      bonusInfo: tryonBonusFree > 0
                        ? (tryonBonusFree === 1
                            ? '🎁 1 try-on offert'
                            : `🎁 ${tryonBonusFree} try-ons disponibles`)
                        : null },
                  ];
                })().map(item => {
                  const isInf = item.max === Infinity || !isFinite(item.max);
                  const pct = isInf ? 0 : Math.min(100, Math.round((item.value / item.max) * 100));
                  const over = !isInf && item.value >= item.max;
                  // v1.12.196 : message élégant pour Try-on free (pas de limite atteinte)
                  // v1.12.586 — bugfix : se baser sur currentTier (à jour via reloadProfile)
                // au lieu de tryonUsage qui peut être stale au moment du render.
                // v1.12.617 — EXCLURE ce message si Free a un bonus actif :
                // afficher alors la barre normale pour qu'il puisse voir le compteur.
                const tryonsAvailableFree = (addonsBalance?.tryons || 0);
                const isTryonFree = item.isTryon
                  && (currentTier === 'free' || ((userLimits?.tryons_per_month) ?? 0) === 0)
                  && tryonsAvailableFree === 0;
                  if (isTryonFree) {
                    return (
                      <View key={item.label} style={s.usageItem}>
                        <View style={s.usageItemTop}>
                          <View style={s.usageItemLeft}>
                            <Text style={s.usageItemIcon}>{item.icon}</Text>
                            <Text style={s.usageItemLabel}>{item.label}</Text>
                          </View>
                          <Text style={{ fontSize: 11, fontWeight: '700', color: '#B8860B', letterSpacing: 0.5 }}>
                            {lang === 'en' ? '✨ SUBSCRIBERS' : '✨ ABONNÉS'}
                          </Text>
                        </View>
                        <View style={{
                          flexDirection: 'row', alignItems: 'center', gap: 8,
                          marginTop: 8, padding: 10,
                          backgroundColor: '#FFF7E0',
                          borderWidth: 1, borderColor: '#F0DCA0', borderRadius: 8,
                        }}>
                          <Text style={{ fontSize: 16 }}>🪞</Text>
                          <Text style={{ flex: 1, fontSize: 12, color: '#8B6F3D', lineHeight: 17 }}>
                            {lang === 'en'
                              ? 'Virtual try-on on a model is reserved for subscribers (Basic, Premium or Gold). Choose a plan to use it.'
                              : 'L\'essayage virtuel sur mannequin est réservé aux abonnés (Basic, Premium ou Gold). Choisissez une formule pour l\'utiliser.'}
                          </Text>
                        </View>
                      </View>
                    );
                  }
                  // v1.10.52 : Pour Try-ons, afficher la date de reset avec heure/minute
                  // basée sur l'anniversaire d'abonnement (timestamp complet TIMESTAMPTZ).
                  // v1.12.619 — Free n'a PAS de renewal (try-on one-shot/TTL).
                  let tryonResetMsg = null;
                  if (item.isTryon && tryonUsage.nextResetDate && currentTier !== 'free') {
                    tryonResetMsg = formatRenewalTime(tryonUsage.nextResetDate, lang, t);
                  }
                  return (
                    <View key={item.label} style={s.usageItem}>
                      <View style={s.usageItemTop}>
                        <View style={s.usageItemLeft}>
                          <Text style={s.usageItemIcon}>{item.icon}</Text>
                          <Text style={s.usageItemLabel}>{item.label}</Text>
                        </View>
                        <Text style={[s.usageItemVal, over && s.usageItemValOver]}>
                          {item.value} <Text style={s.usageItemMax}>{isInf ? `/ ∞ ${item.unit}` : `/ ${item.max} ${item.unit}`}</Text>
                        </Text>
                      </View>
                      <View style={s.usageItemBar}>
                        <View style={[s.usageItemBarFill, { width: (isInf ? 10 : pct)+'%' }, over && s.usageItemBarOver]} />
                      </View>
                      {item.isTryon && tryonResetMsg && !over && (
                        <Text style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                          {tryonResetMsg}
                        </Text>
                      )}
                      {over && !item.isLooks && (
                        <Text style={{ fontSize: 11, color: '#D32F2F', marginTop: 4 }}>
                          {lang === 'en'
                            ? (item.isWardrobe
                                ? 'Limit reached. Buy a +50 Boost or upgrade to a higher plan to add more items.'
                                : item.isAI
                                  ? 'Limit reached. AI scans reset every night at midnight (Paris), or upgrade to a higher plan for more daily scans.'
                                : item.isTryon && tryonResetMsg
                                  ? `Limit reached. ${tryonResetMsg.replace('🔄 ', '')} or upgrade to a higher plan.`
                                  : 'Limit reached. Quotas reset on your subscription anniversary, or upgrade to a higher plan.')
                            : (item.isWardrobe
                                ? 'Limite atteinte. Achetez un Boost +50 ou passez à un plan supérieur pour ajouter plus d\'articles.'
                                : item.isAI
                                  ? 'Limite atteinte. Vos analyses IA sont réinitialisées chaque nuit à minuit (Paris), ou passez à un plan supérieur pour plus de scans quotidiens.'
                                : item.isTryon && tryonResetMsg
                                  ? `Limite atteinte. ${tryonResetMsg.replace('🔄 ', '')} ou passez à un plan supérieur.`
                                  : 'Limite atteinte. Quotas réinitialisés à l\'anniversaire de votre abonnement, ou passez à un plan supérieur.')}
                        </Text>
                      )}
                      {over && item.isLooks && (
                        <Text style={{ fontSize: 11, color: '#888', marginTop: 4, fontStyle: 'italic' }}>
                          {lang === 'en'
                            ? `Capped at ${item.max} looks per your plan. Upgrade for more.`
                            : `Plafonné à ${item.max} looks selon votre formule. Passez à un plan supérieur pour en voir plus.`}
                        </Text>
                      )}
                    </View>
                  );
                })}
              </View>

              {/* Bandeau "Abonnement expiré" — visible si l'user avait un abonnement
                  qui n'est plus valide (date dépassée). On affiche un état neutre
                  élégant au lieu de "PLAN ACTIF" + bandeau rouge incohérent. */}
              {currentTier === 'free' && isSubscriptionExpired(user) && (() => {
                // Identifier le tier précédent depuis l'historique côté user
                // (user.tier peut être conservé même après expiration, sinon on dit
                // "votre abonnement" générique)
                const previousTier = user?.tier && user.tier !== 'free' ? user.tier : null;
                const previousLabel = previousTier
                  ? (previousTier === 'basic' ? 'Basic'
                  : previousTier === 'premium' ? 'Premium'
                  : previousTier === 'gold' ? 'Gold' : null)
                  : null;
                const expiresAt = user?.premiumExpiresAt;
                let dateStr = '';
                if (expiresAt) {
                  const d = new Date(expiresAt);
                  if (!isNaN(d.getTime())) {
                    const months = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
                    dateStr = `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
                  }
                }
                return (
                  <View style={s.expiredCard}>
                    <View style={s.expiredHeaderRow}>
                      <Text style={s.expiredIcon}>⏱</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={s.expiredTitle}>Abonnement expiré</Text>
                        <Text style={s.expiredSubtitle}>
                          {previousLabel
                            ? `Votre abonnement ${previousLabel}${dateStr ? ` a pris fin le ${dateStr}` : ' a expiré'}.`
                            : `Votre abonnement${dateStr ? ` a pris fin le ${dateStr}` : ' a expiré'}.`}
                        </Text>
                        <Text style={s.expiredDescription}>
                          Vous êtes actuellement sur le plan gratuit. Choisissez une formule ci-dessous pour réactiver les fonctionnalités premium.
                        </Text>
                      </View>
                    </View>
                    {/* CTA Renouveler — affiche + scrolle vers le TABLEAU DES PLANS
                        (tiers payants), pas vers le bas (logout). v1.12.971 */}
                    <TouchableOpacity
                      onPress={() => {
                        setShowAllPlans(true);
                        setScrollToPlansRequested(true);
                      }}
                      style={s.expiredCta}
                      activeOpacity={0.85}
                    >
                      <Text style={s.expiredCtaText}>Renouveler mon abonnement</Text>
                      <Text style={s.expiredCtaArrow}>↓</Text>
                    </TouchableOpacity>
                  </View>
                );
              })()}

              {/* ═══ v1.8.12 : carte verte "Plan actif" supprimée car doublon ═══
                   Avant : la carte premiumCard (noire/dorée) ET activePlanCard
                     (verte/blanche) s'affichaient toutes les deux pour un user
                     abonné → confusion visuelle.
                   Maintenant : on garde uniquement la carte premiumCard noire
                     (rendue ailleurs dans le composant) avec les avantages
                     détaillés du tier actif. */}

              {/* ═══ v1.8.12 : bouton "Changer de formule" supprimé ici car doublon ═══
                   Le bouton équivalent est déjà dans la carte premiumCard (ligne ~1702),
                   avec le même comportement (setShowAllPlans + scroll). Garder les deux
                   créait de la confusion visuelle pour l'user. */}

              {/* Tableau comparatif — visible quand on peut choisir un plan */}
              {(currentTier === 'free' || showAllPlans) && (
                <View ref={plansWrapRefBottom} onLayout={onPlansLayout} style={{ marginBottom: 14 }} collapsable={false}>
                  <PlanComparisonTable
                    currentTier={currentTier}
                    showHeader={false}
                    compact
                  />
                </View>
              )}

              {/* Toggle Mensuel/Annuel affichable si showAllPlans ou free */}
              {(currentTier === 'free' || showAllPlans) && (
                <View style={s.billingToggle}>
                  <TouchableOpacity
                    style={[s.billingTab, billingPeriod === 'monthly' && s.billingTabActive]}
                    onPress={() => setBillingPeriod('monthly')}
                    activeOpacity={0.85}
                  >
                    <Text style={[s.billingTabText, billingPeriod === 'monthly' && s.billingTabTextActive]}>
                      Mensuel
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[s.billingTab, billingPeriod === 'yearly' && s.billingTabActive]}
                    onPress={() => setBillingPeriod('yearly')}
                    activeOpacity={0.85}
                  >
                    <Text style={[s.billingTabText, billingPeriod === 'yearly' && s.billingTabTextActive]}>
                      Annuel
                    </Text>
                    <View style={s.billingDiscountBadge}>
                      <Text style={s.billingDiscountText}>{isFR ? '2 mois offerts' : '2 months free'}</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              )}

              {/* Plans — filtrés selon l'abonnement actif */}
              {/* Si abonné et !showAllPlans → aucune card affichée (le nouveau bandeau suffit) */}
              {(currentTier === 'free' || showAllPlans) && (
              <View style={s.plansRow}>
                {(billingPeriod === 'yearly' ? PREMIUM_PLANS_YEARLY : PREMIUM_PLANS)
                  .map(plan => {
                    const planTier = plan.tier || plan.id.replace('_yearly', '');
                    const planPeriod = plan.id.includes('_yearly') ? 'yearly' : 'monthly';
                    // ═══ FIX 2026-04 : isActive = même TIER + même PÉRIODE ═══
                    // Avant : isActive = tier seul → si user en monthly_basic, la carte
                    //   basic_yearly était aussi affichée comme "ACTIF" → impossible de
                    //   passer du mensuel à l'annuel (et inversement).
                    // Maintenant : on autorise l'upgrade vers une autre période du même
                    //   tier (ex: basic monthly → basic yearly) avec un libellé clair.
                    const isActive = planTier === currentTier && planPeriod === currentPeriod;
                    const isSameTierDiffPeriod = planTier === currentTier && currentPeriod && planPeriod !== currentPeriod;
                    // ═══ Calcul du % de réduction pour les plans annuels ═══
                    // Compare le prix annuel vs (prix mensuel × 12).
                    // PREMIUM_PLANS_YEARLY.price est déjà l'équivalent mensuel.
                    let yearlyDiscount = null;
                    if (planPeriod === 'yearly' && plan.yearly) {
                      const monthlyPlan = require('../services/PremiumService').PREMIUM_PLANS.find(p => p.tier === planTier);
                      if (monthlyPlan) {
                        const fullPriceYear = monthlyPlan.price * 12;
                        const discountPct = Math.round((1 - plan.yearly / fullPriceYear) * 100);
                        if (discountPct > 0) yearlyDiscount = discountPct;
                      }
                    }
                    return (
                  <TouchableOpacity key={plan.id} style={[s.planCard, plan.popular && s.planCardPopular, isActive && s.planCardActive]}
                    onPress={() => !isActive && handleSubscribe(plan.id)} activeOpacity={isActive ? 1 : 0.8}>
                    {plan.badge && !isActive && (
                      <View style={s.planBadgeTop}>
                        <Text style={s.planBadgeTopTxt}>{plan.badge}</Text>
                      </View>
                    )}
                    {isActive && (
                      <View style={[s.planBadgeTop, { backgroundColor: '#10B981' }]}>
                        <Text style={s.planBadgeTopTxt}>{t('active_caps')}</Text>
                      </View>
                    )}
                    <Text style={[s.planCardLabel, plan.popular && s.planCardLabelPop]}>{plan.label}</Text>
                    {planPeriod === 'yearly' && plan.yearly ? (
                      <>
                        <View style={s.planCardPriceRow}>
                          <Text style={[s.planCardPrice, plan.popular && s.planCardPricePop]}>{plan.yearly}</Text>
                          <Text style={s.planCardCurrency}>€/an</Text>
                        </View>
                        <Text style={s.planCardAnnual}>{isFR ? `soit ${plan.price}€/mois` : `i.e. €${plan.price}/mo`}</Text>
                      </>
                    ) : (
                      <View style={s.planCardPriceRow}>
                        <Text style={[s.planCardPrice, plan.popular && s.planCardPricePop]}>{plan.price}</Text>
                        <Text style={s.planCardCurrency}>€/mois</Text>
                      </View>
                    )}
                    {/* Features résumées selon tier */}
                    <View style={s.planFeats}>
                      {(() => {
                        const tierId = plan.tier || plan.id.replace('_yearly', '');
                        // ═══ FIX 2026-04 : feats dérivés de TIERS (source de vérité) ═══
                        // Avant : liste hardcodée → risque de divergence avec TIERS
                        //   à chaque modification des limites métier.
                        // Maintenant : on lit directement TIERS et formate les valeurs.
                        const t = TIERS[tierId];
                        if (!t) return null;
                        const fmt = (n) => n === Infinity ? '∞' : n;
                        const wardrobeStr = `${fmt(t.wardrobe_items)} article${t.wardrobe_items > 1 || t.wardrobe_items === Infinity ? 's' : ''}`;
                        const aiStr       = `${fmt(t.ai_requests)} IA/jour`;
                        const looksStr    = t.looks_per_day === Infinity ? '∞ looks' : `${t.looks_per_day} looks/jour`;
                        // v1.12.920 — Voyages masqués (feature reportée post-lancement)
                        const tripsStr    = null;
                        // v1.12.816 — Aligné sur PlanComparisonTable (displayMaxEurPer100)
                        const _maxEur2 = t.cashback?.displayMaxEurPer100 ?? 0;
                        const _exStr2 = lang === 'en'
                          ? `€${_maxEur2.toFixed(2)}`
                          : `${_maxEur2.toFixed(2).replace('.', ',')} €`;
                        // v1.12.921 — Texte raccourci pour tenir dans les cartes mobiles étroites
                        const cashbackStr = lang === 'en'
                          ? `💰 ${_exStr2} / €100`
                          : `💰 ${_exStr2} / 100€`;
                        const items = [wardrobeStr, aiStr, looksStr];
                        if (tripsStr) items.push(tripsStr);
                        items.push(cashbackStr);
                        if (tierId === 'gold') items.push('👑 Support VIP');
                        return items.map((f, i) => (
                          <Text key={i} style={s.planFeatsItem}>· {f}</Text>
                        ));
                      })()}
                    </View>
                    <View style={[s.planCardBtn, plan.popular && s.planCardBtnPop, isActive && s.planCardBtnActive]}>
                      {purchasing
                        ? <ActivityIndicator size="small" color={plan.popular ? "#fff" : "#888"} />
                        : <Text style={[s.planCardBtnTxt, plan.popular && s.planCardBtnTxtPop, isActive && s.planCardBtnTxtActive]}>
                            {isActive
                              ? t('current_plan_btn')
                              : isSameTierDiffPeriod
                                ? (planPeriod === 'yearly' ? t('switch_to_yearly_btn') : t('switch_to_monthly_btn'))
                                : t('choose')}
                          </Text>}
                    </View>
                  </TouchableOpacity>
                    );
                  })}
              </View>
              )}

              <Text style={s.planLegal}>{t('no_engagement')}</Text>
                {/* v1.12.934 — Apple Guideline 3.1.2(c) : disclosure auto-renouvellement
                    + liens EULA/Privacy DANS le flux d'achat (cartes d'abonnement). */}
                <Text style={[s.planLegal, { marginTop: 6 }]}>
                  {lang === 'fr'
                    ? "Abonnement auto-renouvelable. Le paiement est débité lors de la confirmation. Le renouvellement est automatique à la même période et au même tarif, sauf annulation au moins 24 h avant la fin de la période en cours. Gérez ou résiliez votre abonnement dans les réglages de votre compte Apple ID."
                    : "Auto-renewable subscription. Payment is charged upon confirmation of purchase. It renews automatically at the same price and duration unless canceled at least 24 hours before the end of the current period. Manage or cancel your subscription anytime in your Apple ID account settings."}
                </Text>
                <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  <Text onPress={() => openLegalUrl('terms', lang)} style={{ fontSize: 11, color: '#666', textDecorationLine: 'underline' }}>
                    {lang === 'fr' ? "Conditions d'utilisation (EULA)" : 'Terms of Use (EULA)'}
                  </Text>
                  <Text style={{ fontSize: 11, color: '#BDBDBD' }}>{'    ·    '}</Text>
                  <Text onPress={() => openLegalUrl('privacy', lang)} style={{ fontSize: 11, color: '#666', textDecorationLine: 'underline' }}>
                    {t('privacy_policy')}
                  </Text>
                </View>
              <TouchableOpacity onPress={handleRestorePurchases} style={{ alignItems: 'center', paddingTop: 10 }}>
                {restoring
                  ? <ActivityIndicator size="small" color="#999" />
                  : <Text style={{ fontSize: 12, color: '#999', textDecorationLine: 'underline' }}>
                      {t('restore_purchases')}
                    </Text>}
              </TouchableOpacity>

              {/* [MODE TEST] Bouton pour revenir en Free sans aucun paiement */}
              {currentTier !== 'free' && (
                <TouchableOpacity
                  onPress={() => {
                    Alert.alert(
                      '[MODE TEST] Repasser en Free ?',
                      'Cette action est uniquement pour tester. Vous redeviendrez free instantanément.',
                      [
                        { text: 'Annuler', style: 'cancel' },
                        {
                          text: 'Confirmer',
                          style: 'destructive',
                          onPress: () => handleSubscribe('free'),
                        },
                      ]
                    );
                  }}
                  style={{ alignItems: 'center', paddingTop: 14, paddingBottom: 4 }}
                >
                  <Text style={{ fontSize: 11, color: '#E74C3C', letterSpacing: 1, textTransform: 'uppercase' }}>
                    [Test] Repasser en Free
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

        {/* v1.12.175 : Mon mannequin perso (photo full body pour Vertex VTO) */}
        {hasPersonalMannequin && (
          <View style={s.aboutSection}>
            <Text style={s.aboutLabel}>{t('my_mannequin_caps')}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 16 }}>
              {personalMannequinUrl ? (
                <Image
                  source={{ uri: personalMannequinUrl }}
                  style={{ width: 64, height: 86, borderRadius: 8, backgroundColor: '#EEE', borderWidth: 1, borderColor: '#EAE6DF' }}
                  resizeMode="cover"
                />
              ) : (
                <View style={{ width: 64, height: 86, borderRadius: 8, backgroundColor: '#EEE', alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 24 }}>👤</Text>
                </View>
              )}
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: '#1A1A1A' }}>{t('your_personal_photo')}</Text>
                <Text style={{ fontSize: 11, color: '#6B6B6B', marginTop: 2, lineHeight: 16 }}>
                  {t('mannequin_explanation')}
                </Text>
              </View>
            </View>
            <TouchableOpacity
              style={s.aboutRow}
              activeOpacity={0.7}
              onPress={() => {
                showAppAlert(
                  'Supprimer ma photo ?',
                  "Votre photo ne sera plus utilisée pour les essayages virtuels et sera effacée de nos serveurs.\n\n⚠️ Limitation : 1 photo perso / 7 jours. Si vous avez uploadé votre photo récemment, vous devrez attendre la fin de la fenêtre de 7 j pour en créer une nouvelle.",
                  [
                    { text: t('cancel'), style: 'cancel' },
                    {
                      text: 'Supprimer',
                      style: 'destructive',
                      onPress: async () => {
                        try {
                          const { deletePersonalMannequin } = require('../services/TryOnService');
                          await deletePersonalMannequin(user.accessToken);
                          setHasPersonalMannequin(false);
                          setPersonalMannequinUrl(null);
                          showAppAlert(t('photo_deleted'), t('photo_deleted_msg'), [{ text: 'OK' }]);
                        } catch (e) {
                          showAppAlert(t('error'), t('delete_failed').replace('{err}', e.message), [{ text: 'OK' }]);
                        }
                      },
                    },
                  ],
                );
              }}
            >
              <Text style={[s.aboutRowText, { color: '#E53935' }]}>{t('delete_personal_photo')}</Text>
              <Text style={s.aboutRowChevron}>›</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* À propos — liens légaux */}
        <View style={s.aboutSection}>
          <Text style={s.aboutLabel}>{t('about_caps')}</Text>
          <TouchableOpacity
            style={s.aboutRow}
            onPress={() => openLegalUrl('terms', lang)}
            activeOpacity={0.7}
          >
            <Text style={s.aboutRowText}>{t('terms_conditions')}</Text>
            <Text style={s.aboutRowChevron}>›</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={s.aboutRow}
            onPress={() => openLegalUrl('privacy', lang)}
            activeOpacity={0.7}
          >
            <Text style={s.aboutRowText}>{t('privacy_policy')}</Text>
            <Text style={s.aboutRowChevron}>›</Text>
          </TouchableOpacity>
          {/* v1.12.940 — Apple 1.1 : accès aux règles communautaires */}
          <TouchableOpacity
            style={s.aboutRow}
            onPress={() => openLegalUrl('community', lang)}
            activeOpacity={0.7}
          >
            <Text style={s.aboutRowText}>{lang === 'en' ? 'Community Guidelines' : 'Règles communautaires'}</Text>
            <Text style={s.aboutRowChevron}>›</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={s.aboutRow}
            onPress={() => openLegalUrl('cgv', lang)}
            activeOpacity={0.7}
          >
            <Text style={s.aboutRowText}>{t('terms_of_sale')}</Text>
            <Text style={s.aboutRowChevron}>›</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={s.aboutRow}
            onPress={() => openLegalUrl('mentions', lang)}
            activeOpacity={0.7}
          >
            <Text style={s.aboutRowText}>{lang === 'en' ? 'Legal notice' : 'Mentions légales'}</Text>
            <Text style={s.aboutRowChevron}>›</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={s.aboutRow}
            onPress={() => openLegalUrl('cookies', lang)}
            activeOpacity={0.7}
          >
            <Text style={s.aboutRowText}>{lang === 'en' ? 'Cookies policy' : 'Politique cookies'}</Text>
            <Text style={s.aboutRowChevron}>›</Text>
          </TouchableOpacity>
          {/* v1.12.967 — Toggle RGPD : mesure d'audience anonyme (GA4). OFF = opt-out total. */}
          <View style={[s.aboutRow, { alignItems: 'center' }]}>
            <View style={{ flex: 1, paddingRight: 12 }}>
              <Text style={s.aboutRowText}>{lang === 'en' ? 'Anonymous audience measurement' : "Mesure d'audience anonyme"}</Text>
              <Text style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                {lang === 'en'
                  ? 'Google Analytics — helps improve the app. No ads, no resale.'
                  : "Google Analytics — aide à améliorer l'app. Aucune publicité, aucune revente."}
              </Text>
            </View>
            <Switch
              value={analyticsOn}
              onValueChange={handleToggleAnalytics}
              trackColor={{ false: '#ccc', true: '#B8860B' }}
              thumbColor="#fff"
            />
          </View>
          {/* v1.12.182 — Portabilité RGPD (art. 20) : export JSON de toutes les données */}
          <TouchableOpacity
            style={s.aboutRow}
            activeOpacity={0.7}
            onPress={async () => {
              try {
                const token = user?.accessToken;
                if (!token) { showAppAlert(t('login_required'), t('reconnect_to_export')); return; }
                showAppAlert(t('export_in_progress'), t('downloading_personal_data'));
                const { AWS_CONFIG } = require('../config/aws_config');
                const res = await fetch(`${AWS_CONFIG.apiUrl}/profile/export`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const json = JSON.stringify(data, null, 2);
                if (Platform.OS === 'web' && typeof document !== 'undefined') {
                  const blob = new Blob([json], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `mytenue-export-${new Date().toISOString().slice(0,10)}.json`;
                  document.body.appendChild(a); a.click(); a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                  showAppAlert(t('export_ready'), t('export_downloaded_summary')
                    .replace('{items}', data.counts?.wardrobe_items || 0)
                    .replace('{looks}', data.counts?.looks || 0)
                    .replace('{tryons}', data.counts?.tryons || 0), [{ text: 'OK' }]);
                } else {
                  // Mobile : partager via Share API
                  const { Share } = require('react-native');
                  await Share.share({ message: json, title: 'Export MyTenue' });
                }
              } catch (e) {
                showAppAlert(t('error'), t('export_failed').replace('{err}', e.message), [{ text: 'OK' }]);
              }
            }}
          >
            <Text style={s.aboutRowText}>{t('export_my_data')}</Text>
            <Text style={s.aboutRowChevron}>›</Text>
          </TouchableOpacity>
          {Platform.OS === 'web' && (
            <TouchableOpacity
              style={s.aboutRow}
              activeOpacity={0.7}
              onPress={() => {
                try {
                  if (typeof window !== 'undefined' && typeof window.mytenueResetCookieConsent === 'function') {
                    window.mytenueResetCookieConsent();
                  } else {
                    showAppAlert('Cookies', "Le bandeau cookies sera proposé au prochain chargement.", [{ text: 'OK' }]);
                  }
                } catch {}
              }}
            >
              <Text style={s.aboutRowText}>{t('cookie_preferences')}</Text>
              <Text style={s.aboutRowChevron}>›</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* ═══ v1.12.990 — Suivez-nous (Instagram + TikTok) ═══ */}
        <View style={{ alignItems: 'center', marginTop: 26, marginBottom: 10 }}>
          <Text style={{ fontSize: 13, fontWeight: '700', color: theme.text, marginBottom: 12, letterSpacing: 0.3 }}>
            {t('follow_us')}
          </Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => { try { Linking.openURL('https://www.instagram.com/mytenueapp'); } catch (e) {} }}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 18, paddingVertical: 11, borderRadius: 24, backgroundColor: '#E4405F' }}
            >
              <Ionicons name="logo-instagram" size={20} color="#FFFFFF" />
              <Text style={{ color: '#FFFFFF', fontWeight: '700', fontSize: 13 }}>Instagram</Text>
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => { try { Linking.openURL('https://www.tiktok.com/@mytenue'); } catch (e) {} }}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 18, paddingVertical: 11, borderRadius: 24, backgroundColor: '#000000' }}
            >
              <Ionicons name="logo-tiktok" size={20} color="#FFFFFF" />
              <Text style={{ color: '#FFFFFF', fontWeight: '700', fontSize: 13 }}>TikTok</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Sign out — v1.12.117 : sur web, Alert.alert multi-bouton ne marche
            pas (mappe sur window.alert qui n'a qu'OK). On utilise confirm(). */}
        <TouchableOpacity
          style={s.signOut}
          onPress={() => {
            // v1.12.118 — popup AppDialog brandé MyTenue (cross-platform)
            showAppAlert(
              t('sign_out'),
              t('sign_out_q'),
              [
                { text: t('cancel'),   style: 'cancel'      },
                { text: t('sign_out'), style: 'destructive', onPress: signOut },
              ],
            );
          }}
        >
          <Text style={s.signOutText}>{t('sign_out')}</Text>
        </TouchableOpacity>

        {/* ═══ Suppression de compte (RGPD / App Store §5.1.1(v)) ═══ */}
        {/* Bouton discret sous "Se déconnecter". Double confirmation pour
            éviter toute suppression accidentelle. */}
        <TouchableOpacity
          style={s.deleteAccount}
          onPress={() => {
            // v1.12.611 — Remplace window.confirm natif par une vraie modale app
            // (AppModal). Le flow 2 étapes est géré par DEUX rendus successifs
            // du MÊME modal (deleteStep change), donc plus de cascade fragile.
            console.log('[Profile] 🗑 delete account button clicked');
            setDeleteStep('step1');
          }}
        >
          <Text style={s.deleteAccountText}>{t('delete_account')}</Text>
        </TouchableOpacity>

        {/* ═══ Version dynamique (v1.7.95) — avant : v1.7.74 hardcod\u00e9 → mensonger ═══ */}
        <Text style={s.version}>
          MyTenue v{require('../../app.json').expo.version} · {t('developed_with')}
        </Text>

      </ScrollView>
      <PremiumModal
        visible={showPremium}
        onClose={() => setShowPremium(false)}
        trigger="wardrobe"
        onSubscribe={handleSubscribe}
      />
      {/* v1.12.847 — Modal Utiliser mes Crédits Mytenue */}
      <UseCreditsModal
        visible={showUseCreditsModal}
        onClose={() => setShowUseCreditsModal(false)}
        currentTier={currentTier || user?.tier || 'free'}
        onUsed={() => { /* refresh balance via useEffect dep */ }}
      />
      {/* ═══ v1.8.5 : popup d'upgrade depuis le banner Cashback ═══ */}
      {/* ═══ v1.8.10 : feature dynamique selon le contexte ═══ */}
      <UpgradeModal
        visible={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={currentTier || 'free'}
        feature={upgradeModalFeature}
        onSubscribe={async (planId) => {
          setShowUpgradeModal(false);
          await handleSubscribe(planId);
        }}
      />
      {/* v1.12.608 — V5.6 : confirmation downgrade Free avec preview des impacts */}
      <DowngradeFreeConfirmModal
        visible={showDowngradeConfirm}
        onClose={() => setShowDowngradeConfirm(false)}
        currentItemCount={statsData?.pieces || 0}
        onConfirm={performCancelSubscription}
      />
      {/* v1.12.613 — Modal suppression compte. Callback stabilisé pour éviter
           que AppModal useEffect cleanup ferme la modal à chaque re-render. */}
      <AppModal
        visible={deleteStep !== 'idle'}
        transparent
        animationType="fade"
        webBackdropClosable={false}
        onRequestClose={deleteModalClose}
      >
        <View style={[
          { padding: 20 },
          // Web : AppModal centre déjà (overlay + scroll). Native : RN Modal est un
          // passthrough → on centre nous-mêmes verticalement, sinon le popup colle en
          // haut (sous la barre d'état : heure/wifi). + léger backdrop.
          Platform.OS === 'web'
            ? { alignSelf: 'center', width: '100%', maxWidth: 420 }
            : { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.45)' },
        ]}>
          <View style={{
            width: '100%', maxWidth: 420,
            backgroundColor: '#FFF', borderRadius: 16, padding: 22,
            borderWidth: 2, borderColor: deleteStep === 'error' ? '#C0392B' : '#FECACA',
            shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 30, shadowOffset: { width: 0, height: 10 },
            elevation: 12,
          }}>
            <Text style={{ fontSize: 20, fontWeight: '800', color: '#1a1f36', textAlign: 'center', marginBottom: 10 }}>
              {deleteStep === 'error' ? '❌' : '⚠️'} {t('delete_account')}
            </Text>
            {deleteStep === 'error' ? (
              <>
                <Text style={{ fontSize: 13, color: '#C0392B', lineHeight: 19, textAlign: 'center', marginBottom: 8, fontWeight: '700' }}>
                  {t('delete_account_error')}
                </Text>
                {!!deleteError && (
                  <Text style={{ fontSize: 11, color: '#666', lineHeight: 16, textAlign: 'center', marginBottom: 20, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
                    {deleteError}
                  </Text>
                )}
              </>
            ) : (
              <Text style={{ fontSize: 13, color: '#444', lineHeight: 19, textAlign: 'center', marginBottom: 20 }}>
                {deleteStep === 'step2'
                  ? t('delete_account_q2')
                  : t('delete_account_q1')}
              </Text>
            )}
            {deleteStep === 'step2' && (
              <Text style={{ fontSize: 11, color: '#C0392B', textAlign: 'center', fontWeight: '700', marginBottom: 14, letterSpacing: 0.3 }}>
                {lang === 'en'
                  ? 'FINAL CONFIRMATION — This cannot be undone.'
                  : 'DERNIÈRE CONFIRMATION — Action irréversible.'}
              </Text>
            )}
            <View style={{ gap: 10 }}>
              {deleteStep !== 'error' && (
                <TouchableOpacity
                  style={{ backgroundColor: '#C0392B', paddingVertical: 13, borderRadius: 10, alignItems: 'center', opacity: deleteStep === 'deleting' ? 0.6 : 1 }}
                  disabled={deleteStep === 'deleting'}
                  onPress={async () => {
                    if (deleteStep === 'step1') {
                      setDeleteStep('step2');
                      return;
                    }
                    // step2 → exécution
                    setDeleteStep('deleting');
                    try {
                      console.log('[Profile] 🗑 calling deleteAccount()...');
                      let result = await deleteAccount();
                      console.log('[Profile] 🗑 deleteAccount result:', result);
                      // v1.12.897 — Si abonnement actif, demander confirmation puis retry avec force
                      if (result?.warning === 'active_subscription') {
                        const msg = lang === 'en'
                          ? 'You have an active subscription. Cancel it via App Store / Google Play first.\n\nIf you want to proceed anyway, the subscription will remain charged by the store until you cancel it manually.\n\nContinue with deletion?'
                          : 'Vous avez un abonnement actif. Annulez-le d\'abord via l\'App Store / Google Play.\n\nSi vous souhaitez quand même continuer, l\'abonnement restera prélevé par le store tant que vous ne l\'annulez pas manuellement.\n\nPoursuivre la suppression ?';
                        const confirmed = Platform.OS === 'web'
                          ? (typeof window !== 'undefined' && window.confirm(msg))
                          : await new Promise(res => Alert.alert(
                              lang === 'en' ? 'Active subscription' : 'Abonnement actif',
                              msg,
                              [
                                { text: lang === 'en' ? 'Cancel' : 'Annuler', style: 'cancel', onPress: () => res(false) },
                                { text: lang === 'en' ? 'Delete anyway' : 'Supprimer quand même', style: 'destructive', onPress: () => res(true) },
                              ],
                            ));
                        if (!confirmed) {
                          setDeleteStep('idle');
                          return;
                        }
                        // Retry avec force=true
                        result = await deleteAccount({ force: true });
                      }
                      // deleteAccount() fait signOut → l'app va naviguer vers Login.
                      // On laisse 'deleting' (la modal disparaîtra avec l'unmount).
                    } catch (e) {
                      console.error('[Profile] 🗑 deleteAccount error:', e?.message);
                      setDeleteError(e?.message || 'Unknown error');
                      setDeleteStep('error');
                    }
                  }}
                >
                  {deleteStep === 'deleting' ? (
                    <ActivityIndicator color="#FFF" size="small" />
                  ) : (
                    <Text style={{ color: '#FFF', fontWeight: '800', fontSize: 14, letterSpacing: 0.3 }}>
                      {deleteStep === 'step2' ? t('delete_confirm') : (lang === 'en' ? 'Continue' : 'Continuer')}
                    </Text>
                  )}
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={{ paddingVertical: 12, alignItems: 'center' }}
                disabled={deleteStep === 'deleting'}
                onPress={() => { setDeleteError(''); setDeleteStep('idle'); }}
              >
                <Text style={{ color: '#666', fontWeight: '600', fontSize: 13 }}>
                  {deleteStep === 'error' ? 'OK' : t('cancel')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </AppModal>
      <RestoreResultModal
        visible={!!restoreResult}
        kind={restoreResult?.kind}
        details={restoreResult?.details}
        onClose={() => setRestoreResult(null)}
      />
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe:          { flex: 1, backgroundColor: '#FFFFFF' },
  topbar:        { paddingHorizontal: spacing.xl, paddingTop: spacing.md, paddingBottom: spacing.md },
  topbarBadge:   { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  title:         { fontSize: 22, fontWeight: '300', letterSpacing: 0.3 },

  // ═══ HERO PROFIL PREMIUM ═══
  heroProfile:        { position: 'relative', marginHorizontal: spacing.xl, marginTop: spacing.md, marginBottom: spacing.lg, borderRadius: radius.lg, overflow: 'hidden', backgroundColor: '#FFF9E6', borderWidth: 1, borderColor: '#F5DEB3' },
  heroProfileBg:      { position: 'absolute', top: 0, left: 0, right: 0, height: 80, backgroundColor: 'rgba(212, 175, 55, 0.08)' },
  heroProfileContent: { alignItems: 'center', padding: spacing.xl },
  avatarHero:         { width: 84, height: 84, borderRadius: 42, borderWidth: 3, borderColor: '#FFF', backgroundColor: '#F5F5F5' },
  avatarHeroPlaceholder: { width: 84, height: 84, borderRadius: 42, borderWidth: 3, borderColor: '#FFF', backgroundColor: '#D4AF37', alignItems: 'center', justifyContent: 'center' },
  avatarHeroText:     { fontSize: 28, fontWeight: '300', color: '#FFF', letterSpacing: 2 },
  heroProfileName:    { fontSize: 22, fontWeight: '500', letterSpacing: 0.5, marginTop: spacing.md, textAlign: 'center' },
  heroProfileEmail:   { fontSize: 12, color: colors.text.muted, letterSpacing: 0.3, marginTop: 4, textAlign: 'center' },
  heroProfileCityRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 },
  heroProfileCityIcon:{ fontSize: 11 },
  heroProfileCity:    { fontSize: 11, color: colors.text.secondary, letterSpacing: 0.3 },
  heroBadges:         { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: spacing.xs, justifyContent: 'center' },
  heroBadge:          { paddingHorizontal: 10, paddingVertical: 4, borderRadius: radius.pill, borderWidth: 1 },
  heroBadgeText:      { fontSize: 9, letterSpacing: 1.5, fontWeight: '700' },
  heroProfileSince:   { fontSize: 10, color: colors.text.faint, marginTop: spacing.md, fontStyle: 'italic' },

  // ═══ STATS BANNER ═══
  statsRowNew:    { flexDirection: 'row', alignItems: 'center', marginHorizontal: spacing.xl, marginBottom: spacing.lg, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, paddingVertical: spacing.md, paddingHorizontal: spacing.md },
  statCellNew:    { flex: 1, alignItems: 'center' },
  statDivNew:     { width: 1, height: 30, backgroundColor: colors.bg.border },
  statNumNew:     { fontSize: 18, fontWeight: '600', color: colors.text.primary },
  statLblNew:     { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginTop: 3 },

  // ═══ MON STYLE IA ═══
  styleCard:          { backgroundColor: '#FFFFFF', borderRadius: radius.lg, borderWidth: 1, borderColor: colors.bg.border, padding: spacing.lg, overflow: 'hidden' },
  styleTaglineRow:    { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: spacing.md },
  styleTaglineEmoji:  { fontSize: 28 },
  styleTagline:      { flex: 1, fontSize: 15, fontWeight: '600', color: colors.text.primary, lineHeight: 20 },
  styleDivider:       { height: 1, backgroundColor: colors.bg.border, marginVertical: spacing.md },
  styleRow:           { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.md },
  styleRowColumn:     { marginBottom: spacing.md },
  styleRowLabel:      { fontSize: 9, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 8, flex: 1 },
  styleRowValue:      { flex: 2, flexDirection: 'row', alignItems: 'center', gap: 8 },
  formalityBar:       { flex: 1, height: 6, backgroundColor: '#F0F0F0', borderRadius: 3, overflow: 'hidden' },
  formalityFill:      { height: '100%', backgroundColor: '#D4AF37', borderRadius: 3 },
  formalityText:      { fontSize: 11, fontWeight: '600', color: colors.text.primary, minWidth: 40, textAlign: 'right' },
  colorPalette:       { flexDirection: 'row', gap: 10, flexWrap: 'wrap' },
  colorItem:          { alignItems: 'center', minWidth: 52 },
  colorSwatch:        { width: 34, height: 34, borderRadius: 17, borderWidth: 1.5, marginBottom: 4 },
  colorName:          { fontSize: 9, color: colors.text.primary, fontWeight: '600', textTransform: 'capitalize' },
  colorPct:           { fontSize: 9, color: colors.text.faint, marginTop: 1 },
  styleTags:          { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  styleTag:           { backgroundColor: '#F5F3EE', paddingHorizontal: 10, paddingVertical: 5, borderRadius: radius.pill, borderWidth: 1, borderColor: '#E8E0C8' },
  styleTagText:       { fontSize: 10, color: '#8B7500', fontWeight: '600', letterSpacing: 0.3 },
  styleFooter:        { flexDirection: 'row', paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: colors.bg.border, marginTop: spacing.sm },
  styleFooterCell:    { flex: 1, alignItems: 'center' },
  styleFooterNum:     { fontSize: 13, fontWeight: '700', color: colors.text.primary },
  styleFooterLbl:     { fontSize: 8, color: colors.text.faint, marginTop: 2, textAlign: 'center', letterSpacing: 0.5 },
  styleAiTag:         { marginTop: spacing.md, alignSelf: 'center', paddingHorizontal: 10, paddingVertical: 4, backgroundColor: '#F8F6F0', borderRadius: radius.pill, borderWidth: 1, borderColor: '#E8E0C8' },
  styleAiTagText:     { fontSize: 9, color: '#8B7500', letterSpacing: 0.3 },

  // ═══ Empty style card ═══
  styleEmptyCard:     { alignItems: 'center', padding: spacing.xl, borderRadius: radius.lg, backgroundColor: '#FFF9E6', borderWidth: 1, borderColor: '#F5DEB3', borderStyle: 'dashed' },
  styleEmptyEmoji:    { fontSize: 48, marginBottom: spacing.md },
  styleEmptyTitle:    { fontSize: 14, fontWeight: '600', color: '#B8860B', marginBottom: 6 },
  styleEmptyDesc:     { fontSize: 11, color: colors.text.faint, textAlign: 'center', lineHeight: 16, marginBottom: spacing.md },
  styleEmptyCta:      { paddingHorizontal: spacing.lg, paddingVertical: 8, backgroundColor: '#D4AF37', borderRadius: radius.pill },
  styleEmptyCtaText:  { color: '#FFF', fontSize: 11, fontWeight: '700', letterSpacing: 0.5 },

  // ═══ ACHIEVEMENTS ═══
  levelCard:          { flexDirection: 'row', alignItems: 'center', gap: spacing.md, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, padding: spacing.md, marginBottom: spacing.md },
  levelCardLeft:      { alignItems: 'center', paddingRight: spacing.md, borderRightWidth: 1, borderRightColor: colors.bg.border, minWidth: 70 },
  levelCardRight:     { flex: 1 },
  levelLabel:         { fontSize: 8, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 2 },
  levelNum:           { fontSize: 32, fontWeight: '700', color: '#B8860B', lineHeight: 36 },
  levelPoints:        { fontSize: 9, color: colors.text.secondary, marginTop: 2 },
  levelHeader:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  levelTitle:         { fontSize: 12, fontWeight: '700', color: colors.text.primary },
  levelNext:          { fontSize: 9, color: colors.text.faint },
  levelBar:           { height: 8, backgroundColor: '#F0F0F0', borderRadius: 4, overflow: 'hidden' },
  levelBarFill:       { height: '100%', backgroundColor: '#D4AF37', borderRadius: 4 },

  achSubtitle:        { fontSize: 10, letterSpacing: 1.5, color: colors.text.secondary, fontWeight: '700', marginTop: spacing.md, marginBottom: 8, textTransform: 'uppercase' },
  achGrid:            { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  achCard:            { width: '48%', padding: spacing.md, borderRadius: radius.md, borderWidth: 1, position: 'relative', minHeight: 100 },
  achCardLocked:      { backgroundColor: '#FAFAFA', borderColor: '#EEE' },
  achIcon:            { fontSize: 28, marginBottom: 6 },
  achTitle:           { fontSize: 12, fontWeight: '700', letterSpacing: 0.2 },
  achTitleLocked:     { fontSize: 12, fontWeight: '600', color: colors.text.secondary, letterSpacing: 0.2 },
  achDesc:            { fontSize: 9, color: colors.text.faint, marginTop: 3, lineHeight: 12 },
  achTierBadge:       { position: 'absolute', top: 8, right: 8, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  achTierText:        { fontSize: 7, fontWeight: '700', color: '#FFF', letterSpacing: 0.5 },
  achProgressBar:     { marginTop: 8, height: 4, backgroundColor: '#EEE', borderRadius: 2, overflow: 'hidden' },
  achProgressFill:    { height: '100%', backgroundColor: '#D4AF37', borderRadius: 2 },
  achProgressText:    { fontSize: 9, color: colors.text.faint, marginTop: 3, fontWeight: '600' },

  // ═══ QUICK ACCESS PROFILE ═══
  quickSection:       { paddingHorizontal: spacing.xl, marginBottom: spacing.lg },
  sectionHeader:      { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: spacing.md },
  quickGridProfile:   { flexDirection: 'row', gap: 10 },
  adminBanner:        { marginTop: 12, flexDirection: 'row', alignItems: 'center', backgroundColor: '#1A1A1A', borderRadius: 12, padding: 14, borderWidth: 1.5, borderColor: '#D4AF37' },
  adminBannerTitle:   { fontSize: 14, fontWeight: '700', color: '#D4AF37', letterSpacing: 0.3 },
  adminBannerSub:     { fontSize: 11, color: '#999', marginTop: 2 },
  adminBannerArrow:   { fontSize: 22, color: '#D4AF37', fontWeight: '600' },
  // v1.12.608 — Boutique add-ons standalone
  shopBanner:         { marginTop: 12, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F4ECDD', borderRadius: 12, padding: 14, borderWidth: 1.5, borderColor: '#D4AF37' },
  shopBannerTitle:    { fontSize: 14, fontWeight: '700', color: '#1a1f36', letterSpacing: 0.3 },
  shopBannerSub:      { fontSize: 11, color: '#666', marginTop: 2 },
  shopBannerArrow:    { fontSize: 22, color: '#D4AF37', fontWeight: '600' },

  // ═══ Bannière Compléter le profil ═══
  editProfileBtn: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 4,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF7E0',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#D4AF72',
  },
  editProfileBtnTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1A1814',
    letterSpacing: 0.3,
  },
  editProfileBtnSub: {
    fontSize: 11,
    color: '#7A6F5C',
    marginTop: 2,
  },
  editProfileBtnArrow: {
    fontSize: 22,
    color: '#B8860B',
    fontWeight: '600',
  },
  quickCardProfile:   { flex: 1, borderRadius: radius.md, borderWidth: 1, padding: spacing.md, alignItems: 'center', minHeight: 90 },
  quickCardProfileIcon:{ fontSize: 28, marginBottom: 6 },
  quickCardProfileLabel:{ fontSize: 13, fontWeight: '700', color: colors.text.primary, letterSpacing: 0.2 },
  quickCardProfileSub:{ fontSize: 9, color: colors.text.secondary, marginTop: 2, letterSpacing: 0.2 },

  // Anciens styles conservés pour compatibilité
  profileHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 14, paddingHorizontal: spacing.xl, paddingTop: spacing.lg, paddingBottom: spacing.lg },
  avatarLarge:   { width: 56, height: 56, borderRadius: 28, borderWidth: 0.5, borderColor: colors.accent.main, backgroundColor: colors.bg.surface, alignItems: 'center', justifyContent: 'center' },
  avatarText:    { fontSize: 18, fontWeight: '200', color: colors.accent.main, letterSpacing: 1 },
  avatarLargeImg:{ width: 56, height: 56, borderRadius: 28 },
  profileInfo:   { flex: 1 },
  profileName:   { fontSize: 20, fontWeight: '200', letterSpacing: 3, color: colors.text.primary },
  profileCity:   { fontSize: 9, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase', marginTop: 3 },
  planBadge:     { alignSelf: 'flex-start', backgroundColor: colors.accent.ghost, borderRadius: radius.pill, borderWidth: 0.5, borderColor: colors.text.faint, paddingHorizontal: 8, paddingVertical: 2, marginTop: 6 },
  planBadgeText: { fontSize: 8, letterSpacing: 2, color: colors.accent.main, textTransform: 'uppercase' },
  profileSince:  { alignItems: 'flex-end' },
  profileSinceLabel:{ fontSize: 7, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase' },
  profileSinceVal:{ fontSize: 9, color: colors.text.secondary, marginTop: 2 },

  statsRow:      { flexDirection: 'row', paddingHorizontal: spacing.xl, gap: 8, marginBottom: spacing.lg },
  statCard:      { flex: 1, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, paddingVertical: 12, alignItems: 'center' },
  statNum:       { fontSize: 20, fontWeight: '200', color: colors.text.primary, letterSpacing: 0.5 },
  statLabel:     { fontSize: 7, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase', marginTop: 3 },

  section:       { paddingHorizontal: spacing.xl, marginBottom: spacing.md },
  sectionLabel:  { fontSize: 8, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase', marginBottom: spacing.sm },

  prefCard:      { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  prefRow:       { paddingVertical: 8 },
  prefRowBorder: { borderTopWidth: 0.5, borderTopColor: colors.bg.border },
  prefLabel:     { fontSize: 8, letterSpacing: 2.5, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 6 },
  prefTags:      { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
  prefTag:       { backgroundColor: colors.bg.surface, borderRadius: radius.pill, borderWidth: 0.5, borderColor: colors.bg.border, paddingHorizontal: 8, paddingVertical: 3 },
  prefTagText:   { fontSize: 9, color: colors.text.secondary, letterSpacing: 0.5 },
  prefBudget:    { fontSize: 11, color: colors.text.secondary, letterSpacing: 0.3 },

  settingCard:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  settingLeft:   { flexDirection: 'row', alignItems: 'center', gap: 12 },
  settingIcon:   { fontSize: 18 },
  settingTitle:  { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3, marginBottom: 2 },
  settingVal:    { fontSize: 9, letterSpacing: 1, color: colors.text.faint },
  settingArrow:  { fontSize: 18, color: colors.text.faint },

  settingsCard:  { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  toggleRow:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8 },
  toggleRowBorder:{ borderTopWidth: 0.5, borderTopColor: colors.bg.border },
  toggleLabel:   { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3 },

  subCard:       { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.text.faint, padding: spacing.md },
  subHeader:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  subName:       { fontSize: 13, color: colors.accent.main, letterSpacing: 2, fontWeight: '500' },
  subPrice:      { fontSize: 12, color: colors.accent.main, letterSpacing: 0.5 },
  subPerks:      { fontSize: 11, color: colors.text.faint, lineHeight: 20, marginBottom: spacing.md },
  subBtn:        { borderWidth: 0.5, borderColor: colors.bg.border, paddingVertical: 8, alignItems: 'center' },
  subBtnText:    { fontSize: 8, letterSpacing: 2.5, color: colors.text.faint, textTransform: 'uppercase' },

  linkRow:       { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  linkIcon:      { fontSize: 18 },
  linkLabel:     { flex: 1, fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3 },
  linkArrow:     { fontSize: 18, color: colors.text.faint },

  // ═══ Section "À propos" avec liens légaux ═══
  aboutSection: {
    marginHorizontal: spacing.xl,
    marginBottom: spacing.lg,
  },
  aboutLabel: {
    fontSize: 10,
    letterSpacing: 2.5,
    color: colors.text.faint,
    fontWeight: '600',
    marginBottom: 12,
    marginLeft: 4,
  },
  aboutRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: colors.bg.surface,
    borderRadius: 10,
    marginBottom: 8,
  },
  aboutRowText: {
    fontSize: 14,
    color: colors.text.primary,
    fontWeight: '500',
  },
  aboutRowChevron: {
    fontSize: 22,
    color: colors.text.faint,
    fontWeight: '300',
  },
  // ═══ Bouton "Se déconnecter" mis en évidence en v1.7.48 ═══
  // Avant : bordure fine 0.5px gris, texte 9px lettrespacing 3 — trop discret,
  //   les utilisateurs ne le trouvaient pas et tombaient parfois sur "Supprimer
  //   mon compte" par confusion (résultat : compte effacé alors qu'ils voulaient
  //   juste se déconnecter pour changer d'utilisateur).
  // Maintenant : bordure plus marquée + texte plus lisible. Pas de fond solide
  //   pour rester sobre mais clairement identifiable comme bouton actionnable.
  signOut:       { marginHorizontal: spacing.xl, borderWidth: 1, borderColor: colors.text.primary, borderRadius: 10, paddingVertical: 14, alignItems: 'center', marginBottom: spacing.sm, backgroundColor: '#FFFFFF' },
  signOutText:   { fontSize: 13, letterSpacing: 1.5, color: colors.text.primary, textTransform: 'uppercase', fontWeight: '600' },
  // Bouton "Supprimer mon compte" — discret, sous le bouton de déconnexion.
  // Pas de bordure ni de fond, juste un texte rouge atténué pour rester
  // discret tout en restant cliquable et reconnaissable comme action
  // destructive (App Store §5.1.1(v) RGPD).
  // ═══ Bouton "Supprimer mon compte" rendu plus discret en v1.7.48 ═══
  // Action très destructive (suppression RGPD irréversible) → on ne veut PAS
  // qu'un user cliquez dessus par erreur. Texte gris clair, plus petit, sans
  // soulignement, marge top large pour le séparer visuellement de "Se déconnecter".
  deleteAccount:     { marginHorizontal: spacing.xl, paddingVertical: 8, alignItems: 'center', marginTop: spacing.lg, marginBottom: spacing.sm, opacity: 0.55 },
  deleteAccountText: { fontSize: 10, letterSpacing: 0.3, color: colors.text.faint, fontWeight: '400' },
  version:       { textAlign: 'center', fontSize: 8, letterSpacing: 2, color: colors.text.faint, paddingBottom: spacing.sm },

  premiumCard:          { backgroundColor: '#111', borderRadius: 16, padding: 20, marginBottom: 4 },
  premiumTopRow:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  premiumBadge:         { backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 5, alignSelf: 'flex-start', marginBottom: 6 },
  premiumBadgeTxt:      { fontSize: 11, fontWeight: '700', color: '#fff', letterSpacing: 1.5 },
  premiumPlanTxt:       { fontSize: 13, color: 'rgba(255,255,255,0.6)' },
  // ═══ Texte d'avertissement annulation (jaune/orange) ═══
  premiumCancelledTxt:  { fontSize: 11, color: '#FCD34D', marginTop: 4, fontWeight: '500' },
  // ═══ v1.7.80 : Texte de renouvellement (vert clair) — abonné actif ═══
  premiumRenewsTxt:     { fontSize: 11, color: '#A5D6A7', marginTop: 4, fontWeight: '500' },
  premiumCheck:         { fontSize: 22, color: '#fff' },
  premiumDivider:       { height: 0.5, backgroundColor: 'rgba(255,255,255,0.15)', marginVertical: 14 },
  premiumPerks:         { gap: 10 },
  premiumPerkRow:       { flexDirection: 'row', alignItems: 'center', gap: 10 },
  premiumPerkIcon:      { fontSize: 16, width: 24 },
  premiumPerkTxt:       { fontSize: 13, color: 'rgba(255,255,255,0.85)' },
  premiumSwitchBtn:     { backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: 14, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  premiumSwitchTxt:     { fontSize: 13, fontWeight: '600', color: '#fff' },
  premiumSwitchSub:     { fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 },
  premiumSaveBadge:     { backgroundColor: '#2E7D32', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3 },
  premiumSaveTxt:       { fontSize: 10, color: '#fff', fontWeight: '700' },
  // ═══ Bouton "Changer de formule" dans la carte Premium ═══
  // Style légèrement différent du bouton "Passer à l'annuel" pour ne pas
  // entrer en compétition visuelle : fond plus doux + bordure dorée discrète.
  premiumChangeFormulaBtn:  { marginTop: 10, backgroundColor: 'rgba(212, 175, 55, 0.12)', borderRadius: 10, padding: 14, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderWidth: 1, borderColor: 'rgba(212, 175, 55, 0.35)' },
  premiumChangeFormulaTxt:  { fontSize: 13, fontWeight: '600', color: '#fff' },
  premiumChangeFormulaSub:  { fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 },
  premiumCancelBtn:     { alignItems: 'center', paddingTop: 14 },
  premiumCancelTxt:     { fontSize: 12, color: 'rgba(255,255,255,0.35)', textDecorationLine: 'underline' },

  usageCard:            { backgroundColor: '#F8F8F8', borderRadius: 14, padding: 16, marginBottom: 12 },
  usageCardTitle:       { fontSize: 11, fontWeight: '600', color: '#888', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 },
  usageItem:            { marginBottom: 12 },
  usageItemTop:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  usageItemLeft:        { flexDirection: 'row', alignItems: 'center', gap: 8 },
  usageItemIcon:        { fontSize: 16 },
  usageItemLabel:       { fontSize: 13, color: '#333' },
  usageItemVal:         { fontSize: 13, fontWeight: '600', color: '#111' },
  usageItemValOver:     { color: '#A32D2D' },
  usageItemMax:         { fontSize: 11, fontWeight: '400', color: '#999' },
  usageItemBar:         { height: 4, backgroundColor: '#E8E8E8', borderRadius: 2, overflow: 'hidden' },
  usageItemBarFill:     { height: '100%', backgroundColor: '#111', borderRadius: 2 },
  usageItemBarOver:     { backgroundColor: '#A32D2D' },

  plansRow:             { flexDirection: 'row', gap: 10, marginBottom: 10 },
  planCard:             { flex: 1, borderWidth: 1.5, borderColor: '#E8E8E8', borderRadius: 14, padding: 16, alignItems: 'center', position: 'relative', backgroundColor: '#fff', gap: 4 },
  planCardPopular:      { borderColor: '#111', borderWidth: 2 },
  planBadgeTop:         { position: 'absolute', top: -11, backgroundColor: '#111', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 10 },
  planBadgeTopTxt:      { fontSize: 10, color: '#fff', fontWeight: '700' },
  planCardLabel:        { fontSize: 10, fontWeight: '500', color: '#999', letterSpacing: 1, textTransform: 'uppercase' },
  planCardLabelPop:     { color: '#111' },
  planCardPriceRow:     { flexDirection: 'row', alignItems: 'baseline', gap: 2, marginTop: 4 },
  planCardPrice:        { fontSize: 28, fontWeight: '300', color: '#999' },
  planCardPricePop:     { color: '#111', fontWeight: '500' },
  planCardCurrency:     { fontSize: 12, color: '#999' },
  planCardAnnual:       { fontSize: 11, color: '#999' },
  planCardBtn:          { marginTop: 10, paddingVertical: 8, paddingHorizontal: 16, borderRadius: 8, borderWidth: 1, borderColor: '#E0E0E0', width: '100%', alignItems: 'center' },
  planCardBtnPop:       { backgroundColor: '#111', borderColor: '#111' },
  planCardBtnTxt:       { fontSize: 12, fontWeight: '500', color: '#888' },
  planCardBtnTxtPop:    { color: '#fff' },
  planLegal:            { fontSize: 11, color: '#BDBDBD', textAlign: 'center', marginBottom: 4 },

  // ═══ Toggle mensuel/annuel ═══
  billingToggle: {
    flexDirection: 'row',
    backgroundColor: '#F5F3EE',
    borderRadius: 100,
    padding: 4,
    marginBottom: 18,
    alignSelf: 'center',
  },
  billingTab: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 100,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  billingTabActive: {
    backgroundColor: '#1A1A1A',
  },
  billingTabText: {
    fontSize: 12, fontWeight: '600', color: '#888',
    letterSpacing: 0.5,
  },
  billingTabTextActive: {
    color: '#D4AF72',
  },
  billingDiscountBadge: {
    backgroundColor: '#FFF9E6',
    borderRadius: 8,
    paddingHorizontal: 6, paddingVertical: 2,
  },
  billingDiscountText: {
    fontSize: 9, color: '#B8860B', fontWeight: '700', letterSpacing: 0.5,
  },

  // ═══ Bandeau "Plan actif" ═══
  // ═══ Bandeau plan actif enrichi ═══
  expiredCard: {
    // Carte ambrée discrète : ni rouge alarme, ni vert succès — juste un état neutre
    // qui indique "fin de période" sans être négatif. Cohérent avec la palette dorée.
    backgroundColor: '#FEF3C7',
    borderWidth: 1.5,
    borderColor: '#FCD34D',
    borderRadius: 16,
    padding: 18,
    marginBottom: 14,
  },
  expiredHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
  },
  expiredIcon: {
    fontSize: 28,
    color: '#D97706',
    marginTop: 2,
  },
  expiredTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#92400E',
    marginBottom: 6,
    letterSpacing: 0.2,
  },
  expiredSubtitle: {
    fontSize: 14,
    color: '#78350F',
    lineHeight: 20,
    marginBottom: 8,
  },
  expiredDescription: {
    fontSize: 13,
    color: '#92400E',
    lineHeight: 19,
    opacity: 0.85,
  },
  expiredCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1A1814',
    paddingVertical: 14,
    paddingHorizontal: 22,
    borderRadius: 10,
    marginTop: 14,
    gap: 8,
  },
  expiredCtaText: {
    color: '#D4B896',
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0.6,
  },
  expiredCtaArrow: {
    color: '#D4B896',
    fontSize: 14,
    fontWeight: '700',
  },
  activePlanCard: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1.5,
    borderColor: '#86EFAC',
    borderRadius: 16,
    padding: 18,
    marginBottom: 14,
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 2,
  },
  activePlanHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 14,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#BBF7D0',
  },
  activePlanHeaderLeft: { flex: 1 },
  activePlanHeaderBadge: {
    fontSize: 9, fontWeight: '800',
    color: '#10B981',
    letterSpacing: 2.5,
    marginBottom: 8,
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  activePlanTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  activePlanEmojiLg: {
    fontSize: 38,
  },
  activePlanName: {
    fontSize: 22, fontWeight: '800',
    color: '#1A1A1A',
    letterSpacing: 0.3,
    lineHeight: 26,
  },
  activePlanPeriod: {
    fontSize: 13, fontWeight: '500',
    color: '#666',
  },
  activePlanTagline: {
    fontSize: 12,
    color: '#65A30D',
    marginTop: 2,
    fontStyle: 'italic',
  },
  activePlanBenefitsList: {
    gap: 10,
  },
  // ═══ Bloc date de renouvellement / expiration ═══
  renewalBlock: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    borderRadius: 8,
    marginBottom: 12,
  },
  renewalText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
  activePlanBenefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  activePlanBenefitIcon: {
    fontSize: 16,
    width: 20,
    textAlign: 'center',
  },
  activePlanBenefitLabel: {
    fontSize: 13,
    color: '#1F2937',
    fontWeight: '500',
    flex: 1,
    letterSpacing: 0.1,
  },

  // Bouton changer de formule
  changeFormulaBtn: {
    alignSelf: 'center',
    paddingHorizontal: 22, paddingVertical: 12,
    borderRadius: 100,
    backgroundColor: '#1A1A1A',
    marginBottom: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
    elevation: 2,
  },
  changeFormulaBtnText: {
    fontSize: 11, fontWeight: '700', color: '#fff',
    letterSpacing: 1.2, textTransform: 'uppercase',
  },

  // Card plan actif
  planCardActive: {
    borderColor: '#10B981', borderWidth: 2,
    backgroundColor: '#F0FDF4',
  },
  planCardBtnActive: {
    backgroundColor: '#10B981',
  },
  planCardBtnTxtActive: {
    color: '#FFF',
  },

  // ═══ Features dans chaque plan card ═══
  planFeats: { marginTop: 10, alignSelf: 'stretch' },
  planFeatsItem: { fontSize: 10, color: '#666', lineHeight: 15 },
});
